/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import Onboarding from './Onboarding';
import { 
  CreditCard, Check, X, Lock, User, Settings, LogOut, Bell, ChevronRight, Plus, CheckCircle,
  XCircle, Layout, Building2, PieChart, Gift, Users, Copy, 
  Trash2, Save, Upload, QrCode, TrendingUp, Clock, Image as ImageIcon,
  ShieldCheck, Palette, Webhook, AlertTriangle, Key, Terminal, Camera, Search, Filter,
  UserPlus, Sliders, Briefcase, Mail, Fingerprint, Info, Sparkles, Globe, ArrowLeft,
  MessageCircle, Edit3, Edit, Send, Heart, Tag, ListPlus, Megaphone, Activity, Loader2, MapPin, Menu, Star,
  Frown, Annoyed, Meh, Smile, Laugh
} from 'lucide-react';
import { PoweredByPaxfidely } from './PoweredByPaxfidely';

// IMPORTACIONES DE FIREBASE (Apunta a tu nuevo archivo centralizado)
import { db, auth } from './firebase'; 
import { collection, doc, setDoc, onSnapshot, query, addDoc, updateDoc, deleteDoc, serverTimestamp, writeBatch, getDocs } from 'firebase/firestore';
import { signInAnonymously, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';

/**
 * PAXFIDELY - VERSIÓN 22.0 (LUXURY EDITION) + MULTI-TENANT SAAS
 * -------------------------------------------------------------------------
 * - Integración con Onboarding dinámico
 * - Detección de Tenant (Hotel) por URL (?hotel=id-del-hotel)
 */

const urlParams = new URLSearchParams(window.location.search);
const isDemoMode = urlParams.get('demo') === 'true';
const activeTenantId = urlParams.get('hotel');

// EL MOTOR SAAS: Si hay un hotel en la URL, usa su isla privada. Si no, usa la base general.
const appId = activeTenantId ? `tenant-${activeTenantId}` : (isDemoMode ? "demo-paxfidely" : "luxury-loyalty-v1");

const translations = {
  Español: {
    welcome: "Bienvenido", status: "Nivel", pts: "PUNTOS", upload_invoice: "CARGAR CONSUMO",
    history: "MOVIMIENTOS", rewards: "Canjes", solicit: "SOLICITAR",
    digital_card: "TARJETA DIGITAL", profile: "PREFERENCIAS", save: "GUARDAR", 
    staff_access: "Portal Staff", guest_access: "Portal Socio", language: "Idioma",
    hotels: "Hoteles", guests: "Socios", catalog_empty: "No hay premios disponibles actualmente.",
    general_chat: "Iniciar Chat con Recepción", contact_hotel: "Contacto Rápido", website: "Sitio Web",
    rate_exp: "VALORAR EXPERIENCIA", rate_thanks: "¡Gracias por su valoración!", available_benefits: "BENEFICIOS DISPONIBLES",
    my_history: "MI HISTORIAL Y CUENTA", concept: "Concepto y Comunicación", state: "Estado", chat_btn: "Ver Chat",
    profile_title: "DESEAMOS CONOCERLO MEJOR", profile_desc: "Seleccione las opciones que nos ayudarán a brindarle una atención personalizada.",
    no_history: "No tienes movimientos registrados."
  },
  English: {
    welcome: "Welcome", status: "Tier", pts: "POINTS", upload_invoice: "UPLOAD RECEIPT",
    history: "HISTORY", rewards: "Rewards", solicit: "REDEEM",
    digital_card: "DIGITAL CARD", profile: "PREFERENCES", save: "SAVE", 
    staff_access: "Staff Portal", guest_access: "Guest Portal", language: "Language",
    hotels: "Hotels", guests: "Guests", catalog_empty: "No rewards available currently.",
    general_chat: "Start Chat with Front Desk", contact_hotel: "Quick Contact", website: "Website",
    rate_exp: "RATE EXPERIENCE", rate_thanks: "Thanks for your feedback!", available_benefits: "AVAILABLE BENEFITS",
    my_history: "MY HISTORY & ACCOUNT", concept: "Concept & Communication", state: "Status", chat_btn: "View Chat",
    profile_title: "WE WANT TO KNOW YOU BETTER", profile_desc: "Select the options that will help us provide you with personalized attention.",
    no_history: "You have no registered movements."
  }
};

const RatingFaces = ({ currentRating, onRate, size = 24, readOnly = false }) => {
    const faces = [
        { val: 1, icon: Frown, color: 'text-red-500', bg: 'bg-red-50', label: 'Malo' },
        { val: 2, icon: Annoyed, color: 'text-orange-500', bg: 'bg-orange-50', label: 'Regular' },
        { val: 3, icon: Meh, color: 'text-zinc-500', bg: 'bg-zinc-100', label: 'Neutro' },
        { val: 4, icon: Smile, color: 'text-green-500', bg: 'bg-green-50', label: 'Bueno' },
        { val: 5, icon: Laugh, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Excelente' }
    ];

    return (
        <div className="flex items-center gap-3">
            {faces.map(f => (
                <button 
                    key={f.val} type="button" disabled={readOnly} onClick={() => onRate && onRate(f.val)}
                    className={`p-1.5 rounded-full transition-all duration-300 ${currentRating === f.val ? f.bg + ' scale-110 ring-2 ring-current shadow-sm' : 'opacity-70 grayscale hover:opacity-100 hover:grayscale-0'}`}
title={f.label}
>
    <f.icon size={size} className={currentRating === f.val ? f.color : 'text-zinc-600'} strokeWidth={currentRating === f.val ? 2 : 1.5} />
                </button>
            ))}
        </div>
    );
};

const AppContent = () => {
  // =========================================================================
  // DETECCIÓN INTELIGENTE DE DOMINIO COMERCIAL (.COM) VS PLATAFORMA (.APP)
  // =========================================================================
  const currentHostname = window.location.hostname;
  const location = useLocation();
  const navigate = useNavigate();

  // Forzamos que si entran por paxfidely.com, el sistema los mantenga en la raíz comercial
  const isCommercialLandingDomain = currentHostname.includes('paxfidely.com');

  // EFECTO DE REDIRECCIÓN AUTOMÁTICA (EL ESCUDO INTERCEPTOR)
  useEffect(() => {
    const isAppDomain = currentHostname.includes('paxfidely.app') || currentHostname.includes('paxfidely-app');
    const urlParams = new URLSearchParams(window.location.search);
    const isDemo = urlParams.get('demo') === 'true';
    const isPreview = urlParams.get('preview') === 'true'; // LLAVE DEL CREADOR
    const hotel = urlParams.get('hotel');
    
    if (location.pathname === '/') {
      // 1. DOMINIO OPERATIVO (.APP): Si entran a la raíz pura, van directo a trabajar (Login Staff)
      if (isAppDomain && !isDemo && !hotel && !isPreview) {
        navigate('/admin', { replace: true });
      } 
      // 2. ENTORNO REAL: Si hay hotel pero NO es demo y NO es preview (Huésped escaneando QR)
      else if (hotel && !isDemo && !isPreview) {
        navigate(`/guest?hotel=${hotel}`, { replace: true });
      }
    }
  }, [currentHostname, location.pathname, navigate]);

  const [isFormLoginMode, setIsFormLoginMode] = useState(false); // Flipped para clientes activos

  const [user, setUser] = useState(null);
  const [adminSubView, setAdminSubView] = useState('dashboard');
  const [configView, setConfigView] = useState('branding'); 
  const [upgradeRequired, setUpgradeRequired] = useState(null);
  const [guestSubView, setGuestSubView] = useState('home'); 
  const [currentUser, setCurrentUser] = useState(null); 
  const [guestLoginData, setGuestLoginData] = useState({ dni: '', pin: '' });
  const [staffSession, setStaffSession] = useState(null); 
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const [loginData, setLoginData] = useState({ identifier: '', pin: '' });
  const [adminLoginData, setAdminLoginData] = useState({ email: '', pin: '' });

  const [showModal, setShowModal] = useState(null); 
  const [isChainPricing, setIsChainPricing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // ESTADO NUEVO: Guarda la información del hotel dinámico que viene de la URL
  const [tenantData, setTenantData] = useState(null);

  const [selectedGuest, setSelectedGuest] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedBenefit, setSelectedBenefit] = useState(null); 
  const [chatMessage, setChatMessage] = useState("");
  const [newChatMsg, setNewChatMsg] = useState(""); 

  const [dirSearchTerm, setDirSearchTerm] = useState("");
  const [dirTierFilter, setDirTierFilter] = useState("Todos");
  const [dirHotelFilter, setDirHotelFilter] = useState("Todos");
  const [catalogAdminFilter, setCatalogAdminFilter] = useState("Todos");

  const [campaignTarget, setCampaignTarget] = useState("Todos"); 
  const [campaignHotel, setCampaignHotel] = useState("Todos");
  const [campaignMsg, setCampaignMsg] = useState(""); 
  const [campaignImage, setCampaignImage] = useState("");
  const [campaignLog, setCampaignLog] = useState([]); // Historial de envíos

  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [config, setConfig] = useState({
    isChainMode: false, chainName: "PaxFidely System", hotelName: "PaxFidely", 
    primaryColor: "#C5A059", logo: "https://images.paxfidely.com/placeholders/logo-default.png",
    selectedLanguage: "Español", welcomePoints: 500,
    tiers: [
        { id: 'tier1', active: true, name: "Classic", cashback: 2, color: "#9ca3af" },
        { id: 'tier2', active: true, name: "Gold", cashback: 4, color: "#fbbf24" },
        { id: 'tier3', active: true, name: "Platinum", cashback: 6, color: "#60a5fa" },
        { id: 'tier4', active: true, name: "Select", cashback: 8, color: "#18181b" }
    ]
  });

  const [preferenceSettings, setPreferenceSettings] = useState({
      "Preferencias de Estancia": ["Piso Alto", "Cerca del Ascensor", "Almohada Extra", "Late Check-out"],
      "Perfil Gastronómico": ["Vegano", "Celíaco", "Sin Lactosa", "Alergia a Frutos Secos"],
      "Motivo de Viaje": ["Negocios", "Escapada Romántica", "Vacaciones Familiares"]
  });
  const [newPrefCategory, setNewPrefCategory] = useState("");
  const [newPrefTag, setNewPrefTag] = useState({});
  const [guestLanguage, setGuestLanguage] = useState(null);
  const [guestLocation, setGuestLocation] = useState('Global');
  const [activeAdminHotel, setActiveAdminHotel] = useState('Todos');

  const [benefits, setBenefits] = useState([]);
  const [guests, setGuests] = useState([]);
  const [requests, setRequests] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [staff, setStaff] = useState([]);

  const [billAmount, setBillAmount] = useState("");
  const [billDesc, setBillDesc] = useState("");
  const [newPinInput, setNewPinInput] = useState("");

  const [newBenefit, setNewBenefit] = useState({ title: '', cost: '', desc: '', image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&q=80&w=500', stock: 50, hotelId: 'Todos' });
  const [newHotel, setNewHotel] = useState({ name: '', city: '', welcomePoints: 500 });
  const [newStaff, setNewStaff] = useState({ name: '', email: '', role: 'Operador', pin: '', hotelId: 'Todos' });
  const [newGuest, setNewGuest] = useState({ name: '', dni: '', phone: '', tier: 'Classic', pin: '', points: 500 });
  // --- INYECCIÓN DE ESTADOS PARA LA LANDING V25.0 ---
  const [pricingSegment, setPricingSegment] = useState('individual');
  const [enterpriseWhiteLabel, setEnterpriseWhiteLabel] = useState(false);
  const [roomCount, setRoomCount] = useState(30); // Estado para la calculadora de ROI
  const isGuestRoute = location.pathname === '/guest';

  const globalSatisfaction = useMemo(() => {
      const rated = requests.filter(r => r.rating > 0);
      if (rated.length === 0) return 0;
      const sum = rated.reduce((acc, r) => acc + r.rating, 0);
      return (sum / rated.length).toFixed(1);
  }, [requests]);

  const guestSatisfaction = (gId) => {
      const rated = requests.filter(r => (r.guestId === gId || r.dni === gId) && r.rating > 0);
      if (rated.length === 0) return 0;
      const sum = rated.reduce((acc, r) => acc + r.rating, 0);
      return (sum / rated.length).toFixed(1);
  };

  const getSatisfactionColor = (val) => {
      if (val >= 4.5) return 'text-amber-500';
      if (val >= 3.5) return 'text-green-500';
      if (val >= 2.5) return 'text-zinc-500';
      if (val >= 1.5) return 'text-orange-500';
      return 'text-red-500';
  };

  const isDemoSeeded = useMemo(() => hotels.some(h => h.id === 'demo-hotel-1') || guests.some(g => g.id === 'demo-guest-1'), [hotels, guests]);

  const getDisplayTier = (tierStr) => {
     const safeTiers = config.tiers || []; if (!tierStr) return safeTiers.find(t => t.active)?.name || 'Classic'; 
      const low = tierStr.toLowerCase();
      if (low === 'white') return 'Classic';
      if (low === 'orange') return 'Gold';
      if (low === 'blue') return 'Platinum';
      if (low === 'black') return 'Select';
      return tierStr; 
  };

  const activeHotelData = useMemo(() => {
      let targetId = null;
      if (isGuestRoute) targetId = guestLocation !== 'Global' ? guestLocation : activeTenantId;
      else if (staffSession) targetId = activeAdminHotel === 'Todos' ? null : activeAdminHotel;
      if (targetId && targetId !== 'Global') return hotels.find(h => h.id === targetId) || null;
      return null;
  }, [isGuestRoute, guestLocation, staffSession, activeAdminHotel, hotels, activeTenantId]);

  const currentBranding = useMemo(() => {
      if (config.isChainMode && activeHotelData) {
          return { name: activeHotelData.name || config.hotelName, primaryColor: activeHotelData.primaryColor || config.primaryColor, logo: activeHotelData.logo || config.logo, welcomePoints: activeHotelData.welcomePoints || config.welcomePoints };
      }
      return { name: config.isChainMode ? config.chainName : config.hotelName, primaryColor: config.primaryColor, logo: config.logo, welcomePoints: config.welcomePoints };
  }, [activeHotelData, config]);

  useEffect(() => { if (!guestLanguage && config.selectedLanguage) setGuestLanguage(config.selectedLanguage); // eslint-disable-next-line react-hooks/exhaustive-deps
}, [config.selectedLanguage]);
  useEffect(() => { 
    if (currentUser) { 
      if (config.isChainMode && hotels.length > 0) { 
        if (currentUser.registeredAtHotel && currentUser.registeredAtHotel !== 'Global') {
          setGuestLocation(currentUser.registeredAtHotel); 
        } else {
          const primeraActiva = hotels.find(h => h.active !== false);
          setGuestLocation(primeraActiva ? primeraActiva.id : 'Global');
        }
      } else {
        setGuestLocation('Global'); 
      }
    } 
  }, [currentUser, hotels, config.isChainMode]);
  useEffect(() => { if (staffSession) setActiveAdminHotel(staffSession.hotelId || 'Todos'); }, [staffSession]);
  useEffect(() => { if (config.isChainMode) setDirHotelFilter(activeAdminHotel); else setDirHotelFilter('Todos'); }, [activeAdminHotel, config.isChainMode]);
  // --- CIRUGÍA LÁSER: LIMPIEZA AUTOMÁTICA DE MEMORIA DE CHATS ---
  useEffect(() => { 
      if (!showModal) {
          setChatMessage(""); 
          setNewChatMsg("");
      }
  }, [showModal]);

  const activeLang = isGuestRoute && currentUser ? (guestLanguage || config.selectedLanguage) : config.selectedLanguage;
  const t = translations[activeLang] || translations.Español;
  const isMasterOrChain = staffSession?.role === 'System' || staffSession?.role === 'Superadmin';
// --- INICIO CIRUGÍA LÁSER 1: CEREBRO BILLING GUARD ---
  const billingState = useMemo(() => {
    // 1. PRIORIDAD MÁXIMA: Si usas el simulador manual
    if (config.simulateExpiredTrial) return { isExpired: true, daysLeft: 0 };

    // 2. Si el Superadmin global forzó la cuenta a Activa, o no hay fecha de creación (hoteles viejos), pasa.
    if (config.subscriptionStatus === 'ACTIVE' || !config.createdAt) return { isExpired: false, daysLeft: 14 };

    // 3. Cálculo real de 14 días
    const msPassed = Date.now() - (config.createdAt.seconds ? config.createdAt.seconds * 1000 : config.createdAt);
    const daysPassed = Math.floor(msPassed / (1000 * 60 * 60 * 24));
    const daysLeft = 14 - daysPassed;

    return { isExpired: daysLeft < 0, daysLeft: Math.max(0, daysLeft) };
  }, [config.createdAt, config.subscriptionStatus, config.simulateExpiredTrial]);
  // --- FIN CIRUGÍA LÁSER 1 ---

  const filteredStaffAdmin = useMemo(() => {
      if (!staffSession) return [];
      let list = staff;
      
      // 1. Filtro por Sede
      if (config.isChainMode && activeAdminHotel !== 'Todos') {
          list = list.filter(s => s.hotelId === activeAdminHotel);
      }
      
      // 2. Filtro de Jerarquía (RBAC): Si no soy Superadmin, no veo a los Superadmins
      if (staffSession.role !== 'Superadmin' && staffSession.role !== 'System') {
          list = list.filter(s => s.role !== 'Superadmin' && s.role !== 'System');
      }
      
      return list;
  }, [staff, staffSession, activeAdminHotel, config.isChainMode]);


  const filteredRequestsAdmin = useMemo(() => {
      if (!staffSession) return [];
      if (!config.isChainMode || activeAdminHotel === 'Todos') return requests;
      return requests.filter(r => r.hotelId === activeAdminHotel || r.hotelId === 'Todos' || !r.hotelId);
  }, [requests, staffSession, activeAdminHotel, config.isChainMode]);

  const filteredGuests = useMemo(() => {
      return guests.filter(g => {
          const safeName = g.name || ""; const safeDni = g.dni || ""; const matchesSearch = safeName.toLowerCase().includes(dirSearchTerm.toLowerCase()) || safeDni.includes(dirSearchTerm); 
          const mappedTier = getDisplayTier(g.tier);
          const matchesTier = dirTierFilter === "Todos" || mappedTier === dirTierFilter;
          const matchesHotel = dirHotelFilter === "Todos" || g.registeredAtHotel === dirHotelFilter;
          return matchesSearch && matchesTier && matchesHotel;
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [guests, dirSearchTerm, dirTierFilter, dirHotelFilter, config.tiers]);

  const [guestCatalogFilter, setGuestCatalogFilter] = useState("Local");

  const visibleBenefitsGuest = useMemo(() => {
      if (!config.isChainMode) return benefits;
      const targetLocation = guestCatalogFilter === 'Local' ? guestLocation : guestCatalogFilter;
      if (targetLocation === 'Global') return benefits;
      return benefits.filter(b => b.hotelId === 'Todos' || !b.hotelId || b.hotelId === targetLocation);
  }, [benefits, guestLocation, config.isChainMode, guestCatalogFilter]);

  const hasUnreadGuest = useMemo(() => {
      if (!currentUser) return false;
      return requests.some(r => (r.guestId === currentUser.id || r.dni === currentUser.dni) && r.unreadByGuest);
  }, [requests, currentUser]);


  // FIREBASE SYNC Y AUTENTICACIÓN SEGURA
  useEffect(() => {
    const initAuth = async () => {
        try {
            if (typeof window !== 'undefined' && window.__initial_auth_token) {
                await signInWithCustomToken(auth, window.__initial_auth_token);
            } else {
                await signInAnonymously(auth);
            }
        } catch (e) { 
            console.warn("Fallo de token, forzando modo anónimo...");
            await signInAnonymously(auth).catch(err => console.error(err)); 
        }
    };
    initAuth();
    const unsub = onAuthStateChanged(auth, u => { setUser(u); setLoading(false); });
    return () => unsub();
  }, []);

  // LECTURA DINÁMICA DE LA URL Y EXTRACCIÓN DE DATOS DEL ONBOARDING
  useEffect(() => {
    if (activeTenantId && db) {
        // Escucha en tiempo real el hotel que acabamos de crear en Onboarding
        const unsubTenant = onSnapshot(doc(db, 'hotels', activeTenantId), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setTenantData(data); // Guardamos la data del Onboarding
                
                // Sobrescribimos el diseño de la app para que se vea como este hotel
                setConfig(prev => ({
                    ...prev,
                    hotelName: data.hotelName,
                    chainName: data.hotelName,
                    logo: data.logoUrl,
                    primaryColor: data.template === 'Boutique' ? '#C5A059' : (data.template === 'Urbano' ? '#3b82f6' : '#10b981'), 
                    // Puedes ajustar los colores según tu gusto
                }));
            }
        });
        return () => unsubTenant();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeTenantId]);

  useEffect(() => {
    if (!user) return;
    
    // NOTA: Para no romper tu estructura actual, seguimos leyendo de artifacts
    // Pero si tienes un activeTenantId, idealmente en el futuro leeremos las subcolecciones de ese tenant.
    // Por ahora, cargamos tus datos de artifacts para que no pierdas lo avanzado.
    const path = `/artifacts/${appId}/public/data`;
    
    const unsubs = [
          onSnapshot(doc(db, `/artifacts/${appId}/public/data/settings/config`), s => {
              if (s.exists()) {
                  const data = s.data();
                  setConfig(prev => ({ 
                      ...prev, 
                      ...data, 
                      // Mantenemos los tiers seguros si vienen de la base de datos
                      tiers: data.tiers && data.tiers.length > 0 ? data.tiers : prev.tiers 
                  }));
              }
          }),
        onSnapshot(doc(db, `/artifacts/${appId}/public/data/settings/preferences`), s => s.exists() && setPreferenceSettings(s.data())),
        onSnapshot(collection(db, `${path}/hotels`), s => setHotels(s.docs.map(d => ({...d.data(), id: d.id})))),
        onSnapshot(collection(db, `${path}/benefits`), s => setBenefits(s.docs.map(d => ({...d.data(), id: d.id})))),
        onSnapshot(collection(db, `${path}/staff`), s => setStaff(s.docs.map(d => ({...d.data(), id: d.id})))),
        onSnapshot(collection(db, `${path}/guests`), s => {
            const data = s.docs.map(d => ({...d.data(), id: d.id}));
            setGuests(data);
            if (currentUser) { const up = data.find(g => g.id === currentUser.id); if (up) setCurrentUser(up); }
        }),
        onSnapshot(collection(db, `${path}/requests`), s => setRequests(s.docs.map(d => ({...d.data(), id: d.id})).sort((a,b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)))),
        onSnapshot(collection(db, `${path}/campaigns`), s => setCampaignLog(s.docs.map(d => ({...d.data(), id: d.id})).sort((a,b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))))
    ];
    return () => unsubs.forEach(u => u());
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [user, currentUser?.id, activeTenantId]);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 5000); };
  
  const resetFormState = () => {
    setEditMode(false); setEditingId(null); setShowModal(null);
    setNewBenefit({ title: '', cost: '', desc: '', image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&q=80&w=500', stock: 50, hotelId: config.isChainMode ? (activeAdminHotel === 'Todos' ? 'Todos' : activeAdminHotel) : 'Global' });
    setNewHotel({ name: '', city: '', welcomePoints: 500 });
    setNewStaff({ name: '', email: '', role: 'Operador', pin: '', hotelId: config.isChainMode ? (activeAdminHotel === 'Todos' ? 'Todos' : activeAdminHotel) : 'Global' });
    setNewGuest({ name: '', dni: '', phone: '', tier: 'Classic', pin: '', points: currentBranding.welcomePoints, preferences: [], registeredAtHotel: config.isChainMode ? (activeAdminHotel !== 'Todos' ? activeAdminHotel : '') : 'Global' });
  };

  const openEditModal = (type, item) => {
    setEditMode(true); setEditingId(item.id);
    if (type === 'benefit') { setNewBenefit(item); setShowModal('add_benefit'); }
    if (type === 'hotel') { setNewHotel({...item, primaryColor: item.primaryColor || '#C5A059', welcomePoints: item.welcomePoints || 500 }); setShowModal('add_hotel'); }
    if (type === 'staff') { setNewStaff(item); setShowModal('add_staff'); }
    if (type === 'guest') { setNewGuest({...item}); setShowModal('add_guest'); }
  };

  const handleAction = async (type, payload) => {
    if (!db || !user) return;
    setIsProcessing(true);
    
    // CIRUGÍA LÁSER: Control de duplicados para Huéspedes desde el Hub Staff
    if (type === 'ADD_GUEST') {
        const existeSocio = guests.find(g => g.dni?.trim() === payload.dni?.trim());
        // Si existe, y NO estamos en modo edición de ese mismo socio, bloqueamos
        if (existeSocio && (!editMode || editingId !== existeSocio.id)) {
            showToast("Ya existe un socio registrado con este DNI.", "error");
            setIsProcessing(false);
            return;
        }
    }

    const colName = { 'ADD_BENEFIT': 'benefits', 'ADD_HOTEL': 'hotels', 'ADD_STAFF': 'staff', 'ADD_GUEST': 'guests', 'ADD_REQUEST': 'requests' }[type];
    try {
        if (editMode && editingId) {
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', colName, editingId), payload);
        } else {
            const docRef = await addDoc(collection(db, 'artifacts', appId, 'public', 'data', colName), {...payload, createdAt: serverTimestamp()});
            if(type === 'ADD_GUEST') {
                await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'requests'), { type: 'BONO', guestId: docRef.id, guestName: payload.name, dni: payload.dni, pts: Number(payload.points) || 500, status: 'APROBADO', benefitTitle: 'Bono Inicial', hotelId: payload.registeredAtHotel || 'Global', createdAt: serverTimestamp(), rating: 0, messages: [] });
            }
        }
        showToast("Éxito en la operación"); resetFormState();
    } catch (e) { showToast(e.message, 'error'); } finally { setIsProcessing(false); }
  };

  const handleSeedDemoData = async (forceReset = false) => {
      if (!db || !user || isProcessing) return;
      if (isDemoSeeded && !forceReset) { showToast("La base demo ya está cargada."); return; }
      setIsProcessing(true);
      try {
          const collectionsToClear = ['requests', 'hotels', 'staff', 'benefits', 'guests'];
          for (const colName of collectionsToClear) {
              const snap = await getDocs(collection(db, 'artifacts', appId, 'public', 'data', colName));
              for (const docSnap of snap.docs) await deleteDoc(docSnap.ref);
          }
          await new Promise(resolve => setTimeout(resolve, 1000));
          const insertBatch = writeBatch(db);
          const configRef = doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'config');
          insertBatch.set(configRef, { isChainMode: true, chainName: "Grupo PaxFidely (Demo)", hotelName: "Sede Boutique", primaryColor: "#f4b41a", logo: "https://cdn-icons-png.flaticon.com/512/3030/3030335.png", selectedLanguage: "Español", welcomePoints: 500, tiers: config.tiers });
          const prefsRef = doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'preferences');
          insertBatch.set(prefsRef, { "Gustos": ["Vino Tinto", "Vino Blanco", "Cervezas"], "Almohadas": ["Pluma", "Visco", "Dura"], "Alergias": ["Sin TACC", "Vegano"] });
          const h1Ref = doc(db, 'artifacts', appId, 'public', 'data', 'hotels', 'demo-hotel-1');
          insertBatch.set(h1Ref, { name: "PaxFidely Resort & Spa", city: "Sede Central", welcomePoints: 1000, primaryColor: "#f4b41a" });
          const s1Ref = doc(db, 'artifacts', appId, 'public', 'data', 'staff', 'demo-staff-1');
          insertBatch.set(s1Ref, { name: "Gerencia Demo", email: "gerencia@demo.com", pin: "1234", role: "Superadmin", hotelId: "Todos" });
          const b1Ref = doc(db, 'artifacts', appId, 'public', 'data', 'benefits', 'demo-benefit-1');
          insertBatch.set(b1Ref, { title: "Cena Premium", desc: "Menú de 3 pasos con maridaje de vinos.", cost: 3500, stock: 10, hotelId: "demo-hotel-1", image: "https://images.unsplash.com/photo-1544148103-0773bf10d330?auto=format&fit=crop&q=80&w=500" });
          const g1Ref = doc(db, 'artifacts', appId, 'public', 'data', 'guests', 'demo-guest-1');
          insertBatch.set(g1Ref, { name: "Juan Pérez", dni: "11223344", pin: "1234", points: 8500, tier: "Platinum", registeredAtHotel: "demo-hotel-1", preferences: [] });
          await insertBatch.commit();
          showToast(forceReset ? "¡Entorno purgado!" : "¡Datos inyectados!", "success");
      } catch (e) { showToast(e.message, 'error'); } finally { setIsProcessing(false); }
  };

  const handleAdminApproval = async (req, action) => {
    setIsProcessing(true);
    try {
        const ref = doc(db, 'artifacts', appId, 'public', 'data', 'requests', req.id);
        
        // ==========================================
        // CAMINO A: SI LA ACCIÓN ES RECHAZAR
        // ==========================================
        if (action === 'RECHAZAR') {
            if (req.type === 'BENEFICIO') { 
                const g = guests.find(i => i.id === req.guestId || i.dni === req.dni); 
                if (g) await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'guests', g.id), { points: Number(g.points || 0) + Number(req.cost) }); 
            }
            await updateDoc(ref, { status: 'RECHAZADO' });
        } 
        
        // ==========================================
        // CAMINO B: SI LA ACCIÓN ES APROBAR
        // ==========================================
        else if (action === 'APROBAR') {
            // 1. Si aprueban una FACTURA de consumo: Sumamos puntos al socio
            if (req.type === 'FACTURA') { 
                const g = guests.find(i => i.id === req.guestId || i.dni === req.dni); 
                if (g) {
                    await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'guests', g.id), { 
                        points: Number(g.points || 0) + Number(req.pts),
                        lastActivityAt: serverTimestamp() // Reloj de actividad activo
                    }); 
                }
                // Guardamos los puntos finales validados en el ticket
                await updateDoc(ref, { pts: Number(req.pts) });
            }
            
            // 2. Si aprueban un ALTA de turista en mostrador
            if (req.type === 'ALTA') {
                const pinGenerado = (req.dni && req.dni.length >= 4) ? req.dni.slice(-4) : '1234';
                const newGuestData = {
                    name: req.guestName || 'Sin Nombre',
                    dni: req.dni || '',
                    phone: req.phone || '',
                    points: 500,
                    tier: 'Classic',
                    status: 'ACTIVO', 
                    registeredAtHotel: req.hotelId || 'Global',
                    pin: pinGenerado, 
                    createdAt: serverTimestamp()
                };
                const newGuestRef = await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'guests'), newGuestData);
                
                await addDoc(collection(db, `artifacts/${appId}/public/data/requests`), { 
                    type: 'BONO', guestId: newGuestRef.id, guestName: newGuestData.name, dni: newGuestData.dni, pts: 500, status: 'APROBADO', benefitTitle: 'Bono Inicial (Auto-Registro)', hotelId: req.hotelId || 'Global', createdAt: serverTimestamp(), rating: 0, messages: [] 
                });

                alert(`¡Socio Aprobado!\nEl PIN para ${req.guestName} es: ${pinGenerado}`);
            }

            // Para cualquier tipo de ticket aprobado, actualizamos su estado final
            await updateDoc(ref, { status: 'APROBADO', rating: 0 });
        }

        showToast("Ticket Procesado con éxito"); 
        setShowModal(null);
    } catch (e) { 
        showToast(e.message, 'error'); 
    } finally { 
        setIsProcessing(false); 
    }
  };

  const handleRateExperience = async (id, val) => { try { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'requests', id), { rating: val }); showToast(t.rate_thanks); } catch (e) { showToast(e.message, 'error'); } };
  
  const handleSendMessage = async (id, role) => { 
      if (!chatMessage.trim()) return; 
      const req = requests.find(r => r.id === id); 
      try { 
          await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'requests', id), { 
              messages: [...(req.messages || []), { sender: role, text: chatMessage.trim(), timestamp: Date.now() }], 
              unreadByAdmin: role === 'guest', 
              unreadByGuest: role === 'hotel' 
          }); 
          setChatMessage(""); 
      } catch (e) { showToast(e.message, 'error'); } 
  };
  
  // --- INICIO MOTOR DE CANJES ---
  const handleApproveRequest = async (req) => { 
    setIsProcessing(true); 
    try { 
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'requests', req.id), { 
          status: 'Aprobado',
          updatedAt: new Date()
        }); 
        showToast("Canje aprobado exitosamente.", "success");
    } catch (e) { 
        showToast(e.message, 'error'); 
    } finally { 
        setIsProcessing(false); 
    }
  }; 

  const handleRejectRequest = async (req) => { 
    setIsProcessing(true); 
    try { 
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'requests', req.id), { 
          status: 'Rechazado',
          updatedAt: new Date()
        }); 
        showToast("Canje rechazado. El ticket ha sido cerrado.", "success");
        
        // Nota Arquitectónica: Si el sistema descontó los puntos del huésped en el momento 
        // en que hizo clic en "Solicitar", deberías sumar una línea aquí para devolverle 
        // los puntos a su perfil usando updateDoc en la colección de 'guests'.
    } catch (e) { 
        showToast(e.message, 'error'); 
    } finally { 
        setIsProcessing(false); 
    }
  };
  // --- INICIO MOTOR: PUNTOS DISCRECIONALES ---
  const handleManualPoints = async (guestId, currentPoints) => {
    const pointsStr = window.prompt("Ingrese la cantidad de puntos a inyectar (Use el signo menos '-' adelante si desea descontar puntos):");
    if (!pointsStr) return;
    
    const pointsToAdjust = parseInt(pointsStr);
    if (isNaN(pointsToAdjust) || pointsToAdjust === 0) {
       showToast("Por favor ingrese un número válido distinto de cero.", "error");
       return;
    }
    
    const concept = window.prompt("Escriba el concepto de este ajuste (Ej: Atención por queja, Premio VIP):") || "Ajuste manual de cortesía";
    
    setIsProcessing(true);
    try {
       // 1. Impactar saldo en el perfil del socio
       await updateDoc(doc(db, `/artifacts/${appId}/public/data/guests`, guestId), {
           points: currentPoints + pointsToAdjust,
           lastActivityAt: serverTimestamp() // <-- CIRUGÍA: RELOJ DE ACTIVIDAD
       });
       
       // 2. Registrar la auditoría en la bandeja para que el Admin sepa quién regaló qué
       await addDoc(collection(db, `/artifacts/${appId}/public/data/requests`), {
           type: 'Cortesía',
           status: 'Entregado',
           guestId: guestId,
           guestName: selectedGuest.name,
           benefitTitle: concept,
           points: pointsToAdjust,
           createdAt: serverTimestamp(),
           deliveredAt: serverTimestamp(),
           hotelId: selectedGuest.registeredAtHotel || 'Global'
       });
       
       showToast(`Se han ${pointsToAdjust > 0 ? 'acreditado' : 'descontado'} ${Math.abs(pointsToAdjust)} puntos.`, "success");
    } catch(e) {
       showToast("Error al ajustar puntos: " + e.message, "error");
    } finally {
       setIsProcessing(false);
    }
  };
  // --- FIN MOTOR ---
  // --- FIN MOTOR DE CANJES ---
  const handleMarkDelivered = async (req) => { 
    setIsProcessing(true); 
    try { 
        // Buscamos por ID o, como red de seguridad para tickets viejos, por Título
        const b = benefits.find(i => i.id === req.benefitId || i.title === req.benefitTitle); 
        if (b && b.stock > 0) {
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'benefits', b.id), { stock: b.stock - 1 }); 
        }
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'requests', req.id), { deliveredAt: serverTimestamp() });
        showToast("Marcado como entregado y stock actualizado", "success");
    } catch (e) { 
        showToast(e.message, 'error'); 
    } finally { 
        setIsProcessing(false); 
    }
}; 
    const handleDelete = async (col, id) => { 
      setIsProcessing(true); 
      try { await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', col, id)); showToast("Eliminado con éxito."); } catch (e) { showToast(e.message, 'error'); } finally { setIsProcessing(false); } 
  };
  
  const handleCreateGeneralChat = async () => { 
      if (!newChatMsg.trim() || !currentUser) return; 
      setIsProcessing(true); 
      try { 
          // Buscamos si ya existe un chat general
          let existing = requests.find(r => r.type === 'MENSAJE' && (r.guestId === currentUser.id || r.dni === currentUser.dni));
          if (existing) {
              await updateDoc(doc(db, `artifacts/${appId}/public/data/requests`, existing.id), { 
                  messages: [...(existing.messages || []), { sender: 'guest', text: newChatMsg.trim(), timestamp: Date.now() }], 
                  unreadByAdmin: true 
              });
          } else {
              await addDoc(collection(db, `artifacts/${appId}/public/data/requests`), { 
                  type: 'MENSAJE', guestId: currentUser.id, guestName: currentUser.name, dni: currentUser.dni, 
                  status: 'APROBADO', createdAt: serverTimestamp(), unreadByAdmin: true, hotelId: guestLocation, 
                  messages: [{ sender: 'guest', text: newChatMsg.trim(), timestamp: Date.now() }], rating: 0, benefitTitle: 'Chat Directo' 
              }); 
          }
          showToast("Mensaje enviado"); setShowModal(null); setNewChatMsg(""); 
      } catch (e) { showToast(e.message, 'error'); } finally { setIsProcessing(false); } 
  };
  
  const togglePreference = async (tag) => { 
      let updatedPrefs = currentUser.preferences || []; 
      updatedPrefs = updatedPrefs.includes(tag) ? updatedPrefs.filter(p => p !== tag) : [...updatedPrefs, tag]; 
      try { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'guests', currentUser.id), { preferences: updatedPrefs }); showToast("Preferencia Guardada"); } catch (e) { showToast(e.message, 'error'); } 
  };
  
  const handleFileUpload = (e) => { 
      const file = e.target.files[0]; 
      if (file) { 
          const reader = new FileReader(); 
          reader.onloadend = async () => { 
              const logo = reader.result; 
              setConfig({...config, logo}); 
              try { await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'config'), {...config, logo}); showToast("Identidad actualizada."); } catch (e) { showToast(e.message, 'error'); } 
          }; 
          reader.readAsDataURL(file); 
      } 
  };
  
  const addPrefCategory = () => { 
      if (!newPrefCategory.trim()) return; 
      const updated = { ...preferenceSettings, [newPrefCategory.trim()]: [] }; 
      setPreferenceSettings(updated); setNewPrefCategory(""); 
      if(db && user) setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'preferences'), updated); 
  };
  
  const removePrefCategory = (cat) => { 
      const updated = { ...preferenceSettings }; delete updated[cat]; 
      setPreferenceSettings(updated); 
      if(db && user) setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'preferences'), updated); 
  };
  
  const addPrefTag = (cat) => { 
      if (!newPrefTag[cat] || !newPrefTag[cat].trim()) return; 
      const updated = { ...preferenceSettings, [cat]: [...preferenceSettings[cat], newPrefTag[cat].trim()] }; 
      setPreferenceSettings(updated); setNewPrefTag(prev => ({ ...prev, [cat]: "" })); 
      if(db && user) setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'preferences'), updated); 
  };
  
  const removePrefTag = (cat, tagToRemove) => { 
      const updated = { ...preferenceSettings, [cat]: preferenceSettings[cat].filter(t => t !== tagToRemove) }; 
      setPreferenceSettings(updated); 
      if(db && user) setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'preferences'), updated); 
  };

  const handleLogoutAdmin = () => {
    setStaffSession(null);
    setAdminLoginData({ email: '', pin: '' });
    navigate('/');
  };

  const handleLogoutGuest = () => {
    setCurrentUser(null);
    setLoginData({ identifier: '', pin: '' });
    navigate('/');
  };

 const handleLeadSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    if(!data.name || !data.email || !data.hotel) {
       return showToast("Por favor, complete todos los campos.", "error");
    }

    // --- INICIO NUEVO MOTOR CRM GOOGLE SHEETS ---
    try {
        await fetch("https://script.google.com/macros/s/AKfycbwlbrKL8zppmnCesWbkQBS29PjIzsMiR32UNIT_RF-xBruKt908ht0OVCpVdUiAHjc/exec", {
            method: "POST",
            mode: "no-cors",
            headers: { "Content-Type": "text/plain;charset=utf-8" },
            body: JSON.stringify({
                hotelName: data.hotel,      // <-- AHORA COINCIDE CON TU SCRIPT
                adminName: data.name,       // <-- AHORA COINCIDE CON TU SCRIPT
                email: data.email,
                phone: data.phone || "",
                plan: "No Aplica",          // <-- En el simulador no eligen plan
                etapa: "Simulador / Prospecto" // <-- ETIQUETA CLAVE
            })
        });
    } catch (error) {
        console.error("Error CRM:", error);
    }
    // --- FIN NUEVO MOTOR CRM GOOGLE SHEETS --- 
    setIsProcessing(true);
    try {
        await addDoc(collection(db, `/artifacts/${appId}/public/data/leads`), { 
            ...data, 
            status: 'NUEVO',
            createdAt: serverTimestamp() 
        });
        showToast("¡Datos recibidos! Iniciando simulación...", "success");
        setTimeout(() => {
            window.location.href = '/?demo=true';
        }, 1500);
    } catch (err) { 
        showToast(err.message, 'error'); 
    } finally { 
        setIsProcessing(false); 
    }
  };


  if (loading) return (
      <div className="h-screen bg-zinc-950 flex flex-col items-center justify-center text-amber-500 uppercase tracking-widest text-sm">
          <Loader2 className="animate-spin text-amber-500 mb-4" size={40} />
          Iniciando...
      </div>
  );

return (
    <div className="h-screen bg-zinc-50 flex flex-col overflow-hidden relative font-['Montserrat'] text-[14px]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;900&display=swap');
        body { -webkit-font-smoothing: antialiased; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        label { color: #52525b; font-weight: 500; text-transform: uppercase; font-size: 11px; letter-spacing: 0.1em; margin-bottom: 6px; display: block; }
        input, select, textarea { border-bottom: 1px solid #e4e4e7; background: transparent; padding: 12px 4px; outline: none; width: 100%; transition: all 0.3s; font-weight: 400; }
        input:focus, select:focus, textarea:focus { border-color: ${currentBranding.primaryColor}; background: rgba(0,0,0,0.01); }
        .btn-premium { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .btn-premium:active { transform: scale(0.96); }
      `}</style>
      
      {isDemoMode && (
        <div className="bg-gradient-to-r from-amber-400 to-amber-600 text-zinc-950 text-center py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] z-[999] shadow-md flex items-center justify-center gap-2">
          <AlertTriangle size={14}/> Entorno de Simulación PaxFidely <AlertTriangle size={14}/>
        </div>
      )}
      
      {isProcessing && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-md z-[9999] flex flex-col items-center justify-center text-white animate-in fade-in duration-300">
          <Loader2 className="animate-spin text-amber-500 mb-4" size={48} strokeWidth={1.5} />
          <h2 className="text-sm font-light uppercase tracking-[0.3em] animate-pulse">Sincronizando...</h2>
        </div>
      )}
      
      {toast && (
        <div className={`fixed top-8 left-1/2 -translate-x-1/2 z-[1000] px-6 py-3 rounded-full flex items-center gap-3 shadow-xl animate-in slide-in-from-top-5 duration-300 ${toast.type === 'error' ? 'bg-red-600' : 'bg-zinc-900'} text-white`}>
          {toast.type === 'error' ? <XCircle size={18} /> : <CheckCircle size={18} className="text-green-400" />}
          <span className="text-xs font-medium uppercase tracking-widest leading-none">{toast.msg}</span>
        </div>
      )}

      <Routes>
        <Route path="/onboarding" element={<Onboarding />} />
           {/* --- 1. PANTALLA PRINCIPAL (VENTAS O ACCESO DEMO) --- */}
        <Route path="/" element={
          isDemoMode || activeTenantId ? (
            /* PANTALLA DE ACCESO (POST-FORMULARIO O VIA URL DIRECTA) */
            <div className="flex-1 bg-zinc-950 flex flex-col items-center justify-center p-6 text-center relative overflow-hidden h-screen animate-in fade-in duration-1000">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(197,160,89,0.1),_transparent_60%)]"></div>
              
              {activeTenantId && tenantData ? (
                 <div className="absolute top-10 w-full flex justify-center animate-bounce">
                    <span className="bg-amber-500 text-black px-6 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-[0_0_20px_rgba(245,158,11,0.4)] flex items-center gap-2"><Sparkles size={14}/> Plataforma {tenantData.hotelName} Activa</span>
                 </div>
              ) : (
                 <div className="absolute top-10 w-full flex justify-center animate-bounce">
                    <span className="bg-amber-500 text-black px-6 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-[0_0_20px_rgba(245,158,11,0.4)] flex items-center gap-2"><Sparkles size={14}/> Simulador Desbloqueado con Éxito</span>
                 </div>
              )}

              <div className="relative z-10 w-full max-w-2xl flex flex-col items-center mt-12">
                
                {/* SI HAY UN TENANT ACTIVO MOSTRAMOS SU LOGO AQUÍ */}
                {activeTenantId && tenantData && (
                   <img src={tenantData.logoUrl || currentBranding.logo} alt="Logo" className="h-16 mb-8 object-contain" />
                )}

                <h1 className="text-3xl md:text-4xl text-white mb-2 uppercase tracking-[0.2em] font-light">Elija su Perfil de Acceso</h1>
                <p className="text-zinc-400 text-xs font-medium tracking-widest uppercase mb-16 max-w-lg">
                  El sistema tiene dos caras. Explore la experiencia del cliente o gestione como recepción.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
                  <button onClick={()=>navigate(activeTenantId ? `/guest?hotel=${activeTenantId}` : '/guest')} className="group flex flex-col items-center gap-6 bg-zinc-900/40 p-10 rounded-3xl border border-white/5 hover:border-amber-500/50 transition-all duration-500 hover:bg-zinc-900/80 shadow-lg relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-amber-500/10 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-700"></div>
                    <div className="w-20 h-20 rounded-full border border-zinc-700 flex items-center justify-center group-hover:border-amber-500 transition-colors bg-zinc-950 z-10">
                      <User className="text-zinc-500 group-hover:text-amber-500 transition-colors" size={32} strokeWidth={1.5} />
                    </div>
                    <div className="z-10"><span className="text-white text-sm font-bold uppercase tracking-[0.2em] block mb-2">Portal Huésped</span><span className="text-zinc-500 text-[10px] uppercase tracking-widest">Ver catálogo y puntos</span></div>
                  </button>
                  <button onClick={()=>navigate(activeTenantId ? `/admin?hotel=${activeTenantId}` : '/admin')} className="group flex flex-col items-center gap-6 bg-zinc-900/40 p-10 rounded-3xl border border-white/5 hover:border-white/30 transition-all duration-500 hover:bg-zinc-900/80 shadow-lg relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -mr-10 -mt-10 group-hover:scale-150 transition-transform duration-700"></div>
                    <div className="w-20 h-20 rounded-full border border-zinc-700 flex items-center justify-center group-hover:border-white transition-colors bg-zinc-950 z-10">
                      <ShieldCheck className="text-zinc-500 group-hover:text-white transition-colors" size={32} strokeWidth={1.5} />
                    </div>
                    <div className="z-10"><span className="text-white text-sm font-bold uppercase tracking-[0.2em] block mb-2">Hub Staff</span><span className="text-zinc-500 text-[10px] uppercase tracking-widest">Validar y gestionar</span></div>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* LANDING DE VENTAS DE ALTA GAMA CON SELECTOR DE SEGMENTO - PAXFIDELY V25.0 */
            <div className="flex-1 bg-[#0a0a0a] flex flex-col overflow-y-auto no-scrollbar relative text-zinc-100 font-sans">
              
              {(() => {               
                return (
                  <>
                    {/* 1. BARRA DE NAVEGACIÓN PREMIUM */}
                    <nav className="p-6 md:px-12 flex justify-between items-center relative border-b border-zinc-900 bg-black/40 backdrop-blur-md sticky top-0 z-50">
                      <div className="flex items-center gap-3">
                        <div className="text-amber-500 font-bold text-xl tracking-widest uppercase">
                          PAX<span className="text-white">FIDELY</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <button onClick={()=>document.getElementById('pricing-matrix').scrollIntoView({behavior: 'smooth'})} className="text-xs font-medium text-zinc-400 hover:text-white uppercase tracking-widest transition-colors hidden sm:block">
                          Planes y Precios
                        </button>
                        <button onClick={()=>document.getElementById('demo-form').scrollIntoView({behavior: 'smooth'})} className="px-5 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800 transition-all">
                          Acceso Staff
                        </button>
                      </div>
                    </nav>

                    {/* 2. HERO SECTION: IMPACTO INICIAL */}
                    <div className="flex-1 flex flex-col justify-center px-6 md:px-12 relative z-10 py-24 max-w-6xl mx-auto text-center md:text-left">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,_rgba(217,119,6,0.08),_transparent_50%)] -z-10"></div>
                      
                      <div className="inline-block mx-auto md:mx-0 mb-6 px-4 py-2 border border-amber-500/20 rounded-full bg-amber-500/5 w-max shadow-lg">
                        <span className="text-[9px] font-semibold tracking-[0.2em] uppercase text-amber-500 flex items-center gap-2">
                          <Sparkles size={12}/> EL ESTÁNDAR DE LEALTAD HOTELERA B2B
                        </span>
                      </div>
                      
                      <h1 className="text-4xl md:text-6xl font-light tracking-tight leading-[1.1] mb-6 text-white">
                        Fidelice a sus huéspedes.<br/>
                        <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-600">
                          Potencie sus reservas directas.
                        </span>
                      </h1>
                      
                      <p className="text-zinc-400 text-sm md:text-base font-light tracking-wide leading-relaxed max-w-2xl mb-10 mx-auto md:mx-0">
                        La solución SaaS de alta gama completamente automatizada. Unifique un Portal de Huésped de ultra-lujo con un Hub Operativo autónomo para gestionar cashback, CRM y campañas de marketing sin intervención humana.
                      </p>
                      
                      <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                        <button onClick={()=>document.getElementById('demo-form').scrollIntoView({behavior: 'smooth'})} className="px-8 py-4 bg-amber-500 text-black rounded-xl text-[11px] font-black uppercase tracking-[0.2em] hover:bg-amber-400 transition-all shadow-[0_0_30px_rgba(245,158,11,0.2)] hover:-translate-y-0.5">
                          Probar Simulador Gratis
                        </button>
                        <button onClick={()=>document.getElementById('pricing-matrix').scrollIntoView({behavior: 'smooth'})} className="px-8 py-4 bg-transparent text-white border border-zinc-800 rounded-xl text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-zinc-900 transition-all">
                          Ver Planes V25.0
                        </button>
                      </div>
                    </div>

                    {/* 3. BLOQUE EXPERTO: ARGUMENTACIÓN CIENTÍFICA Y FINANCIERA */}
                    <div className="py-20 px-6 md:px-12 border-t border-zinc-900 bg-zinc-950/50">
                      <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                          <h2 className="text-2xl font-light uppercase tracking-widest text-white">¿Por qué PaxFidely?</h2>
                          <p className="text-zinc-500 text-[10px] font-bold tracking-widest uppercase mt-2">La matemática detrás del aumento de utilidades de su propiedad</p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                          <div className="bg-[#121212] p-6 rounded-2xl border border-zinc-900 hover:border-zinc-800 transition-all">
                            <TrendingUp className="text-amber-500 mb-4" size={24} />
                            <h4 className="text-sm font-bold uppercase tracking-wider mb-2 text-white">Margen Puro (Anti-OTA)</h4>
                            <p className="text-zinc-400 text-xs font-light leading-relaxed">Deje de ceder entre el 18% y 25% de sus ingresos a Booking o Expedia. PaxFidely automatiza el retorno del huésped directo con una tarifa plana mensual sin comisiones.</p>
                          </div>
                          <div className="bg-[#121212] p-6 rounded-2xl border border-zinc-900 hover:border-zinc-800 transition-all">
                            <Clock className="text-amber-500 mb-4" size={24} />
                            <h4 className="text-sm font-bold uppercase tracking-wider mb-2 text-white">Activación Zero-Setup</h4>
                            <p className="text-zinc-400 text-xs font-light leading-relaxed">Sin llamadas comerciales ni integraciones técnicas de semanas. Nuestro motor multi-tenant aprovisiona el entorno privado de su hotel de forma autónoma en menos de 2 minutos.</p>
                          </div>
                          <div className="bg-[#121212] p-6 rounded-2xl border border-zinc-900 hover:border-zinc-800 transition-all">
                            <Fingerprint className="text-amber-500 mb-4" size={24} />
                            <h4 className="text-sm font-bold uppercase tracking-wider mb-2 text-white">Isla Privada Multi-Tenant</h4>
                            <p className="text-zinc-400 text-xs font-light leading-relaxed">Aislamiento hermético de datos bajo el identificador único `tenant-hotelId`. La base de datos de sus huéspedes y perfiles de CRM está estrictamente blindada en la nube de Google.</p>
                          </div>
                        </div>
                      </div>
                    </div>

                  {/* 4. SECCIÓN: OPTIMIZADOR FINANCIERO ACTIVO EN TIEMPO REAL */}
<div id="financial-optimizer" className="py-24 px-6 md:px-12 border-t border-zinc-900 bg-black relative">
  <div className="max-w-4xl mx-auto text-center">
    <h2 className="text-3xl font-light uppercase tracking-widest text-white">Optimizador Financiero</h2>
    <p className="text-zinc-500 text-[10px] font-bold tracking-widest uppercase mt-2 mb-12">Calcule el capital exacto que hoy está cediendo a las OTAs</p>
    
    <div className="bg-[#0c0c0c] border border-zinc-850 p-8 md:p-12 rounded-[2.5rem] text-left shadow-2xl max-w-3xl mx-auto">
      <div className="space-y-10">
        {/* Slider */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Tamaño de la Propiedad</span>
            <span className="text-4xl font-extralight text-amber-500 tracking-tighter">{roomCount} <span className="text-[10px] uppercase font-bold text-zinc-600 tracking-wider ml-1">Habitaciones</span></span>
          </div>
          <input 
            type="range" min="10" max="250" step="5"
            value={roomCount} 
            onChange={(e) => setRoomCount(Number(e.target.value))}
            className="w-full h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-amber-500"
          />
        </div>

        {/* Métricas dinámicas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-8 border-t border-zinc-900">
          <div className="p-6 bg-zinc-950 rounded-2xl border border-zinc-900">
            <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest block mb-1">Comisiones anuales perdidas (18% OTA)</span>
            <span className="text-3xl font-light text-zinc-300 tracking-tight">
              ${Math.round(roomCount * 365 * 0.65 * 90 * 0.18).toLocaleString('de-DE')} USD
            </span>
            <span className="text-[8px] text-zinc-600 block mt-2 italic">*Cálculo estimado sobre 65% Ocupación y ADR de $90 USD.</span>
          </div>

          <div className="p-6 bg-amber-500/[0.02] rounded-2xl border border-amber-500/20 flex flex-col justify-between">
            <div>
              <span className="text-[9px] font-bold text-amber-500 uppercase tracking-widest block mb-1">Impacto de Retorno PaxFidely</span>
              <span className="text-3xl font-bold text-white tracking-tight">100% Amortizado</span>
            </div>
            <p className="text-zinc-400 text-xs font-light leading-relaxed mt-2">
              Su tarifa plana se recupera íntegramente en los primeros <span className="text-amber-400 font-bold">2.5 días</span> de operación de cada mes. El resto es margen puro para el hotel.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

                    {/* 4. EL EMBUDO INTELIGENTE: SIMULADOR O LOGIN DE CLIENTES ACTIVOS */}
            <div id="demo-form" className="py-20 px-6 md:px-12 border-t border-zinc-900 bg-zinc-950">
              <div className="max-w-xl mx-auto text-center relative z-10">
                <h2 className="text-3xl font-light uppercase tracking-widest mb-3 text-white">
                  {isFormLoginMode ? 'Acceso al Hub Staff' : 'Acceso al Simulador'}
                </h2>
                <p className="text-zinc-500 text-[10px] font-bold tracking-[0.2em] uppercase mb-12">
                  {isFormLoginMode ? 'Introduzca sus credenciales oficiales de administración' : 'Experimente el Hub Staff en tiempo real de forma inmediata'}
                </p>
                
                <form 
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (isFormLoginMode) {
                      // =========================================================
                      // LÓGICA AUTOMÁTICA PARA CLIENTES ACTIVOS (LOGIN REAL)
                      // =========================================================
                      const formData = new FormData(e.target);
                      const emailInput = formData.get('email')?.trim().toLowerCase();
                      const pinInput = formData.get('pin')?.trim();

                      setIsProcessing(true);
                      // Buscamos si es tu bypass maestro absoluto
                      if (emailInput === 'master@loyalty.com' && pinInput === '9999') {
                        setStaffSession({ id: 'master', name: 'System', role: 'System', hotelId: 'Todos' });
                        setIsProcessing(false);
                        navigate('/admin');
                        return;
                      }

                      // Buscamos al staff en tu colección sincrónica de Firebase
                      const staffEncontrado = staff.find(s => s.email?.toLowerCase() === emailInput && s.pin === pinInput);
                      if (staffEncontrado) {
                        // VALIDACIÓN DE SEDE ACTIVA
                        const hotelAsignado = hotels.find(h => h.id === staffEncontrado.hotelId);
                        const esStaffLocal = staffEncontrado.role !== 'Superadmin' && staffEncontrado.role !== 'System';
                        
                        if (config.isChainMode && esStaffLocal && hotelAsignado && hotelAsignado.active === false) {
                          setIsProcessing(false);
                          return showToast("Acceso suspendido: Su sede asignada no se encuentra operativa.", "error");
                        }

                        setStaffSession(staffEncontrado);
                        showToast("Autenticación exitosa. Conectando con su Isla Privada...", "success");
                        const hotelParam = staffEncontrado.hotelId !== 'Todos' ? `?hotel=${staffEncontrado.hotelId}` : '';
                        setTimeout(() => {
                          navigate(`/admin${hotelParam}`);
                        }, 1000);
                      } else {
                        showToast("Credenciales de operador incorrectas o cuenta inexistente.", "error");
                      }
                      setIsProcessing(false);
                    } else {
                      // Ejecuta tu handleLeadSubmit original de prospección si es un lead nuevo
                      handleLeadSubmit(e);
                    }
                  }} 
                  className="bg-[#111] p-8 md:p-10 rounded-[2.5rem] border border-zinc-900 text-left space-y-5 shadow-2xl relative"
                >
                  
                  {/* Campo Email: Requerido tanto para Lead como para Cliente Activo */}
                  <div className="space-y-2">
                    <label className="text-zinc-400 text-[10px] font-bold tracking-widest uppercase">Email Corporativo</label>
                    <input type="email" name="email" required className="w-full bg-[#070707] border border-zinc-800 rounded-xl p-4 text-white text-xs outline-none focus:border-amber-500 transition-all" placeholder="gerencia@hotel.com"/>
                  </div>

                  {/* Campos Dinámicos: Solo se muestran si es un PROSPECTO NUEVO */}
                  {!isFormLoginMode ? (
                    <>
                      <div className="space-y-2 animate-in fade-in duration-300">
                        <label className="text-zinc-400 text-[10px] font-bold tracking-widest uppercase">Nombre Completo del Gerente</label>
                        <input type="text" name="name" required className="w-full bg-[#070707] border border-zinc-800 rounded-xl p-4 text-white text-xs outline-none focus:border-amber-500 transition-all" placeholder="Ej: Carlos Martínez"/>
                      </div>
                      <div className="space-y-2 animate-in fade-in duration-300">
                        <label className="text-zinc-400 text-[10px] font-bold tracking-widest uppercase">WhatsApp (Código País + Celular)</label>
                        <input type="tel" name="phone" required className="w-full bg-[#070707] border border-zinc-800 rounded-xl p-4 text-white text-xs outline-none focus:border-amber-500 transition-all" placeholder="Ej: +54 9 11 1234 5678"/>
                      </div>
                      <div className="space-y-2 animate-in fade-in duration-300">
                        <label className="text-zinc-400 text-[10px] font-bold tracking-widest uppercase">Nombre del Hotel / Cadena</label>
                        <input type="text" name="hotel" required className="w-full bg-[#070707] border border-zinc-800 rounded-xl p-4 text-white text-xs outline-none focus:border-amber-500 transition-all" placeholder="Ej: Amérian Resort"/>
                      </div>
                    </>
                  ) : (
                    /* Campo PIN: Solo se muestra si es un CLIENTE ACTIVO intentando loguearse */
                    <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
                      <label className="text-zinc-400 text-[10px] font-bold tracking-widest uppercase">PIN de Acceso Operador (4 dígitos)</label>
                      <input type="password" name="pin" maxLength="4" required className="w-full bg-[#070707] border border-zinc-800 rounded-xl p-4 text-white text-center text-xl font-mono tracking-[0.4em] outline-none focus:border-amber-500 transition-all" placeholder="••••"/>
                    </div>
                  )}
                  
                  <button type="submit" disabled={isProcessing} className="w-full py-4.5 bg-amber-500 text-black rounded-xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-amber-400 transition-all mt-6 shadow-xl disabled:opacity-50 flex justify-center items-center gap-3">
                    {isProcessing ? <Loader2 size={16} className="animate-spin" /> : (isFormLoginMode ? 'Ingresar al Hub Staff' : 'Desbloquear Demo Interactiva')}
                  </button>

                  {/* ENLACE DISCRETO DE MUTACIÓN DE ESTADO DE FORMULARIO */}
                  <div className="text-center pt-4 border-t border-zinc-900/60 mt-4">
                    <button
                      type="button"
                      onClick={() => setIsFormLoginMode(!isFormLoginMode)}
                      className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-amber-500 underline decoration-1 underline-offset-4 transition-all"
                    >
                      {isFormLoginMode ? '← Registrarme para probar el Simulador' : 'Ya soy cliente de PaxFidely →'}
                    </button>
                  </div>

                </form>
              </div>

            </div>

                    {/* 5. SECCIÓN PRECIOS: MATRIZ INMUTABLE V26.0 (SaaS) */}
                    {/* --- NUEVA SECCIÓN DE PRECIOS ESTRATÉGICA --- */}
      <section id="pricing-matrix" className="py-24 bg-zinc-950 relative border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-light tracking-tighter text-white mb-6">
              Una inversión que <span className="font-medium text-amber-500">escala con usted</span>
            </h2>
            <p className="text-zinc-400 text-lg font-light mb-8">
              Elija el nivel tecnológico que su hotel necesita. Sin contratos a largo plazo. 
              Cancele o escale su sede en cualquier momento.
            </p>

            {/* SWITCH INTERACTIVO: INDEPENDIENTE VS CADENA */}
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="bg-white/5 p-1.5 rounded-full border border-white/10 flex inline-flex relative">
                <button 
                  onClick={() => setIsChainPricing(false)}
                  className={`relative z-10 px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest transition-all duration-300 ${!isChainPricing ? 'text-zinc-950 bg-white shadow-md' : 'text-zinc-400 hover:text-white'}`}
                >
                  Hotel Independiente
                </button>
                <button 
                  onClick={() => setIsChainPricing(true)}
                  className={`relative z-10 px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest transition-all duration-300 ${isChainPricing ? 'text-zinc-950 bg-amber-500 shadow-md' : 'text-zinc-400 hover:text-white'}`}
                >
                  Cadenas Hoteleras (Múltiples Sedes)
                </button>
              </div>
              {isChainPricing && (
                <span className="text-[10px] text-amber-500/80 uppercase tracking-widest font-bold bg-amber-500/10 px-3 py-1 rounded-md animate-pulse">
                  Tarifa mayorista: Disminuya el costo unitario al expandir su red
                </span>
              )}
            </div>
          </div>

          {/* GRILLA DE PLANES */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            
            {/* PLAN 1: ESENCIAL */}
            <div className="bg-white/5 rounded-2xl p-8 border border-white/10 flex flex-col relative transition-all hover:bg-white/10 hover:border-white/20">
              <div className="mb-8">
                <h3 className="text-white text-xl tracking-widest uppercase font-light mb-2">
                  {isChainPricing ? 'SEDE CONNECT' : 'BOUTIQUE'}
                </h3>
                <p className="text-zinc-500 text-sm h-10">La base operativa para digitalizar la lealtad de sus huéspedes.</p>
              </div>
              
              <div className="mb-8 border-b border-white/10 pb-8">
                <div className="flex items-baseline">
                  <span className="text-5xl font-light tracking-tighter text-white">$79</span>
                  <span className="text-zinc-500 text-xs font-medium tracking-widest uppercase ml-2">USD / mes {isChainPricing && '(1ra Sede)'}</span>
                </div>
                {isChainPricing && (
                  <div className="mt-4 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex flex-col items-center justify-center shadow-inner">
                    <span className="text-amber-500/80 text-[8px] font-bold uppercase tracking-widest mb-1">A partir de 2 sedes:</span>
                    <span className="text-amber-400 text-2xl font-bold tracking-tight">$59 <span className="text-[9px] text-amber-500 font-medium uppercase tracking-widest">/ mes por hotel</span></span>
                  </div>
                )}
              </div>

              <ul className="flex flex-col gap-4 mb-10 flex-1">
                <li className="flex items-center gap-3 text-zinc-300 text-sm"><Check size={16} className="text-white/30" /> Motor de Puntos Básico</li>
                <li className="flex items-center gap-3 text-zinc-300 text-sm"><Check size={16} className="text-white/30" /> Catálogo de Recompensas</li>
                <li className="flex items-center gap-3 text-zinc-300 text-sm"><Check size={16} className="text-white/30" /> Chat con Recepción y QR</li>
                <li className="flex items-center gap-3 text-zinc-600 text-sm"><X size={16} className="text-zinc-800" /> Sin Módulo de Marketing</li>
              </ul>
              <button onClick={()=>navigate('/onboarding')} className="w-full py-4 rounded-xl border border-white/20 text-white font-bold tracking-widest uppercase text-[10px] transition-all hover:bg-white hover:text-black">
                Comenzar Prueba
              </button>
            </div>

            {/* PLAN 2: PROACTIVO (MÁS POPULAR) */}
            <div className="bg-zinc-900 rounded-2xl p-8 border border-white/20 shadow-2xl relative flex flex-col transform md:-translate-y-4">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-zinc-950 text-[9px] font-black uppercase tracking-[0.2em] px-4 py-1.5 rounded-full shadow-lg">
                Más Popular
              </div>
              <div className="mb-8">
                <h3 className="text-white text-xl tracking-widest uppercase font-medium mb-2">
                  {isChainPricing ? 'SEDE GROWTH' : 'PREMIUM'}
                </h3>
                <p className="text-zinc-400 text-sm h-10">Herramientas activas para impulsar ventas y perfilar clientes.</p>
              </div>

              <div className="mb-8 border-b border-white/10 pb-8">
                <div className="flex items-baseline">
                  <span className="text-5xl font-light tracking-tighter text-white">$149</span>
                  <span className="text-zinc-500 text-xs font-medium tracking-widest uppercase ml-2">USD / mes {isChainPricing && '(1ra Sede)'}</span>
                </div>
                {isChainPricing && (
                  <div className="mt-4 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex flex-col items-center justify-center shadow-inner">
                    <span className="text-amber-500/80 text-[8px] font-bold uppercase tracking-widest mb-1">A partir de 2 sedes:</span>
                    <span className="text-amber-400 text-2xl font-bold tracking-tight">$99 <span className="text-[9px] text-amber-500 font-medium uppercase tracking-widest">/ mes por hotel</span></span>
                  </div>
                )}
              </div>

              <ul className="flex flex-col gap-4 mb-10 flex-1">
                <li className="flex items-center gap-3 text-white font-medium text-sm"><Check size={16} className="text-white" /> Todo lo del plan anterior</li>
                <li className="flex items-center gap-3 text-white text-sm"><Check size={16} className="text-amber-500" /> Módulo de Marketing In-App</li>
                <li className="flex items-center gap-3 text-white text-sm"><Check size={16} className="text-amber-500" /> CRM de Preferencias</li>
                <li className="flex items-center gap-3 text-zinc-600 text-sm"><X size={16} className="text-zinc-800" /> Sin Marca Blanca Total</li>
              </ul>
              <button onClick={()=>navigate('/onboarding')} className="w-full py-4 rounded-xl bg-white text-zinc-950 font-bold tracking-widest uppercase text-[10px] transition-all hover:bg-zinc-200">
                Comenzar Prueba
              </button>
            </div>

            {/* PLAN 3: ABSOLUTO */}
            <div className="bg-white/5 rounded-2xl p-8 border border-white/10 flex flex-col relative transition-all hover:bg-white/10 hover:border-white/20">
              {isChainPricing && (
                 <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-zinc-950 text-[9px] font-black uppercase tracking-[0.2em] px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                   El Estándar Corporativo
                 </div>
              )}
              <div className="mb-8 mt-2">
                <h3 className="text-amber-500 text-xl tracking-widest uppercase font-light mb-2">
                  {isChainPricing ? 'SEDE ENTERPRISE' : 'SIGNATURE'}
                </h3>
                <p className="text-zinc-500 text-sm h-10">Autonomía de marca absoluta e integración de nivel bancario.</p>
              </div>

              <div className="mb-8 border-b border-white/10 pb-8">
                <div className="flex items-baseline">
                  <span className="text-5xl font-light tracking-tighter text-white">$249</span>
                  <span className="text-zinc-500 text-xs font-medium tracking-widest uppercase ml-2">USD / mes {isChainPricing && '(1ra Sede)'}</span>
                </div>
                {isChainPricing && (
                  <div className="mt-4 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex flex-col items-center justify-center shadow-inner">
                    <span className="text-amber-500/80 text-[8px] font-bold uppercase tracking-widest mb-1">A partir de 2 sedes:</span>
                    <span className="text-amber-400 text-2xl font-bold tracking-tight">$189 <span className="text-[9px] text-amber-500 font-medium uppercase tracking-widest">/ mes por hotel</span></span>
                  </div>
                )}
              </div>

              <ul className="flex flex-col gap-4 mb-10 flex-1">
                <li className="flex gap-3 text-zinc-300 text-sm items-start"><Check size={16} className="text-white/30 shrink-0 mt-0.5" /> <span>Todo lo del plan Growth</span></li>
                <li className="flex gap-3 text-zinc-300 text-sm items-start"><Check size={16} className="text-amber-500 shrink-0 mt-0.5" /> <span>Marca Blanca Total (Sin Logo Pax)</span></li>
                <li className="flex gap-3 text-zinc-300 text-sm items-start">
                  <Check size={16} className="text-amber-500 shrink-0 mt-0.5" /> 
                  <span>Acceso API para PMS <span className="block text-[9px] text-zinc-500 mt-1 italic leading-relaxed">*Requiere desarrollo de integración externo a medida.</span></span>
                </li>
              </ul>
              <button onClick={()=>navigate('/onboarding')} className="w-full py-4 rounded-xl border border-amber-500/50 text-amber-500 font-bold tracking-widest uppercase text-[10px] transition-all hover:bg-amber-500 hover:text-zinc-950">
                {isChainPricing ? 'Desplegar Red Enterprise' : 'Activar Nivel Signature'}
              </button>
            </div>

          </div>
          
          {/* AVISO PANEL GRATIS (Solo visible en modo Cadena) */}
          {isChainPricing && (
            <div className="mt-12 text-center max-w-3xl mx-auto bg-amber-500/10 border border-amber-500/20 p-5 rounded-2xl">
              <p className="text-amber-400 text-xs font-medium leading-relaxed">
                * El Panel de Control Global para la <strong>Cadena Hotelera</strong> es 100% Gratuito. Los precios detallados aplican como licencia operativa por cada sede conectada. La sede fundadora abona la tarifa base individual ($79/$149/$249) hasta sumar un segundo hotel a la red, momento en que se activa automáticamente la tarifa mayorista para todas las sedes.
              </p>
            </div>
          )}

        </div>
      </section>

                    {/* 6. SECCIÓN: PAXFIDELY ACADEMY DINÁMICO CON VISOR DE ARTÍCULOS */}
<div id="academy" className="py-24 px-6 md:px-12 border-t border-zinc-900 bg-black relative">
  <div className="max-w-6xl mx-auto">
    <div className="flex justify-between items-end mb-12">
      <div>
        <h2 className="text-3xl font-light uppercase tracking-widest text-white">PaxFidely Academy</h2>
        <p className="text-zinc-500 text-[10px] font-bold tracking-widest uppercase mt-2">Recursos de ingeniería de lealtad para la gerencia moderna</p>
      </div>
      {/* Botón de simulación para el Administrador de Contenidos */}
      <button 
        onClick={() => {
          const titulo = prompt("Título del nuevo artículo corporativo:");
          const categoria = prompt("Categoría (Guía de Éxito / Estrategia Operativa):");
          const copete = prompt("Breve descripción para la placa:");
          const contenido = prompt("Cuerpo completo del texto educativo:");
          if(titulo && copete) {
            alert("Artículo indexado con éxito en el hub autónomo de PaxFidely Academy.");
            // Aquí la lógica conectará directamente al array de estado dinámico
          }
        }}
        className="px-4 py-2 border border-zinc-800 rounded-xl text-[9px] font-bold text-zinc-500 uppercase tracking-widest hover:border-amber-500/30 hover:text-amber-500 transition-all"
      >
        + Panel Admin Academy
      </button>
    </div>

    {/* Grilla dinámica de placas */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {/* Placa 1 */}
      <div className="group bg-[#0a0a0a] border border-zinc-850 p-8 rounded-[2.5rem] hover:border-amber-500/30 transition-all duration-300 flex flex-col justify-between cursor-pointer">
        <div>
          <span className="text-[8px] font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 px-3 py-1 rounded-full uppercase tracking-widest">Guía de Éxito</span>
          <h4 className="text-xl font-medium text-white mt-6 mb-3 tracking-tight">La Matriz de Cashback Definitiva para Hoteles de Alta Gama</h4>
          <p className="text-zinc-500 text-xs font-light leading-relaxed mb-6">Argumentos científicos sobre por qué un programa de incentivos directos duplica la recompra y liquida la dependencia con Booking.</p>
        </div>
        <div className="text-amber-500 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 group-hover:gap-4 transition-all">
          Explorar Estrategia Experta <span>→</span>
        </div>
      </div>

      {/* Placa 2 */}
      <div className="group bg-[#0a0a0a] border border-zinc-850 p-8 rounded-[2.5rem] hover:border-amber-500/30 transition-all duration-300 flex flex-col justify-between cursor-pointer">
        <div>
          <span className="text-[8px] font-bold bg-zinc-900 text-zinc-400 border border-zinc-800 px-3 py-1 rounded-full uppercase tracking-widest">Estrategia Operativa</span>
          <h4 className="text-xl font-medium text-white mt-6 mb-3 tracking-tight">Bono de Bienvenida Dinámico: Captura del 90% en Mostrador</h4>
          <p className="text-zinc-500 text-xs font-light leading-relaxed mb-6">Metodología para digitalizar al huésped analógico en el check-in usando incentivos inmediatos en piloto automático.</p>
        </div>
        <div className="text-amber-500 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 group-hover:gap-4 transition-all">
          Ver Mejores Prácticas <span>→</span>
        </div>
      </div>
    </div>
  </div>
</div>

                    {/* 6. PIE DE PÁGINA COMERCIAL */}
                    <footer className="p-12 border-t border-zinc-900 bg-black text-center text-zinc-600 text-[10px] uppercase tracking-widest">
                      <p className="mb-2">© 2026 PaxFidely SaaS. Todos los derechos reservados.</p>
                      <p className="font-light text-zinc-700">Luxury Loyalty Systems Inc.</p>
                    </footer>
                  </>
                );
              })()}

            </div>
          )
        } />

        
        {/* --- 2. PORTAL HUÉSPED (CON INTERCEPTOR DE BAJA EN TIEMPO REAL) --- */}
        <Route path="/guest" element={(() => {
          // INTERCEPTOR: Si la sede actual está desactivada, bloqueamos con Look & Feel Premium
          if (activeHotelData && activeHotelData.active === false) {
            return (
              <div className="min-h-screen w-full bg-[#0a0a0a] flex flex-col items-center justify-center p-6 text-center select-none relative font-['Montserrat']">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[100px] pointer-events-none"></div>
                <div className="relative z-10 max-w-md space-y-6 flex flex-col items-center">
                  <div className="h-16 flex items-center justify-center mb-4">
                    {activeHotelData.logo ? (
                      <img src={activeHotelData.logo} alt={activeHotelData.name} className="max-h-full object-contain grayscale opacity-70" />
                    ) : (
                      <span className="text-zinc-500 font-bold tracking-widest uppercase text-xl">{activeHotelData.name}</span>
                    )}
                  </div>
                  <div className="w-16 h-16 bg-zinc-900/80 rounded-2xl flex items-center justify-center mx-auto border border-zinc-800 shadow-xl">
                    <Lock className="text-amber-500/70 animate-pulse" size={24} />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-xl font-light text-white tracking-tight uppercase">Sistemas en <span className="font-semibold text-amber-500">Optimización</span></h2>
                    <p className="text-zinc-400 text-xs font-light leading-relaxed max-w-xs">
                      Los servicios de lealtad digital en la sede <span className="text-zinc-200 font-medium">{activeHotelData.name}</span> se encuentran temporalmente en mantenimiento técnico.
                    </p>
                  </div>
                  <div className="pt-4 border-t border-zinc-900 w-full">
                    <p className="text-[9px] text-zinc-500 uppercase tracking-widest bg-zinc-900/30 px-4 py-2.5 rounded-lg inline-block border border-zinc-900/50 leading-none">
                      🔒 Sus puntos acumulados y nivel siguen 100% seguros
                    </p>
                  </div>
                  {config.isChainMode && (
                    <button 
                      onClick={() => {
                        const primeraSedeActiva = hotels.find(h => h.active !== false);
                        setGuestLocation(primeraSedeActiva ? primeraSedeActiva.id : 'Global');
                        setGuestCatalogFilter('Local');
                      }} 
                      className="mt-6 text-[10px] font-bold text-amber-500 hover:text-amber-400 uppercase tracking-widest bg-white/5 border border-white/10 px-5 py-2.5 rounded-xl transition-all active:scale-95"
                    >
                      Explorar otras sedes de la red
                    </button>
                  )}
                </div>
              </div>
            );
          }

          // Si la sede está activa, fluye el Portal normal
          return currentUser ? (
            currentUser.pin === (currentUser.dni && currentUser.dni.length >= 4 ? currentUser.dni.slice(-4) : '1234') ? (
              /* PANTALLA DE BLOQUEO FUERTE */
              <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-6 text-white relative">
                <div className="w-full max-w-sm bg-zinc-900 rounded-[2.5rem] p-8 border border-zinc-800 text-center space-y-6 shadow-2xl">
                  <div className="w-16 h-16 bg-amber-500/10 text-amber-500 border border-amber-500/20 rounded-2xl flex items-center justify-center mx-auto">
                    <ShieldCheck size={32} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-light uppercase tracking-widest mb-2">Seguridad de la Cuenta</h3>
                    <p className="text-zinc-400 text-[10px] uppercase tracking-widest leading-relaxed">Detectamos que está utilizando el PIN provisional de bienvenida de 4 dígitos basado en su identificación.</p>
                  </div>
                  <div className="space-y-2 text-left">
                    <label className="text-zinc-400">Establecer Nuevo PIN (4 dígitos)</label>
                    <input type="password" placeholder="••••" maxLength="4" className="text-2xl tracking-[0.5em] font-mono text-center p-4 bg-zinc-950 border border-zinc-800 rounded-2xl w-full outline-none text-white focus:border-amber-500 transition-all shadow-inner" value={newPinInput} onChange={e => setNewPinInput(e.target.value)} />
                  </div>
                  <div className="space-y-3 pt-2">
                    <button onClick={async () => {
                      if(newPinInput.trim().length !== 4) return showToast("El PIN debe ser de 4 dígitos", "error");
                      setIsProcessing(true);
                      try {
                        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'guests', currentUser.id), { pin: newPinInput.trim() });
                        setCurrentUser(prev => ({ ...prev, pin: newPinInput.trim() }));
                        showToast("PIN actualizado con éxito", "success");
                        setNewPinInput("");
                      } catch(e) { showToast(e.message, 'error'); } finally { setIsProcessing(false); }
                    }} className="w-full py-4 bg-amber-500 text-black rounded-xl text-[10px] font-bold uppercase tracking-[0.2em] shadow-lg hover:bg-amber-400 transition-all">Actualizar y Continuar</button>
                    <button onClick={async () => {
                      setIsProcessing(true);
                      try {
                        const pinConfirmado = currentUser.pin + " ";
                        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'guests', currentUser.id), { pin: pinConfirmado });
                        setCurrentUser(prev => ({ ...prev, pin: pinConfirmado }));
                        showToast("Clave actual confirmada", "success");
                      } catch(e) { showToast(e.message, 'error'); } finally { setIsProcessing(false); }
                    }} className="w-full py-4 bg-transparent text-zinc-400 text-[9px] font-medium uppercase tracking-[0.2em] transition-all hover:text-white">Continuar con clave actual ({currentUser.pin})</button>
                  </div>
                </div>
              </div>
            ) : (
              /* PORTAL DEL HUÉSPED NORMAL */
              <div className="flex-1 bg-zinc-50 flex flex-col overflow-hidden relative animate-in fade-in duration-500">
                <div className="bg-zinc-950 text-white px-4 py-4 rounded-b-[2rem] shadow-xl relative shrink-0 z-10" style={{backgroundColor: currentBranding.primaryColor === '#C5A059' ? '#09090b' : currentBranding.primaryColor}}>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-white p-1.5 rounded-lg shadow-sm">
                        <img src={currentBranding.logo} className="h-5 object-contain" alt="logo" />
                      </div>
                      <div className="leading-none">
                        <p className="font-bold text-xs uppercase tracking-widest text-white truncate max-w-[220px] sm:max-w-xs">{currentUser.name}</p>
                        <p className="text-white/80 text-[8px] font-medium uppercase tracking-[0.2em] mt-0.5">{getDisplayTier(currentUser.tier)}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setGuestLanguage(activeLang === 'Español' ? 'English' : 'Español')} className="p-2 rounded-lg text-white/70 hover:text-white transition-colors">
                        <Globe size={16} strokeWidth={1.5} />
                      </button>
                      <button onClick={handleLogoutGuest} className="p-2 text-white/70 hover:text-red-400 transition-colors">
                        <LogOut size={16} strokeWidth={1.5}/>
                      </button>
                    </div>
                  </div>
                  <div className="bg-black/20 p-2.5 rounded-xl border border-white/10 shadow-inner flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-baseline gap-1.5 pl-2">
                        <h2 className="text-2xl font-light tracking-tighter leading-none text-white m-0">{Number(currentUser.points || 0).toLocaleString()}</h2>
                        <span className="text-[8px] font-bold text-white/70 uppercase tracking-widest m-0">{t.pts}</span>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={()=>setShowModal('invoice_upload')} className="bg-white text-zinc-950 px-3 py-2 rounded-lg text-[8px] font-bold uppercase tracking-widest shadow-sm transition-all hover:bg-zinc-200 active:scale-95 flex items-center gap-1.5 whitespace-nowrap"><Upload size={12}/> Ticket</button>
                        <button onClick={()=>setShowModal('digital_card')} className="bg-black/30 text-white px-3 py-2 rounded-lg text-[8px] font-bold uppercase tracking-widest border border-white/10 shadow-sm transition-all hover:bg-black/50 active:scale-95 flex items-center gap-1.5 whitespace-nowrap"><QrCode size={12}/> Card</button>
                      </div>
                    </div>
                    {(() => {
                      if (Number(currentUser.points) <= 0) return null;
                      const lastActGuest = currentUser.lastActivityAt?.seconds ? currentUser.lastActivityAt.seconds * 1000 : (currentUser.createdAt?.seconds ? currentUser.createdAt.seconds * 1000 : Date.now());
                      const expireDate = new Date(lastActGuest);
                      expireDate.setMonth(expireDate.getMonth() + 24);
                      const msLeft = expireDate.getTime() - Date.now();
                      const daysLeft = Math.floor(msLeft / (1000 * 60 * 60 * 24));
                      const isUrgent = daysLeft <= 90;
                      return (
                        <div className={`px-3 py-1.5 rounded-lg border flex items-center gap-1.5 text-[8px] uppercase tracking-widest font-bold ${isUrgent ? 'bg-amber-500/20 border-amber-500/50 text-amber-400 animate-pulse' : 'bg-white/5 border-white/10 text-white/50'}`}>
                          <Clock size={10} strokeWidth={2} />
                          {isUrgent ? `¡Atención! Sus puntos vencen en ${daysLeft} días` : `Vigentes hasta: ${expireDate.toLocaleDateString()}`}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-72 md:pb-96 no-scrollbar">
                  {guestSubView === 'home' && (
                    <div className="space-y-6 max-w-2xl mx-auto animate-in fade-in duration-500">
                      {(() => {
                        const activeHotelObj = hotels.find(h => h.id === guestLocation);
                        const displayBookingUrl = config.isChainMode && activeHotelObj?.bookingUrl ? activeHotelObj.bookingUrl : config.bookingUrl;
                        if (!displayBookingUrl) return null;
                        return (
                          <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-3xl p-5 md:p-6 shadow-lg flex flex-col md:flex-row items-center justify-between gap-4 border border-zinc-700/50 relative overflow-hidden group">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_0%,rgba(197,160,89,0.15),transparent_50%)]"></div>
                            <div className="relative z-10 text-center md:text-left flex-1">
                              <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-1">Reserva Directa</h4>
                              <p className="text-zinc-400 text-[9px] font-medium uppercase tracking-[0.2em] leading-relaxed">Mejor tarifa garantizada y acumulación de puntos en su estadía</p>
                            </div>
                            <button onClick={() => window.open(displayBookingUrl, '_blank')} className="relative z-10 w-full md:w-auto px-6 py-3 bg-amber-500 text-zinc-950 text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl shadow-[0_0_15px_rgba(245,158,11,0.3)] hover:shadow-[0_0_25px_rgba(245,158,11,0.5)] transition-all active:scale-95 whitespace-nowrap" style={{backgroundColor: currentBranding.primaryColor, color: currentBranding.primaryColor === '#C5A059' ? '#000' : '#fff'}}>Reservar Ahora</button>
                          </div>
                        );
                      })()}
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-3 ml-2">
                        <h3 className="font-medium text-zinc-500 uppercase tracking-[0.2em] text-[10px] flex items-center gap-2 shrink-0">
                          <div className="w-4 h-[1px]" style={{backgroundColor: currentBranding.primaryColor}}></div>{t.available_benefits}
                        </h3>
                        {config.isChainMode && (
                          <div className="relative w-full md:w-auto">
                            <select value={guestCatalogFilter} onChange={(e) => setGuestCatalogFilter(e.target.value)} className="appearance-none w-full md:min-w-[280px] !bg-zinc-900 focus:!bg-zinc-900 !text-white border border-zinc-800 p-3.5 pr-10 rounded-xl text-xs font-medium tracking-wide outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all shadow-md cursor-pointer">
                              <option value="Local" className="bg-zinc-900 text-white py-2">📍 {hotels.find(h => h.id === guestLocation)?.name || 'Mi Hotel'} (Sede Actual & Global)</option>
                              <optgroup label="Explorar Otras Sedes" className="bg-zinc-950 text-amber-500 font-bold">
                                {hotels.filter(h => h.id !== guestLocation && h.active !== false).map(h => (
                                  <option key={h.id} value={h.id} className="bg-zinc-900 text-white py-2">{h.name}</option>
                                ))}
                              </optgroup>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-amber-500"><ChevronRight size={14} strokeWidth={2} className="rotate-90" /></div>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-5">
                        {visibleBenefitsGuest.map(b => (
                          <div key={b.id} className="bg-white rounded-3xl border border-zinc-200 overflow-hidden flex flex-col group shadow-sm hover:shadow-md transition-all">
                            <div className="h-40 bg-zinc-100 relative flex items-center justify-center overflow-hidden">
                              {b.image && b.image.includes('http') ? <img src={b.image} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt={b.title} /> : <div className="text-6xl opacity-20 group-hover:scale-110 transition-transform duration-500">{b.image}</div>}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                              <div className="absolute top-4 right-4 bg-zinc-950 text-white px-3 py-1 rounded-full font-medium text-[9px] uppercase tracking-widest border border-white/10">{b.cost} {t.pts}</div>
                              <div className="absolute bottom-4 left-5 right-5"><h4 className="font-medium text-white text-base uppercase tracking-widest leading-none drop-shadow-md">{b.title}</h4></div>
                            </div>
                            <div className="p-5">
                              <p className="text-zinc-500 text-[11px] mb-5 leading-relaxed font-light line-clamp-2">{b.desc}</p>
                              <button onClick={async () => {
                                setIsProcessing(true);
                                try {
                                  const hotelTarget = b.hotelId === 'Todos' ? guestLocation : b.hotelId;
                                  const newReqData = { type: 'BENEFICIO', benefitId: b.id, benefitTitle: b.title, cost: b.cost, guestId: currentUser.id, guestName: currentUser.name, dni: currentUser.dni, status: 'PENDIENTE', hotelId: hotelTarget, pts: 0, rating: 0, messages: [{ sender: 'hotel', text: `¡Excelente elección! Hemos recibido su solicitud para: ${b.title}. ¿Prefiere que se lo acerquemos a su habitación o pasará a retirarlo?`, timestamp: Date.now() }], createdAt: serverTimestamp(), unreadByGuest: false, unreadByAdmin: true };
                                  const docRef = await addDoc(collection(db, `artifacts/${appId}/public/data/requests`), newReqData);
                                  const newPoints = Number(currentUser.points) - Number(b.cost);
                                  await updateDoc(doc(db, `artifacts/${appId}/public/data/guests`, currentUser.id), { points: newPoints });
                                  setCurrentUser({...currentUser, points: newPoints});
                                  setSelectedRequest({ id: docRef.id, ...newReqData });
                                  setShowModal('guest_chat_modal');
                                } catch(e) { showToast(e.message, 'error'); } finally { setIsProcessing(false); }
                              }} disabled={Number(currentUser.points) < Number(b.cost) || isProcessing} className="w-full py-3.5 rounded-xl font-medium text-[10px] tracking-[0.2em] bg-zinc-950 text-white disabled:opacity-25 disabled:bg-zinc-500 transition-all active:scale-95 shadow-md flex justify-center items-center gap-2">{isProcessing ? <Loader2 size={16} className="animate-spin" /> : t.solicit}</button>
                            </div>
                          </div>
                        ))}
                        {visibleBenefitsGuest.length === 0 && <div className="p-16 text-center bg-white rounded-3xl border border-dashed border-zinc-200"><Gift size={40} className="mx-auto text-zinc-200 mb-4" strokeWidth={1} /><p className="text-[10px] text-zinc-400 font-medium uppercase tracking-widest">{t.catalog_empty}</p></div>}
                      </div>
                    </div>
                  )}
                  {guestSubView === 'history' && (
                    <div className="max-w-2xl mx-auto space-y-5 animate-in fade-in duration-500">
                      <div className="flex justify-between items-center px-1"><h3 className="font-medium text-zinc-500 uppercase tracking-[0.2em] text-[10px]">{t.my_history}</h3><button onClick={()=>setShowModal('new_general_chat')} className="text-amber-600 hover:text-amber-700 bg-amber-50 px-3 py-1.5 rounded-lg text-[9px] font-medium uppercase tracking-widest transition-all flex items-center gap-1.5"><MessageCircle size={14}/> {t.general_chat}</button></div>
                      <div className="bg-white rounded-3xl border border-zinc-200 shadow-sm overflow-hidden">
                        <div className="w-full text-left text-xs">
                          {requests.filter(r=>r.guestId===currentUser.id||r.dni===currentUser.dni).map(req => (
                            <div key={req.id} className="border-b border-zinc-200 p-5 hover:bg-zinc-50 transition-all flex flex-col md:flex-row justify-between gap-4">
                              <div>
                                <p className="text-zinc-600 font-mono text-[9px] mb-1.5">{new Date(req.createdAt?.seconds*1000 || Date.now()).toLocaleDateString()}</p>
                                <p className="font-bold text-zinc-900 text-sm mb-1 uppercase tracking-wide">{req.type === 'MENSAJE' ? 'Tema General' : req.benefitTitle}</p>
                                <p className={`font-medium text-lg tracking-tighter ${req.pts > 0 ? 'text-green-600' : 'text-zinc-800'} ${(req.status === 'Pendiente' || req.status === 'PENDIENTE') ? 'opacity-50' : ''}`}>{req.pts > 0 ? `+${req.pts}` : req.cost > 0 ? `-${req.cost}` : ''} <span className="text-[9px] font-bold uppercase">{t.pts}</span></p>
                                {req.messages && req.messages.length > 0 && <div className="mt-3 space-y-2 border-l-2 border-amber-400 pl-3 py-1">{req.messages.map((m, idx) => <p key={idx} className="text-[10px] text-zinc-700 leading-relaxed"><strong className="uppercase text-[9px] text-amber-600">{m.sender==='hotel'?'Staff':'Tú'}:</strong> {m.text}</p>)}</div>}
                                {req.status !== 'Pendiente' && req.status !== 'PENDIENTE' && req.type !== 'ALTA' && req.type !== 'MENSAJE' && <div className="mt-4 bg-zinc-50 p-4 rounded-xl border border-zinc-200 shadow-sm w-max"><p className="text-[8px] font-bold text-zinc-500 mb-2 uppercase tracking-widest">{req.rating > 0 ? 'SU VALORACIÓN' : t.rate_exp}</p><RatingFaces currentRating={req.rating || 0} onRate={(v)=>handleRateExperience(req.id, v)} size={24} /></div>}
                              </div>
                              <div className="flex flex-row md:flex-col justify-between md:items-end md:justify-start gap-3 items-center border-t md:border-none border-zinc-100 pt-3 md:pt-0">
                                <span className={`px-3 py-1 rounded-lg font-bold text-[9px] uppercase tracking-widest shadow-sm flex items-center gap-1 ${ (req.status === 'APROBADO' || req.deliveredAt || req.status === 'Entregado') ? 'bg-green-50 text-green-700 border border-green-200' : req.status === 'RECHAZADO' ? 'bg-red-50 text-red-700 border border-red-200' : (req.status === 'Pendiente' || req.status === 'PENDIENTE') ? 'bg-amber-50 text-amber-600 border border-amber-200' : 'bg-zinc-100 text-zinc-700 border border-zinc-300' }`}>{req.deliveredAt && <CheckCircle size={10} strokeWidth={2.5}/>}{(req.status === 'Pendiente' || req.status === 'PENDIENTE') && <Clock size={10} strokeWidth={2.5}/>}{req.deliveredAt ? 'ENTREGADO' : (req.status === 'Pendiente' || req.status === 'PENDIENTE' ? 'EN REVISIÓN' : req.status)}</span>
                                <button onClick={()=>{ setSelectedRequest(req); setShowModal('guest_chat_modal'); if(req.unreadByGuest) updateDoc(doc(db, `artifacts/${appId}/public/data/requests`, req.id), { unreadByGuest: false }); }} className="flex items-center gap-1.5 text-amber-600 hover:text-amber-800 transition-all font-bold text-[10px] uppercase tracking-widest relative"><MessageCircle size={16} strokeWidth={1.5}/> {t.chat_btn}{req.unreadByGuest && <span className="absolute -top-1 -right-2 w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-sm"></span>}</button>
                              </div>
                            </div>
                          ))}
                          {requests.filter(r=>r.guestId===currentUser.id||r.dni===currentUser.dni).length === 0 && <div className="p-12 text-center"><p className="text-[11px] text-zinc-400 font-light uppercase tracking-widest">{t.no_history}</p></div>}
                        </div>
                      </div>
                    </div>
                  )}
                  {guestSubView === 'profile' && (
                    <div className="space-y-6 max-w-2xl mx-auto animate-in fade-in duration-500">
                      <h3 className="font-medium text-zinc-500 uppercase tracking-[0.2em] text-[10px] ml-2 flex items-center gap-2"><div className="w-4 h-[1px]" style={{backgroundColor: currentBranding.primaryColor}}></div>{t.profile_title}</h3>
                      <div className="bg-white rounded-3xl border border-zinc-200 p-6 shadow-sm">
                        <p className="text-[11px] text-zinc-500 uppercase tracking-widest leading-relaxed font-light mb-8">{t.profile_desc}</p>
                        <div className="space-y-8">
                          {Object.entries(preferenceSettings).map(([category, tags]) => (
                            <div key={category}>
                              <h4 className="text-[10px] font-medium text-zinc-900 uppercase tracking-[0.2em] mb-4 flex items-center gap-2 border-b border-zinc-100 pb-2"><Tag size={12} style={{color: currentBranding.primaryColor}} /> {category}</h4>
                              <div className="flex flex-wrap gap-2">{tags.map(tag => { const active = (currentUser.preferences || []).includes(tag); return <button key={tag} onClick={() => togglePreference(tag)} className={`px-4 py-2 rounded-xl text-[10px] font-medium uppercase tracking-widest transition-all duration-300 ${active ? 'text-white border border-transparent' : 'bg-zinc-50 border border-zinc-200 text-zinc-500'}`} style={active ? {backgroundColor: currentBranding.primaryColor} : {}}>{tag}</button>; })}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="mt-12 pt-8 pb-24 border-t border-zinc-100 space-y-4">
                        <h4 className="text-[10px] font-medium text-zinc-900 uppercase tracking-[0.2em] flex items-center gap-2"><Key size={14} className="text-amber-500" /> Seguridad de la cuenta</h4>
                        <p className="text-[11px] text-zinc-500 uppercase tracking-widest font-light leading-relaxed mb-4">Modifique su PIN de acceso de 4 dígitos para proteger su cuenta.</p>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <input type="password" placeholder="Nuevo PIN (4 dígitos)" maxLength="4" value={newPinInput} onChange={e => setNewPinInput(e.target.value)} className="bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-3 text-xs outline-none flex-1 font-mono text-center tracking-[0.3em]" />
                          <button onClick={async () => { if(newPinInput.trim().length !== 4) return showToast("El PIN debe ser de 4 dígitos", "error"); setIsProcessing(true); try { await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'guests', currentUser.id), { pin: newPinInput.trim() }); setCurrentUser(prev => ({ ...prev, pin: newPinInput.trim() })); showToast("PIN modificado con éxito", "success"); setNewPinInput(""); } catch(e) { showToast(e.message, 'error'); } finally { setIsProcessing(false); } }} className="bg-zinc-950 text-white px-6 py-3 rounded-xl text-[10px] font-medium uppercase tracking-widest hover:bg-zinc-800 transition-all shadow-md">Cambiar PIN</button>
                        </div>
                      </div>
                      {(config.termsUrl || config.privacyUrl) && <div className="flex flex-wrap justify-center gap-4 mt-8 pt-8 border-t border-zinc-200 w-full">{config.termsUrl && <a href={config.termsUrl} target="_blank" rel="noreferrer" className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-800 transition-all">Términos y Condiciones</a>}{config.privacyUrl && <a href={config.privacyUrl} target="_blank" rel="noreferrer" className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-800 transition-all">Política de Privacidad</a>}</div>}
                    </div>
                  )}
                </main>
                <PoweredByPaxfidely hotelId={currentUser?.registeredAtHotel && currentUser.registeredAtHotel !== 'Global' ? currentUser.registeredAtHotel : (new URLSearchParams(window.location.search).get('hotel') || '')} isWhiteLabel={(config.plan || '').startsWith('Elite') || config.plan === 'Signature' || config.plan === 'Enterprise+WB'} />
                <nav className="fixed bottom-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-80 bg-white/95 backdrop-blur-xl border border-zinc-200 p-2 flex justify-between items-center z-50 rounded-2xl shadow-2xl">
                  {[ {id:'home', label:'Canjes', icon:Layout}, {id:'history', label:'Cuenta', icon:Clock}, {id:'profile', label:'Perfil', icon:Heart} ].map(tab => <button key={tab.id} onClick={() => setGuestSubView(tab.id)} className={`flex flex-col items-center justify-center flex-1 py-2 rounded-xl transition-all duration-300 relative ${guestSubView === tab.id ? 'bg-zinc-950 text-white shadow-md' : 'text-zinc-600 hover:bg-zinc-100'}`}><tab.icon size={20} strokeWidth={guestSubView === tab.id ? 2 : 1.5}/><span className="text-[8px] font-medium uppercase mt-1.5 tracking-widest">{tab.label}</span>{tab.id === 'history' && hasUnreadGuest && <div className="absolute top-1 right-1/4 w-2 h-2 bg-amber-500 rounded-full border-2 border-white animate-pulse"></div>}</button>)}
                </nav>
              </div>
            )
          ) : (
            /* --- LOGIN / REGISTRO DEL HUÉSPED (Efecto Wow Móvil) --- */
            <div className="min-h-screen bg-[#fafafa] flex flex-col items-center justify-center p-6 relative overflow-hidden animate-in fade-in duration-700 w-full font-['Montserrat']">
              <div className="absolute top-0 left-0 w-full h-72 opacity-20" style={{ background: `linear-gradient(to bottom, ${currentBranding.primaryColor}, transparent)` }}></div>
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-10" style={{ backgroundColor: currentBranding.primaryColor }}></div>
              <div className="w-full max-w-sm bg-white rounded-[2.5rem] p-8 md:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.05)] relative z-10 border border-zinc-100/50 backdrop-blur-xl">
                <div className="flex justify-center mb-8">
                  {currentBranding.logo && currentBranding.logo !== 'PaxFidely' ? <img src={currentBranding.logo} alt="Hotel Logo" className="h-16 max-h-16 object-contain drop-shadow-sm" /> : <div className="text-amber-500 font-bold text-2xl tracking-widest uppercase">PAX<span className="text-zinc-900">FIDELY</span></div>}
                </div>
                <div className="text-center mb-8"><h2 className="text-2xl font-light text-zinc-900 mb-1 tracking-tight">Bienvenido</h2><p className="text-[9px] font-medium text-zinc-400 uppercase tracking-[0.25em]">Programa de Beneficios Oficial</p></div>
                <div className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest ml-1">DNI / Identificador</label>
                    <div className="relative"><User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-300" strokeWidth={1.5} /><input type="text" placeholder="Ingrese su documento" value={guestLoginData.dni} onChange={e=>setGuestLoginData({...guestLoginData, dni: e.target.value})} className="p-4 pl-12 bg-zinc-50/50 border border-zinc-200 rounded-2xl w-full font-medium outline-none focus:bg-white focus:border-zinc-400 transition-all text-sm shadow-inner"/></div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest ml-1">PIN de Acceso</label>
                    <input type="password" placeholder="••••" maxLength="4" className="text-2xl tracking-[0.5em] font-mono text-center p-4 bg-zinc-50/50 border border-zinc-200 rounded-2xl w-full outline-none focus:bg-white focus:border-zinc-400 transition-all shadow-inner" value={guestLoginData.pin} onChange={e=>setGuestLoginData({...guestLoginData, pin: e.target.value})} />
                  </div>
                </div>
                <button onClick={() => {
                  const f = guests.find(g => g.dni?.trim() === guestLoginData.dni.trim() && (g.pin || '1234').trim() === guestLoginData.pin.trim() && (g.status === 'ACTIVO' || !g.status));
                  if(f) { setCurrentUser(f); showToast("Bienvenido a sus beneficios.", "success"); } else { showToast("Credenciales inválidas o cuenta inactiva", "error"); }
                }} className="w-full py-4 rounded-2xl text-white uppercase tracking-[0.2em] text-[10px] font-bold shadow-lg transition-all hover:scale-[1.02] active:scale-95 mt-8 flex items-center justify-center gap-2" style={{ backgroundColor: currentBranding.primaryColor, color: currentBranding.primaryColor === '#C5A059' ? '#000' : '#fff' }}>Ingresar al Portal <ChevronRight size={16} strokeWidth={2}/></button>
                {(config.termsUrl || config.privacyUrl) && <p className="text-center text-[9px] text-zinc-400 mt-6 px-4">Al continuar, usted acepta los {config.termsUrl && <a href={config.termsUrl} target="_blank" rel="noreferrer" className="underline mx-1 hover:text-zinc-600">Términos y Condiciones</a>} {config.termsUrl && config.privacyUrl && 'y la'} {config.privacyUrl && <a href={config.privacyUrl} target="_blank" rel="noreferrer" className="underline mx-1 hover:text-zinc-600">Política de Privacidad</a>} del establecimiento.</p>}
                <div className="mt-8 pt-6 border-t border-zinc-100 text-center"><p className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest mb-3">¿Aún no es socio?</p><button onClick={() => setShowModal('guest_register')} className="text-xs font-bold underline decoration-2 underline-offset-4 transition-all hover:opacity-80" style={{ color: currentBranding.primaryColor }}>Crear mi cuenta ahora</button></div>
              </div>
              <div className="mt-8 flex items-center justify-center gap-2 opacity-50"><Lock size={12} className="text-zinc-400" /><span className="text-[9px] font-medium uppercase tracking-widest text-zinc-400">Powered by PaxFidely</span></div>
            </div>
          );
        })()} />

{/* --- 3. PORTAL STAFF --- */}
        <Route path="/admin" element={staffSession ? (
          <div className="flex-1 flex overflow-hidden relative animate-in fade-in">
            {/* SIDEBAR ELEGANTE */}
            <aside className={`fixed md:relative top-0 left-0 h-full w-64 bg-[#0a0a0a] border-r border-neutral-800 flex flex-col shrink-0 z-40 transition-transform duration-500 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
              <div className="h-20 border-b border-neutral-800 flex items-center justify-center bg-[#0a0a0a] shadow-md px-6 w-full">
                <img src={currentBranding.logo} className="max-h-8 object-contain" alt="logo" />
              </div>
              <nav className="flex-1 p-4 space-y-2 overflow-y-auto no-scrollbar">
                {[
                  {id:'dashboard', label:'Resumen', icon:PieChart, reqPlan: 'Boutique'}, 
                  {id:'requests', label:'Bandeja', icon:Bell, reqPlan: 'Boutique'}, 
                  {id:'benefits', label:'Catálogo', icon:Gift, reqPlan: 'Boutique'}, 
                  {id:'guests', label:'Directorio', icon:Users, reqPlan: 'Boutique'}, 
                  // Módulo de Sedes condicional para modo cadena
                  ...(config.isChainMode && (staffSession?.role === 'System' || staffSession?.id === 'master') ? [
                    {id:'hotels', label:'Sedes', icon:Building2, reqPlan: 'Enterprise'}
                  ] : []),
                  {id:'campaigns', label:'Marketing', icon:Megaphone, reqPlan: 'Premium'}
                ].map(item => {
                  const unread = item.id === 'requests' && requests.some(r => r.unreadByAdmin || r.status === 'Pendiente' || r.status === 'PENDIENTE');
                  
                  // REGLA DE NEGOCIO: Evita estados fantasmas en la lectura del plan
                  const currentPlan = config.plan || (config.isChainMode ? 'Enterprise' : 'Boutique');
                  const plansHierarchy = { 'Boutique': 1, 'CONNECT': 1, 'Premium': 2, 'GROWTH': 2, 'Signature': 3, 'ENTERPRISE': 3 };
                  const isLocked = plansHierarchy[currentPlan] < plansHierarchy[item.reqPlan];

                  return (
                    <button key={item.id} onClick={() => { 
                        // Si está bloqueado por el plan, disparamos el Modal de Upsell
                        if (isLocked) {
                           return setUpgradeRequired({ module: item.label, plan: item.reqPlan });
                        }

                        setAdminSubView(item.id); 
                        setIsMobileMenuOpen(false); 
                        if (item.id === 'campaigns') {
                          setCampaignHotel((staffSession?.role === 'Admin' || staffSession?.role === 'Operador') ? staffSession.hotelId : 'Todos');
                        }
                      }} 
                      className={`w-full p-3.5 rounded-xl flex items-center justify-between transition-all duration-300 ${adminSubView === item.id ? 'bg-amber-500 text-black shadow-md' : 'text-zinc-500 hover:bg-zinc-900 hover:text-white'}`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon size={18} strokeWidth={adminSubView === item.id ? 2 : 1.5} className={isLocked ? 'opacity-30' : ''}/> 
                        <span className={`text-[10px] font-medium uppercase tracking-[0.2em] ${isLocked ? 'opacity-30' : ''}`}>{item.label}</span>
                      </div>
                      {unread && <div className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)] animate-pulse"></div>}
                      {isLocked && <Lock size={12} className="text-zinc-600"/>}
                    </button>
                  );
                })}
                <div className="pt-8 mt-8 border-t border-white/5 space-y-2">
                  {(isMasterOrChain || staffSession?.role === 'Admin') && (
                    <button onClick={() => { setAdminSubView('config'); setConfigView(isMasterOrChain ? 'branding' : 'staff'); setIsMobileMenuOpen(false); }} className={`w-full p-3.5 rounded-xl flex items-center gap-3 transition-all ${adminSubView === 'config' ? 'bg-white text-zinc-950' : 'text-zinc-500 hover:bg-zinc-900'}`}>
                      <Settings size={18} strokeWidth={1.5}/> <span className="text-[10px] font-medium uppercase tracking-[0.2em]">Configuración</span>
                    </button>
                  )}
                </div>
              </nav>
              <div className="p-5 border-t border-white/5">
                <button onClick={handleLogoutAdmin} className="w-full py-3 text-zinc-500 hover:text-red-400 text-[10px] font-medium uppercase tracking-widest flex items-center justify-center gap-2 transition-all border border-white/5 rounded-xl hover:border-red-500/30">
                  <LogOut size={16} strokeWidth={1.5}/> Salir del Hub
                </button>
              </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0 bg-zinc-50/50 overflow-hidden relative">
              {/* HEADER OSCURO (Look & Feel Premium) */}
              <header className="h-20 bg-[#0a0a0a] border-b border-neutral-800 px-6 flex items-center justify-between z-10 shadow-md backdrop-blur-md">
                 <div className="flex items-center gap-4">
                    <button onClick={()=>setIsMobileMenuOpen(true)} className="md:hidden text-neutral-400 p-2 hover:bg-neutral-800 rounded-xl transition-all"><Menu size={24} strokeWidth={1.5} /></button>
                    <div className="flex flex-col">
                      <h2 className="text-[11px] font-medium uppercase tracking-[0.3em] text-neutral-500 mb-1 leading-none">{adminSubView}</h2>
                      {isMasterOrChain && config.isChainMode && (
                          <div className="flex items-center gap-2">
                             <MapPin size={12} className="text-amber-500" strokeWidth={1.5} />
                             <select className="bg-transparent border-none p-0 text-[10px] font-medium uppercase tracking-widest text-white outline-none cursor-pointer hover:text-amber-500 transition-colors [&>option]:bg-neutral-900" value={activeAdminHotel} onChange={(e) => setActiveAdminHotel(e.target.value)}>
                                <option value="Todos">Global Network</option>
                                {hotels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                             </select>
                          </div>
                      )}
                    </div>
                 </div>

                 {/* SATISFACCIÓN GLOBAL INDICATOR */}
                 <div className="flex items-center gap-6">
                    <div className="hidden lg:flex flex-col items-end border-r border-neutral-800 pr-6">
                        <p className="text-[8px] font-medium text-neutral-500 uppercase tracking-widest mb-1.5 leading-none">Satisfacción Global</p>
                        <div className="flex items-center gap-2 leading-none">
                            <span className={`text-xl font-light tracking-tighter ${getSatisfactionColor(globalSatisfaction)}`}>{globalSatisfaction ? `${globalSatisfaction}/5` : 'N/A'}</span>
                            <RatingFaces currentRating={Math.round(globalSatisfaction)} readOnly size={16} />
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden md:block leading-none">
                          <span className="text-[11px] font-medium text-white block mb-1 uppercase tracking-widest">{staffSession.name}</span>
                          <span className="text-[9px] font-light text-neutral-500 uppercase tracking-[0.2em]">{staffSession.role}</span>
                      </div>
                      <div className="w-10 h-10 bg-neutral-900 rounded-xl flex items-center justify-center text-neutral-400 border border-neutral-800">
                        <User size={18} strokeWidth={1.5}/>
                      </div>
                    </div>
                 </div>
              </header>
              
              <main className="flex-1 overflow-y-auto p-6 md:p-10 no-scrollbar relative">
                
                {/* --- INICIO CIRUGÍA LÁSER 2: BILLING GUARD (PANTALLA DE BLOQUEO) --- */}
                {billingState.isExpired && adminSubView !== 'config' ? (
                  <div className="absolute inset-0 z-50 bg-zinc-950 flex flex-col items-center justify-center p-4 md:p-6 animate-in fade-in duration-500">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(197,160,89,0.1),transparent_70%)]"></div>
                    
                    <div className="bg-zinc-900 border border-zinc-800 p-8 md:p-10 rounded-[2.5rem] shadow-2xl max-w-2xl w-full relative z-10 flex flex-col items-center text-center">
                      <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mb-6 border border-amber-500/20 shadow-[0_0_30px_rgba(245,158,11,0.15)]">
                        <Lock size={28} className="text-amber-500" strokeWidth={1.5} />
                      </div>
                      
                      <h2 className="text-2xl md:text-3xl text-white font-light uppercase tracking-widest mb-3">Activación Definitiva</h2>
                      <p className="text-zinc-400 text-[10px] md:text-xs uppercase tracking-widest leading-relaxed font-medium mb-8 max-w-lg">
                        Sus 14 días de prueba han concluido. Seleccione su plan para mantener su plataforma operativa.
                      </p>

                      {/* PANEL EDUCATIVO DE TRANSICIÓN */}
                      <div className="bg-[#0a0a0a] border border-zinc-800/80 rounded-2xl p-6 mb-8 w-full text-left space-y-5 shadow-inner">
                        <h4 className="text-amber-500 text-[9px] font-bold uppercase tracking-widest border-b border-zinc-800/80 pb-3 mb-4 flex items-center gap-2">
                          <Sparkles size={12} /> ¿Cómo operará de ahora en adelante?
                        </h4>
                        
                        <div className="flex gap-4 items-start">
                          <div className="bg-zinc-800/50 p-2 rounded-lg shrink-0 mt-0.5"><ShieldCheck className="text-zinc-300" size={16} strokeWidth={1.5} /></div>
                          <div>
                            <p className="text-zinc-200 text-sm font-medium mb-1">Su Acceso Exclusivo</p>
                            <p className="text-zinc-500 text-[10px] font-light leading-relaxed">Usted y su equipo ingresarán directamente a este Hub Operativo. La pantalla doble de bienvenida desaparecerá para optimizar su tiempo.</p>
                          </div>
                        </div>
                        
                        <div className="flex gap-4 items-start">
                          <div className="bg-zinc-800/50 p-2 rounded-lg shrink-0 mt-0.5"><QrCode className="text-zinc-300" size={16} strokeWidth={1.5} /></div>
                          <div>
                            <p className="text-zinc-200 text-sm font-medium mb-1">El Flujo del Huésped</p>
                            <p className="text-zinc-500 text-[10px] font-light leading-relaxed">Sus clientes seguirán aterrizando directamente en la app móvil al escanear el código QR en la recepción, sin fricciones.</p>
                          </div>
                        </div>

                        <div className="flex gap-4 items-start">
                          <div className="bg-zinc-800/50 p-2 rounded-lg shrink-0 mt-0.5"><User className="text-zinc-300" size={16} strokeWidth={1.5} /></div>
                          <div>
                            <p className="text-zinc-200 text-sm font-medium mb-1">¿Desea ver el portal de un cliente?</p>
                            <p className="text-zinc-500 text-[10px] font-light leading-relaxed">Vaya a Directorio {'>'} Ficha del Socio {'>'} "Ver Huésped". El sistema le mostrará exactamente la interfaz y los puntos de ese socio en tiempo real.</p>
                          </div>
                        </div>
                      </div>

                      {/* BOTONERA DE ACCIÓN */}
                      <div className="flex flex-col sm:flex-row gap-4 w-full">
                        <button 
                          onClick={() => window.open('https://buy.stripe.com/test_link_generico', '_blank')} 
                          className="flex-1 py-4 bg-amber-500 text-black rounded-xl text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-amber-400 transition-all shadow-lg active:scale-95 flex justify-center items-center gap-2"
                        >
                          <CreditCard size={16} /> Activar Suscripción
                        </button>
                        <button 
                          onClick={() => window.open('https://wa.me/tunumerodewk', '_blank')}
                          className="flex-1 py-4 bg-transparent text-white border border-white/20 rounded-xl text-[10px] font-medium uppercase tracking-[0.2em] hover:bg-white/5 transition-all flex justify-center items-center gap-2"
                        >
                          <MessageCircle size={16} /> Contactar a Ventas
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                {/* --- AQUÍ CONTINÚA EL CÓDIGO NORMAL --- */}
                
                {/* --- DASHBOARD --- */}
                {adminSubView === 'dashboard' && (
                  <div className="space-y-8 animate-in fade-in duration-500">
                    {isDemoMode && isMasterOrChain && (
                      <div className="bg-amber-50 p-6 rounded-3xl border border-amber-100 flex flex-col md:flex-row justify-between items-center gap-6 shadow-sm">
                        <div className="flex items-center gap-4">
                           <div className="bg-white p-3 rounded-2xl shadow-sm text-amber-500 animate-bounce"><Sparkles size={24} strokeWidth={1.5} /></div>
                           <div>
                              <h4 className="text-amber-900 text-[11px] font-medium uppercase mb-1 tracking-widest">Herramientas de Sandbox</h4>
                              <p className="text-amber-700/80 text-[10px] font-light uppercase tracking-[0.15em] max-w-sm">Utilice el reset global para purgar la base de datos de pruebas.</p>
                           </div>
                        </div>
                        <button disabled={isProcessing} onClick={()=>handleSeedDemoData(true)} className="bg-zinc-950 text-white px-8 py-3.5 rounded-xl text-[10px] font-medium uppercase tracking-[0.2em] hover:bg-black shadow-md transition-all active:scale-95 flex items-center gap-2">
                           <Upload size={14}/> Force Factory Reset
                        </button>
                      </div>
                    )}
                    
                    {(() => {
                      // Detectamos si estamos viendo toda la red o una sede específica
                      const isGlobalView = config.isChainMode && activeAdminHotel === 'Todos';
                      
                      // Cálculos Globales
                      const totalGuests = guests.length;
                      const totalPoints = guests.reduce((a,g) => a + Number(g.points || 0), 0);
                      const pendingGlobal = requests.filter(r => r.status === 'Pendiente' || r.status === 'PENDIENTE').length;
                      const deliveredGlobal = requests.filter(r => r.status === 'Entregado').length;
                      
                      // Cálculos Locales (Sede específica)
                      const localGuests = guests.filter(g => g.registeredAtHotel === activeAdminHotel).length;
                      const localPoints = guests.filter(g => g.registeredAtHotel === activeAdminHotel).reduce((a,g) => a + Number(g.points || 0), 0);
                      const pendingLocal = filteredRequestsAdmin.filter(r => r.status === 'Pendiente' || r.status === 'PENDIENTE').length;
                      const deliveredLocal = filteredRequestsAdmin.filter(r => r.status === 'Entregado').length;

                      // Construimos el array de tarjetas según la vista
                      const stats = isGlobalView ? [
                        { l: "Hoteles en Red", v: hotels.length, i: Building2, c: 'text-blue-500' },
                        { l: "Socios (Red)", v: totalGuests, i: Users, c: 'text-zinc-600' },
                        { l: "En Cola (Red)", v: pendingGlobal, i: Bell, c: 'text-amber-500' },
                        { l: "Capital Pts (Red)", v: totalPoints.toLocaleString(), i: TrendingUp, c: 'text-green-600' },
                        { l: "Premios Canjeados", v: deliveredGlobal, i: Gift, c: 'text-purple-500' },
                        { l: "Satisfacción Global", v: globalSatisfaction ? `${globalSatisfaction}/5` : 'N/A', i: Star, c: 'text-amber-400' }
                      ] : [
                        { l: "Socios (Sede)", v: localGuests, i: Users, c: 'text-blue-500' },
                        { l: "Tickets en Cola", v: pendingLocal, i: Bell, c: 'text-amber-500' },
                        { l: "Puntos (Sede)", v: localPoints.toLocaleString(), i: TrendingUp, c: 'text-green-600' },
                        { l: "Premios Locales", v: deliveredLocal, i: Gift, c: 'text-purple-500' }
                      ];

                      return (
                        <div className={`grid grid-cols-1 md:grid-cols-2 ${isGlobalView ? 'lg:grid-cols-3 xl:grid-cols-6' : 'lg:grid-cols-4'} gap-6`}>
                          {stats.map((k, idx) => (
                            <div key={idx} className="bg-white p-6 rounded-3xl border border-zinc-200 flex flex-col justify-between h-40 shadow-sm transition-all hover:shadow-md">
                              <k.i className={`${k.c} opacity-40`} size={28} strokeWidth={1.5} />
                              <div className="mt-4">
                                <p className="text-[9px] font-medium text-zinc-400 uppercase tracking-[0.25em] mb-2">{k.l}</p>
                                <h4 className="text-3xl font-light text-zinc-900 tracking-tighter">{k.v}</h4>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* --- BANDEJA OPERATIVA --- */}
                {adminSubView === 'requests' && (
                  <div className="space-y-6 animate-in fade-in duration-500">
                    <h3 className="text-2xl font-light uppercase tracking-widest text-zinc-900">Bandeja Operativa</h3>
                    <div className="bg-white rounded-3xl border border-zinc-200 overflow-hidden shadow-sm">
                      <table className="w-full text-left text-xs">
                        <thead>
                          <tr className="bg-zinc-100 border-b border-zinc-200 text-zinc-800 font-bold text-[10px] uppercase tracking-[0.2em]">
                            <th className="p-6">Origen / Tipo</th>
                            <th className="p-6">Huésped</th>
                            <th className="p-6">Concepto / Impacto</th>
                            <th className="p-6 text-right">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRequestsAdmin.filter(r => r.status?.toUpperCase() === 'PENDIENTE' || r.unreadByAdmin || (r.type === 'BENEFICIO' && r.status?.toUpperCase() === 'APROBADO' && !r.deliveredAt)).map(req => (
                            <tr key={req.id} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-all">
                              <td className="p-6 align-top">
                                <span className={`text-[9px] px-3 py-1 rounded-md font-bold tracking-widest uppercase ${req.type === 'FACTURA' ? 'bg-zinc-900 text-white' : req.type === 'BENEFICIO' ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>{req.type}</span>
                                <p className="text-[8px] font-bold text-zinc-500 mt-3 flex items-center gap-1 uppercase tracking-widest"><MapPin size={10}/> {req.hotelId || 'Global'}</p>
                              </td>
                              <td className="p-6 align-top">
                                <p className="text-sm font-bold text-zinc-900 mb-1">{req.guestName}</p>
                                <span className="text-[10px] text-zinc-600 font-bold tracking-widest uppercase">ID: {req.dni}</span>
                              </td>
                              <td className="p-6 align-top">
                                <p className="font-bold text-zinc-800 mb-2 truncate max-w-[200px]">{req.benefitTitle}</p>
                                <p className={`font-light text-xl tracking-tighter ${req.pts > 0 ? 'text-green-600' : 'text-amber-600'}`}>
                                  {req.type === 'ALTA' || req.type === 'MENSAJE' ? 'Tema General' : (req.pts > 0 ? `+${req.pts} PTS` : `-${req.cost} PTS`)}
                                </p>
                              </td>
                              <td className="p-6 text-right align-top">
                                <button
                                  onClick={async () => {
                                    setSelectedRequest(req);
                                    setShowModal('process_request');
                                    if (req.unreadByAdmin) {
                                      try { await updateDoc(doc(db, `artifacts/${appId}/public/data/requests`, req.id), { unreadByAdmin: false }); } catch(e) {}
                                    }
                                  }}
                                  className={`px-6 py-2.5 rounded-xl text-[9px] font-bold uppercase tracking-widest shadow-md transition-all relative active:scale-95 ${req.unreadByAdmin ? 'bg-blue-600 text-white hover:bg-blue-700' : (req.type === 'BENEFICIO' && req.status?.toUpperCase() === 'APROBADO' ? 'bg-amber-500 text-zinc-950 hover:bg-amber-400' : 'bg-zinc-950 text-white hover:bg-zinc-800')}`}
                                >
                                  {req.unreadByAdmin ? 'Responder' : (req.type === 'BENEFICIO' && req.status?.toUpperCase() === 'APROBADO' ? 'Entregar Físico' : 'Evaluar')}
                                  {req.unreadByAdmin && <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse shadow-sm"></span>}
                                </button>
                              </td>                          
                            </tr>
                          ))}
                          {filteredRequestsAdmin.filter(r => r.status?.toUpperCase() === 'PENDIENTE' || r.unreadByAdmin || (r.type === 'BENEFICIO' && r.status?.toUpperCase() === 'APROBADO' && !r.deliveredAt)).length === 0 && (
                            <tr><td colSpan="4" className="p-20 text-center text-zinc-500 uppercase tracking-widest text-[10px] font-bold">Bandeja al día. No hay pendientes.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* --- DIRECTORIO DE SOCIOS --- */}
                {adminSubView === 'guests' && (
                  <div className="space-y-8 animate-in fade-in duration-500">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 px-1">
                      <div>
                          <h3 className="text-2xl font-light uppercase tracking-widest text-zinc-900 mb-2">Directorio Integral</h3>
                          <p className="text-[10px] text-zinc-600 uppercase tracking-[0.2em] font-bold">Gestione la base de datos de socios.</p>
                      </div>
                      <div className="flex items-center gap-3">
                          <button onClick={() => setShowModal('guest_audit')} className="bg-amber-50 text-amber-600 border border-amber-200 px-6 py-3 rounded-xl text-[10px] font-bold uppercase tracking-[0.2em] shadow-sm hover:bg-amber-100 transition-all flex items-center gap-2 active:scale-95">
                            <Activity size={16} strokeWidth={2}/> Auditoría
                          </button>
                          <button onClick={() => { resetFormState(); setShowModal('add_guest'); }} className="bg-zinc-950 text-white px-6 py-3 rounded-xl text-[10px] font-medium uppercase tracking-[0.2em] shadow-md hover:bg-zinc-800 transition-all flex items-center gap-2 active:scale-95">
                            <UserPlus size={16} strokeWidth={1.5}/> Alta Socio
                          </button>
                      </div>
                    </div>

                    <div className="bg-white p-5 rounded-3xl border border-zinc-200 shadow-sm flex flex-col md:flex-row gap-4 items-center">
                        <div className="flex-1 relative w-full">
                          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={16} strokeWidth={1.5} />
                          <input type="text" placeholder="Buscar por Nombre, DNI..." className="w-full pl-12 pr-4 py-3 bg-zinc-50 border border-zinc-100 rounded-xl outline-none text-xs font-medium focus:bg-white focus:border-zinc-300 transition-all" value={dirSearchTerm} onChange={e=>setDirSearchTerm(e.target.value)}/>
                        </div>
                        <div className="w-full md:w-auto flex gap-2">
                            {config.isChainMode && (
                              <select className="w-full md:w-48 bg-zinc-50 border border-zinc-100 rounded-xl px-4 py-3 text-[10px] font-medium uppercase tracking-[0.2em] outline-none cursor-pointer focus:bg-white transition-all" value={dirHotelFilter} onChange={e=>setDirHotelFilter(e.target.value)}>
                                <option value="Todos">🌐 Toda la Cadena</option>
                                {hotels.map(h=>(<option key={h.id} value={h.id}>{h.name}</option>))}
                              </select>
                            )}
                            <select className="w-full md:w-48 bg-zinc-50 border border-zinc-100 rounded-xl px-4 py-3 text-[10px] font-medium uppercase tracking-[0.2em] outline-none cursor-pointer focus:bg-white transition-all" value={dirTierFilter} onChange={e=>setDirTierFilter(e.target.value)}>
                              <option value="Todos">Todos los Niveles</option>
                              {(config.tiers || []).map(t=>(<option key={t.id} value={t.name}>{t.name}</option>))}
                            </select>
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl border border-zinc-200 overflow-hidden shadow-sm">
                      <div className="overflow-x-auto w-full">
                        <table className="w-full text-left text-xs min-w-[700px]">
                          <thead className="bg-zinc-100 text-zinc-800 font-bold text-[10px] uppercase tracking-[0.2em] border-b border-zinc-200">
                            <tr><th className="p-6">Huésped / Credencial</th><th className="p-6 text-center">Nivel Estatus</th><th className="p-6 text-center">Saldo Actual</th><th className="p-6 text-right">Auditoría</th></tr>
                          </thead>
                          <tbody>
                          {filteredGuests.length === 0 ? (
                            <tr><td colSpan="4" className="p-10 text-center text-xs text-zinc-400 font-light uppercase tracking-widest">No hay socios en el directorio.</td></tr>
                          ) : (
                            filteredGuests.map(guest => (
                              <tr key={guest.id} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-all">
                                <td className="p-6 align-top">
                                  <p className="text-sm font-bold text-zinc-900 mb-1">{guest.name}</p>
                                  <span className="text-[10px] text-zinc-600 font-bold tracking-widest uppercase">ID / DNI: {guest.dni}</span>
                                  
                                  {config.isChainMode && (
                                    <p className="text-[9px] text-zinc-500 font-bold tracking-widest uppercase mt-1.5 flex items-center gap-1">
                                      <MapPin size={10} className="text-zinc-400"/>
                                      {guest.registeredAtHotel === 'Global' || !guest.registeredAtHotel ? 'Global Network' : (hotels.find(h=>h.id === guest.registeredAtHotel)?.name || guest.registeredAtHotel)}
                                    </p>
                                  )}
                                  
                                  {/* CIRUGÍA LÁSER: Mostramos el PIN visible para el Staff */}
                                  <p className="text-[9px] text-amber-600 font-medium tracking-widest uppercase mt-2">PIN DE ACCESO: {guest.pin}</p>
                                </td>
                                <td className="p-6 align-top text-center">
                                  <span className="text-[10px] bg-zinc-100 text-zinc-600 px-3 py-1 rounded-md font-medium tracking-widest uppercase">{guest.tier}</span>
                                </td>
                                <td className="p-6 align-top text-center">
                                  <p className="font-light text-xl tracking-tighter text-zinc-800">{guest.points} <span className="text-[9px] font-medium tracking-widest">PTS</span></p>
                                </td>
                                <td className="p-6 text-right align-top">
                                  {/* REPARACIÓN: Conectamos la función original para abrir el perfil del socio */}
                                  <button onClick={() => { setSelectedGuest(guest); setAdminSubView('guest_detail'); }} className="px-6 py-2.5 bg-zinc-100 text-zinc-600 rounded-xl text-[9px] font-medium uppercase tracking-widest shadow-sm hover:bg-zinc-200 transition-all">
                                    Ver Ficha
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                          {filteredRequestsAdmin.filter(req => req.status === 'Pendiente').length === 0 && (
                            <tr>
                              <td colSpan="4" className="p-20 text-center text-zinc-400 uppercase tracking-widest text-[10px] font-light">
                                Bandeja al día. No hay pendientes.
                              </td>
                            </tr>
                          )}
                        </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

{/* --- FICHA DETALLE DEL SOCIO --- */}
                {adminSubView === 'guest_detail' && selectedGuest && (
                  <div className="space-y-6 animate-in slide-in-from-right-4 max-w-5xl">
                    <button onClick={()=>setAdminSubView('guests')} className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 uppercase text-[10px] font-medium tracking-widest transition-all mb-2"><ArrowLeft size={16} strokeWidth={1.5}/> Volver al Listado</button>
                    
                    <div className="bg-zinc-950 text-white p-8 md:p-10 rounded-[2.5rem] shadow-xl relative overflow-hidden" style={{backgroundColor: currentBranding.primaryColor === '#C5A059' ? '#09090b' : currentBranding.primaryColor}}>
  <div className="absolute inset-0 bg-[radial-gradient(circle_at_100%_0%,rgba(255,255,255,0.1),transparent_60%)]"></div>
  <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
    <div className="flex items-center gap-6">
      <div className="w-20 h-20 rounded-full border border-white/10 flex items-center justify-center bg-black/30 shadow-lg relative"><User size={32} className="text-white" strokeWidth={1.5}/><div className="absolute -bottom-1 -right-1 bg-green-500 w-4 h-4 rounded-full border-2 border-zinc-950"></div></div>
      <div><h3 className="text-3xl font-light uppercase tracking-widest mb-1">{selectedGuest.name}</h3><p className="text-white/60 text-xs font-mono tracking-widest">ID: {selectedGuest.dni}</p>
        <div className="mt-4 flex items-center gap-3 bg-white/5 border border-white/10 p-2.5 px-5 rounded-2xl w-max backdrop-blur-md shadow-inner">
          <span className="text-[9px] font-medium uppercase text-white/60 tracking-widest">Score:</span><span className={`text-xl font-light ${getSatisfactionColor(guestSatisfaction(selectedGuest.id))}`}>{guestSatisfaction(selectedGuest.id)||'---'}</span><RatingFaces currentRating={Math.round(guestSatisfaction(selectedGuest.id))} readOnly size={16}/>
        </div>
      </div>
    </div>
    
{/* --- COLUMNA DERECHA ACCIONES Y PUNTOS UNIFICADA --- */}
    <div className="text-left md:text-right w-full md:w-auto border-t border-white/5 pt-6 md:pt-0">
      <p className="text-white text-5xl font-light tracking-tighter mb-1">
        {Number(selectedGuest.points || 0).toLocaleString()} <span className="text-[10px] font-medium text-white/60 uppercase tracking-[0.2em]">PUNTOS</span>
      </p>
      {(() => {
          if (Number(selectedGuest.points) <= 0) return null;
          const lastAct = selectedGuest.lastActivityAt?.seconds ? selectedGuest.lastActivityAt.seconds * 1000 : (selectedGuest.createdAt?.seconds ? selectedGuest.createdAt.seconds * 1000 : Date.now());
          const expireDate = new Date(lastAct);
          expireDate.setMonth(expireDate.getMonth() + 24); // 24 meses de vigencia
          
          const msLeft = expireDate.getTime() - Date.now();
          const daysLeft = Math.floor(msLeft / (1000 * 60 * 60 * 24));
          const isUrgent = daysLeft <= 90;

          return (
              <div className={`mt-2 mb-4 flex items-center md:justify-end gap-1.5 text-[9px] font-bold uppercase tracking-widest ${isUrgent ? 'text-amber-500 animate-pulse' : 'text-white/50'}`}>
                  <Clock size={12} strokeWidth={2} />
                  {isUrgent 
                    ? `¡Atención! Vencen en ${daysLeft} días` 
                    : `Vigentes hasta: ${expireDate.toLocaleDateString()}`}
              </div>
          );
      })()}
      
      <div className="flex flex-col md:items-end gap-3 mt-2">
        <span className="px-5 py-2 rounded-lg bg-black/40 text-white font-medium text-[9px] tracking-[0.2em] border border-white/10 w-max">
          {getDisplayTier(selectedGuest.tier)} MEMBER
        </span>
        
        {/* PANEL DE CONTROL DEL SOCIO (Grilla 2x2) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full mt-4">
          <button 
            onClick={() => handleManualPoints(selectedGuest.id, selectedGuest.points)}
            className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 py-3 px-2 rounded-xl text-[9px] font-bold uppercase tracking-widest transition-all shadow-sm backdrop-blur-md active:scale-95"
          >
            <Sparkles size={14} className="text-amber-400" /> Cortesía
          </button>

          <button 
            onClick={() => window.open(`https://wa.me/${selectedGuest.phone || ''}`, '_blank')} 
            className="w-full flex items-center justify-center gap-2 bg-green-500/10 text-green-500 border border-green-500/20 py-3 px-2 rounded-xl text-[9px] font-bold uppercase tracking-widest hover:bg-green-500 hover:text-black transition-all shadow-sm active:scale-95"
          >
            <MessageCircle size={14} /> WhatsApp
          </button>
          
          <button 
            onClick={() => {
              setCurrentUser(selectedGuest);
              navigate(activeTenantId ? `/guest?hotel=${activeTenantId}` : '/guest');
            }} 
            className="w-full flex items-center justify-center gap-2 bg-zinc-100 text-zinc-900 border border-zinc-200 py-3 px-2 rounded-xl text-[9px] font-bold uppercase tracking-widest hover:bg-white transition-all shadow-sm active:scale-95"
          >
            <User size={14} /> Ver Huésped
          </button>
          
          <button 
            onClick={async () => {
              // Buscamos un chat personal (que no sea una campaña publicitaria)
              let chatReq = requests.find(r => r.type === 'MENSAJE' && r.benefitTitle === 'Chat con Staff' && (r.guestId === selectedGuest.id || r.dni === selectedGuest.dni));

              if (!chatReq) {
                setIsProcessing(true);
                try {
                  const newChatData = {
                    type: 'MENSAJE', guestId: selectedGuest.id, guestName: selectedGuest.name, dni: selectedGuest.dni,
                    status: 'APROBADO', // Nace activo directamente
                    hotelId: selectedGuest.registeredAtHotel || 'Global', pts: 0, rating: 0, benefitTitle: 'Chat con Staff', messages: [], createdAt: serverTimestamp()
                  };
                  const docRef = await addDoc(collection(db, `artifacts/${appId}/public/data/requests`), newChatData);
                  chatReq = { id: docRef.id, ...newChatData };
                } catch(e) { 
                  showToast(e.message, 'error'); 
                } finally {
                  setIsProcessing(false);
                }
              } else if (chatReq.status.toUpperCase() !== 'APROBADO') {
                // Si había quedado un chat viejo trabado en "Rechazado" o "Pendiente", lo reactiva.
                updateDoc(doc(db, `artifacts/${appId}/public/data/requests`, chatReq.id), { status: 'APROBADO' });
                chatReq.status = 'APROBADO';
              }
              
              setSelectedRequest(chatReq);
              setShowModal('process_request');
            }} 
            className="w-full flex items-center justify-center gap-2 bg-amber-500 text-black py-3 px-2 rounded-xl text-[9px] font-bold uppercase tracking-widest hover:bg-amber-400 transition-all shadow-md active:scale-95"
          >
            <Send size={14} /> Iniciar Chat
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

{/* HISTORIAL Y VALORACIONES TABLA */}
<div className="bg-white rounded-3xl border border-zinc-200 overflow-hidden shadow-sm">
  <h4 className="p-6 bg-zinc-50 border-b border-zinc-100 text-[10px] font-medium uppercase tracking-[0.2em] flex items-center gap-3 text-zinc-500">
    <Activity size={18} className="text-amber-500" strokeWidth={1.5}/> Historial y Valoraciones
  </h4>
  <div className="overflow-x-auto w-full">
    <table className="w-full text-left text-xs min-w-[700px]">
      <thead>
        <tr className="bg-zinc-100 text-zinc-800 font-bold text-[10px] uppercase tracking-[0.2em] border-b border-zinc-200">
          <th className="p-6">Fecha</th>
          <th className="p-6">Concepto</th>
          <th className="p-6 text-center">Feedback</th>
          <th className="p-6 text-right">Impacto</th>
        </tr>
      </thead>
      <tbody>
        {requests.filter(r => r.guestId === selectedGuest.id || r.dni === selectedGuest.dni).map(req => (
          <tr key={req.id} className="border-b border-zinc-200 hover:bg-zinc-50/50 transition-all">
            <td className="p-6 text-zinc-600 text-[10px] font-mono">
              {new Date(req.createdAt?.seconds * 1000 || Date.now()).toLocaleDateString()}
            </td>
            <td className="p-6 font-medium text-zinc-900 leading-tight">
              {req.type === 'ALTA' || req.type === 'MENSAJE' ? 'Tema General' : req.benefitTitle}
              <span className="text-[9px] text-zinc-500 font-bold tracking-widest mt-1.5 block uppercase">{req.type}</span>
              
              {/* CHAT EN VIVO INCORPORADO EN LA FILA */}
              {req.messages && req.messages.length > 0 && (
                <div className="mt-3 space-y-2 border-l-2 border-amber-400 pl-3 py-1">
                  {req.messages.map((m, idx) => (
                    <p key={idx} className="text-[10px] text-zinc-700 leading-relaxed">
                      <strong className="uppercase text-[9px] text-amber-600">{m.sender === 'hotel' ? 'Staff' : 'Socio'}:</strong> {m.text}
                    </p>
                  ))}
                </div>
              )}
    </td>
    
    <td className="p-6 text-center flex justify-center">
      {req.rating>0?<RatingFaces currentRating={req.rating} readOnly size={18}/>:<span className="text-[9px] text-zinc-600 font-bold italic uppercase">No Valorada</span>}
    </td>
    
    <td className="p-6 text-right flex flex-col items-end gap-2 justify-center min-w-[120px]">
      
      {/* 1. IMPACTO PRINCIPAL (Puntos, Evaluar o Cerrar Entrega) */}
      {req.type === 'ALTA' && (req.status === 'Pendiente' || req.status === 'PENDIENTE') ? (
         <button onClick={() => setAdminSubView('requests')} className="px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-[9px] font-bold uppercase tracking-widest hover:bg-amber-100 transition-all">Evaluar en Bandeja</button>
      ) : req.type === 'BENEFICIO' && req.status === 'APROBADO' && !req.deliveredAt ? (
         <div className="flex flex-col items-end gap-1">
             <p className="font-light text-xl tracking-tighter text-zinc-900">-{req.cost} <span className="text-[9px] font-bold uppercase">PTS</span></p>
             <button onClick={() => handleMarkDelivered(req)} className="text-[9px] font-bold text-amber-600 underline uppercase hover:text-amber-800 transition-all">Cerrar Entrega</button>
         </div>
      ) : req.type === 'BENEFICIO' && req.deliveredAt ? (
         <div className="flex flex-col items-end gap-1">
             <p className="font-light text-xl tracking-tighter text-zinc-900">-{req.cost} <span className="text-[9px] font-bold uppercase">PTS</span></p>
             <span className="text-[8px] text-green-600 font-bold flex items-center gap-1 uppercase tracking-widest"><CheckCircle size={10} strokeWidth={1.5}/> Entregado</span>
         </div>
      ) : req.pts > 0 ? (
         <p className="font-medium text-xl tracking-tighter text-green-600">+{req.pts} <span className="text-[9px] font-bold uppercase">PTS</span></p>
      ) : req.cost > 0 ? (
         <p className="font-light text-xl tracking-tighter text-zinc-900">-{req.cost} <span className="text-[9px] font-bold uppercase">PTS</span></p>
      ) : req.type !== 'MENSAJE' ? (
         <span className="text-[9px] text-zinc-500 uppercase tracking-widest font-bold">N/A</span>
      ) : null}

      {/* 2. BOTÓN UNIVERSAL DE CHAT (Aparece siempre, en todos los tickets) */}
      {req.type !== 'ALTA' && (
          <button onClick={() => { 
             setSelectedRequest(req); setShowModal('process_request'); 
             if(req.unreadByAdmin) updateDoc(doc(db, `artifacts/${appId}/public/data/requests`, req.id), { unreadByAdmin: false });
          }} className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 transition-all font-bold text-[10px] uppercase tracking-widest relative mt-1">
             <MessageCircle size={16} strokeWidth={1.5}/> {req.messages && req.messages.length > 0 ? 'Ver Chat' : 'Abrir Chat'}
             {req.unreadByAdmin && <span className="absolute -top-1 -right-2 w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-sm"></span>}
          </button>
      )}
      
    </td>
  </tr>
))}                            {requests.filter(r=>r.guestId===selectedGuest.id||r.dni===selectedGuest.dni).length === 0 && (<tr><td colSpan="4" className="p-16 text-center text-zinc-400 font-light uppercase tracking-widest text-[10px]">Sin transacciones registradas.</td></tr>)}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}


                {/* --- CATÁLOGO STAFF --- */}
                {adminSubView === 'benefits' && (
                  <div className="space-y-8 animate-in fade-in duration-500">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-1">
                      <h3 className="text-2xl font-light uppercase tracking-widest text-zinc-900">Catálogo</h3>
                      
                      <div className="flex w-full md:w-auto items-center gap-3">
                        {/* NUEVO FILTRO PARA EL SUPERADMIN */}
                        {config.isChainMode && isMasterOrChain && activeAdminHotel === 'Todos' && (
                          <select 
                            value={catalogAdminFilter} 
                            onChange={(e) => setCatalogAdminFilter(e.target.value)}
                            className="flex-1 md:w-auto bg-white border border-zinc-200 text-zinc-600 text-[9px] font-bold uppercase tracking-widest py-3 px-4 rounded-xl outline-none shadow-sm cursor-pointer"
                          >
                            <option value="Todos">🌐 Toda la Cadena</option>
                            <option value="Global">⭐ Solo Global Network</option>
                            {hotels.map(h => <option key={h.id} value={h.id}>📍 Sede: {h.name}</option>)}
                          </select>
                        )}
                        
                        <button onClick={()=>{resetFormState();setShowModal('add_benefit')}} className="bg-zinc-950 text-white px-6 py-3 rounded-xl text-[10px] font-medium uppercase tracking-[0.2em] shadow-md hover:bg-zinc-800 transition-all flex items-center gap-2 active:scale-95 shrink-0"><Plus size={16} strokeWidth={1.5}/> Nuevo Premio</button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {benefits.filter(b => {
                        // 1. Si es hotel único, mostramos todos
                        if (!config.isChainMode) return true;
                        
                        // 2. Si el Admin ya filtró por sede desde el menú superior principal, respetamos eso
                        if (activeAdminHotel !== 'Todos') return b.hotelId === 'Todos' || b.hotelId === activeAdminHotel || !b.hotelId;
                        
                        // 3. Lógica del nuevo filtro interno (cuando el menú superior está en "Global Network")
                        if (catalogAdminFilter === 'Todos') return true;
                        if (catalogAdminFilter === 'Global') return b.hotelId === 'Todos' || !b.hotelId;
                        return b.hotelId === catalogAdminFilter;
                      }).map(b=>(
                        <div key={b.id} className="bg-white rounded-3xl border border-zinc-200 overflow-hidden group hover:shadow-lg transition-all flex flex-col h-full shadow-sm">
                          <div className="h-40 bg-zinc-100 relative flex items-center justify-center overflow-hidden">
                        {b.image && b.image.includes('http') ? (
            <img src={b.image} className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700" alt="beneficio" />
        ) : (
            <div className="text-6xl opacity-20 group-hover:scale-110 transition-all duration-500">{b.image}</div>
        )}

                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                            
                            {/* ETIQUETA DE SEDE */}
                            {config.isChainMode && (
                              <div className="absolute top-4 left-4 bg-white/90 text-zinc-900 px-2 py-1 rounded text-[8px] font-bold uppercase tracking-widest shadow-sm backdrop-blur-sm flex items-center gap-1">
                                <MapPin size={10} className="text-amber-500"/>
                                {b.hotelId === 'Todos' ? 'Global Network' : (hotels.find(h=>h.id === b.hotelId)?.name || 'Sede Local')}
                              </div>
                            )}

                            <div className="absolute top-4 right-4 bg-zinc-950 text-white px-3 py-1.5 rounded-lg text-[9px] font-medium tracking-widest shadow-md">{b.cost} PTS</div>
                          </div>
                          <div className="p-6 flex-1 flex flex-col justify-between">
                            <div className="mb-6"><h4 className="text-base font-medium uppercase tracking-widest mb-2 text-zinc-900 leading-tight">{b.title}</h4><p className="text-[10px] text-zinc-500 leading-relaxed line-clamp-2 uppercase font-light">{b.desc}</p></div>
                            <div className="flex justify-between items-end pt-5 border-t border-zinc-100 mt-auto">
                              <div><p className="text-[8px] font-medium text-zinc-400 uppercase tracking-widest mb-1">Stock Disponible</p><span className={`text-xl font-light tracking-tighter ${b.stock<5?'text-red-500':'text-zinc-800'}`}>{b.stock} <span className="text-[9px] font-medium uppercase">Uni.</span></span></div>
                              <div className="flex gap-3">
                                <button onClick={()=>{setSelectedBenefit(b);setShowModal('benefit_history')}} className="p-2.5 bg-zinc-50 rounded-lg text-zinc-400 hover:text-amber-600 hover:bg-amber-50 transition-all shadow-sm"><ListPlus size={18} strokeWidth={1.5}/></button>
                                
                                {/* PROTECCIÓN DE CADENA: Solo el Superadmin puede editar/borrar premios globales */}
                                {(!config.isChainMode || isMasterOrChain || b.hotelId !== 'Todos') && (
                                  <>
                                    <button onClick={()=>openEditModal('benefit',b)} className="p-2.5 bg-zinc-50 rounded-lg text-zinc-400 hover:text-amber-600 hover:bg-amber-50 transition-all shadow-sm"><Edit size={18} strokeWidth={1.5}/></button>
                                    <button onClick={()=>handleDelete('benefits',b.id)} className="p-2.5 bg-zinc-50 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-all shadow-sm"><Trash2 size={18} strokeWidth={1.5}/></button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* --- MARKETING Y CAMPAÑAS --- */}
                {adminSubView === 'campaigns' && (
                  <div className="space-y-8 animate-in fade-in duration-500">
                    <div className="flex justify-between items-center px-1">
                      <h3 className="text-2xl font-light uppercase tracking-widest text-zinc-900">Marketing y Campañas</h3>
                    </div>
                    <div className="bg-white p-8 rounded-3xl border border-zinc-200 shadow-sm space-y-6 max-w-3xl">
                      <p className="text-xs text-zinc-500 font-medium uppercase tracking-widest">Enviar notificaciones masivas a los socios</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> 
                         <div className="space-y-4"> 
                            <label>Filtro por Hotel</label> 
                            <select 
                               className="w-full bg-zinc-50 border border-zinc-100 p-4 rounded-xl outline-none font-medium text-sm" 
                               disabled={!isMasterOrChain} 
                               value={!isMasterOrChain ? staffSession?.hotelId : campaignHotel} 
                               onChange={e=>setCampaignHotel(e.target.value)}
                            > 
                               {isMasterOrChain && <option value="Todos">Toda la Cadena</option>} 
                               {!isMasterOrChain ? (
                                  <option value={staffSession?.hotelId}>{hotels.find(h=>h.id === staffSession?.hotelId)?.name || 'Sede Local'}</option>
                               ) : (
                                  hotels.map(h=>(<option key={h.id} value={h.id}>{h.name}</option>))
                               )}
                            </select> 
                         </div> 
                         <div className="space-y-4"> 
                            <label>Segmento de Nivel</label> 
                            <select className="w-full bg-zinc-50 border border-zinc-100 p-4 rounded-xl outline-none font-medium text-sm" value={campaignTarget} onChange={e=>setCampaignTarget(e.target.value)}> 
                               <option value="Todos">Todos los Socios</option> 
                               {(config.tiers || []).map(t=>(<option key={t.id} value={t.name}>Solo Nivel {t.name}</option>))} 
                            </select> 
                         </div> 
                      </div> 
                      
                      {/* INDICADOR DINÁMICO DE SOCIOS */}
                      <div className="bg-amber-50 border border-amber-100 p-5 rounded-2xl flex justify-between items-center shadow-inner">
                         <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Socios Alcanzados por el Filtro:</span>
                         <span className="text-2xl font-light text-amber-600">
                            {guests.filter(g => (g.status === 'ACTIVO' || !g.status) && (campaignTarget === 'Todos' || g.tier === campaignTarget) && ((!isMasterOrChain ? staffSession?.hotelId : campaignHotel) === 'Todos' || g.registeredAtHotel === (!isMasterOrChain ? staffSession?.hotelId : campaignHotel))).length}
                         </span>
                      </div>

                      <div className="space-y-4">
                         <label>Imagen Promocional (Opcional - Efecto WOW)</label>
                         <input type="text" placeholder="Pegue la URL directa de una imagen en alta resolución..." value={campaignImage} onChange={e=>setCampaignImage(e.target.value)} className="w-full bg-zinc-50 border border-zinc-100 p-4 rounded-xl outline-none font-light text-sm transition-all focus:bg-white focus:border-amber-500" />
                      </div>
                      <div className="space-y-4">
                         <label>Mensaje de la Campaña (Respeta saltos de línea)</label>
                         <textarea rows="4" placeholder="Ej: ¡Aprovecha doble puntaje este fin de semana!&#10;&#10;Te esperamos en el Spa." value={campaignMsg} onChange={e=>setCampaignMsg(e.target.value)} className="w-full bg-zinc-50 border border-zinc-100 p-4 rounded-xl outline-none font-light text-sm resize-none transition-all focus:bg-white focus:border-amber-500"></textarea>
                      </div>
                      
                      <button onClick={async () => {
                          const scopeHotel = !isMasterOrChain ? staffSession?.hotelId : campaignHotel;
                          const targets = guests.filter(g => (g.status === 'ACTIVO' || !g.status) && (campaignTarget === 'Todos' || g.tier === campaignTarget) && (scopeHotel === 'Todos' || g.registeredAtHotel === scopeHotel));
                          
                          if(!campaignMsg.trim() && !campaignImage.trim()) return showToast("Por favor añada un mensaje o una imagen", "error");
                          if(targets.length === 0) return showToast("No hay socios que coincidan con este filtro", "error");
                          
                          if(window.confirm(`Impactará a ${targets.length} socios enviando una notificación In-App. ¿Lanzar campaña?`)) {
                              setIsProcessing(true);
                              try {
                                  const batch = writeBatch(db);
                                  targets.forEach(g => {
                                      const newReqRef = doc(collection(db, `artifacts/${appId}/public/data/requests`));
                                      batch.set(newReqRef, {
                                          type: 'MENSAJE',
                                          guestId: g.id,
                                          guestName: g.name,
                                          dni: g.dni,
                                          status: 'Aprobado', 
                                          hotelId: config.isChainMode ? scopeHotel : 'Global',
                                          pts: 0,
                                          rating: 0,
                                          benefitTitle: 'Campaña: Promoción Especial',
                                          messages: [{ sender: 'hotel', text: campaignMsg.trim(), image: campaignImage.trim() || null, timestamp: Date.now() }],
                                          createdAt: serverTimestamp(),
                                          unreadByGuest: true
                                      });
                                  });
                                  
                                  // Guardamos el reporte histórico de la campaña
                                  const logRef = doc(collection(db, `artifacts/${appId}/public/data/campaigns`));
                                  batch.set(logRef, {
                                      hotelFilter: scopeHotel,
                                      tierFilter: campaignTarget,
                                      reach: targets.length,
                                      message: campaignMsg.trim(),
                                      image: campaignImage.trim() || null,
                                      launchedBy: staffSession.name,
                                      originHotel: staffSession.hotelId || 'Global',
                                      createdAt: serverTimestamp()
                                  });

                                  await batch.commit();
                                  showToast(`Campaña entregada con éxito a ${targets.length} socios.`, "success");
                                  setCampaignMsg("");
                                  setCampaignImage("");
                              } catch(e) {
                                  showToast(e.message, "error");
                              } finally {
                                  setIsProcessing(false);
                              }
                          }
                      }} className="w-full py-4 bg-amber-500 text-black rounded-xl text-[10px] font-bold uppercase tracking-[0.2em] shadow-md hover:bg-amber-400 transition-all active:scale-95 mt-4 flex justify-center items-center gap-2">
                         <Send size={18} strokeWidth={2}/> Lanzar Campaña In-App
                      </button>
                      </div>

                      {/* NUEVO: HISTORIAL DE CAMPAÑAS */}
                      <div className="bg-white rounded-3xl border border-zinc-200 shadow-sm overflow-hidden mt-8">
                        <h4 className="p-6 bg-zinc-50 border-b border-zinc-100 text-[10px] font-medium uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
                          <Clock size={16} className="text-amber-500" /> Historial de Envíos
                        </h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs min-w-[700px]">
                            <thead className="bg-zinc-100 text-zinc-800 font-bold text-[9px] uppercase tracking-[0.2em] border-b border-zinc-200">
                              <tr>
                                <th className="p-5">Fecha / Autor</th>
                                <th className="p-5">Segmento</th>
                                <th className="p-5 text-center">Alcance</th>
                                <th className="p-5 w-1/3">Mensaje</th>
                                <th className="p-5 text-right">Acción</th>
                              </tr>
                            </thead>
                            <tbody>
                              {campaignLog.length === 0 ? (
                                <tr><td colSpan="5" className="p-12 text-center text-zinc-400 font-light uppercase tracking-widest text-[10px]">No hay campañas registradas</td></tr>
                              ) : (
                                campaignLog.map(log => (
                                <tr key={log.id} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-all">
                                  <td className="p-5 align-top">
                                    <p className="font-mono text-[10px] text-zinc-600 mb-1">{new Date(log.createdAt?.seconds * 1000 || Date.now()).toLocaleDateString()}</p>
                                    <p className="text-[8px] uppercase tracking-widest text-zinc-400 font-bold">{log.launchedBy}</p>
                                    {log.originHotel && log.originHotel !== 'Global' && (
                                      <p className="text-[7px] uppercase tracking-widest text-amber-600 font-bold mt-1">📍 {hotels.find(h=>h.id === log.originHotel)?.name || log.originHotel}</p>
                                    )}
                                  </td>
                                  <td className="p-5 align-top">
                                    <p className="font-bold text-zinc-800 text-[10px] uppercase mb-1">{log.hotelFilter === 'Todos' ? 'Toda la Cadena' : (hotels.find(h=>h.id === log.hotelFilter)?.name || log.hotelFilter)}</p>
                                    <span className="text-[9px] text-amber-600 font-medium tracking-widest uppercase">{log.tierFilter === 'Todos' ? 'Todos los Niveles' : `Nivel ${log.tierFilter}`}</span>
                                  </td>
                                  <td className="p-5 align-top text-center">
                                    <span className="bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-sm font-light tracking-tighter border border-green-100">{log.reach} <span className="text-[8px] font-bold uppercase tracking-widest">Socios</span></span>
                                  </td>
                                  <td className="p-5 align-top">
                                    {log.image && <p className="text-[8px] font-bold text-blue-500 uppercase tracking-widest mb-1 flex items-center gap-1"><ImageIcon size={10}/> Incluye Imagen</p>}
                                    <p className="text-[10px] text-zinc-600 font-light leading-relaxed line-clamp-3">{log.message}</p>
                                  </td>
                                  <td className="p-5 align-top text-right">
                                    <button 
                                      onClick={() => {
                                          setCampaignMsg(log.message || "");
                                          setCampaignImage(log.image || "");
                                          setCampaignTarget(log.tierFilter || "Todos");
                                          setCampaignHotel(log.hotelFilter || "Todos");
                                          window.scrollTo({ top: 0, behavior: 'smooth' });
                                          showToast("Campaña cargada en el editor", "success");
                                      }}
                                      className="bg-zinc-100 hover:bg-amber-50 text-zinc-600 hover:text-amber-600 px-3 py-2 rounded-lg text-[9px] font-bold uppercase tracking-widest transition-all border border-zinc-200 shadow-sm inline-flex items-center gap-1.5"
                                    >
                                      <Edit3 size={12}/> Reutilizar
                                    </button>
                                  </td>
                                </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

             {/* --- CONFIGURACIÓN HUB Y CRM --- */}
                {adminSubView === 'config' && (
                  <div className="space-y-8 animate-in fade-in max-w-5xl mx-auto pb-16">
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 px-1">
                      {[
                        ...(isMasterOrChain ? [{id:'branding', l:'Identidad', i:Palette, reqPlan: 'Boutique'}] : []),
                        ...(config.isChainMode && isMasterOrChain ? [{id:'hotels', l:'Sedes', i:Building2, reqPlan: 'Enterprise'}] : []),
                        {id:'staff', l:'Equipo', i:UserPlus, reqPlan: 'Boutique'},
                        ...(isMasterOrChain ? [
                          {id:'tiers', l:'Niveles', i:Star, reqPlan: 'Boutique'},
                          {id:'preferences', l:'CRM Perfiles', i:Heart, reqPlan: 'Premium'},
                          {id:'pms', l:'PMS API', i:Webhook, reqPlan: 'Enterprise'}
                        ] : []),
                        {id:'qr', l:'QR Recepción', i:QrCode, reqPlan: 'Boutique'},
                        {id:'referral', l:'Refiera', i:Gift, reqPlan: 'Boutique'}
                      ].map(o=>{
                        const currentPlan = config.plan || 'Boutique';
                        const plansHierarchy = { 'Boutique': 1, 'CONNECT': 1, 'Premium': 2, 'GROWTH': 2, 'Signature': 3, 'ENTERPRISE': 3 };
                        const isLocked = plansHierarchy[currentPlan] < plansHierarchy[o.reqPlan];
                        
                        return (
                          <button key={o.id} onClick={() => {
                              if (isLocked) return showToast(`Exclusivo plan ${o.reqPlan} o superior.`, "error");
                              setConfigView(o.id);
                          }} className={`p-4 rounded-2xl border text-center flex flex-col items-center justify-center gap-3 transition-all duration-300 relative ${configView===o.id?'bg-zinc-950 text-white shadow-md border-zinc-950':'bg-white hover:bg-zinc-50 border-zinc-200'} ${isLocked ? 'opacity-60 cursor-not-allowed bg-zinc-50' : ''}`}>
                            {isLocked && <Lock size={12} className="absolute top-3 right-3 text-zinc-400" />}
                            <o.i size={20} className={configView===o.id?'text-amber-500':'text-zinc-400'} strokeWidth={1.5}/>
                            <h4 className="text-[8px] font-medium uppercase tracking-widest">{o.l}</h4>
                          </button>
                        );
                      })}
                    </div>
                    
                    <div className="pt-6">
                     {/* IDENTIDAD */}
                      {configView==='branding' && (
                        <div className="bg-white p-8 md:p-10 rounded-3xl border border-zinc-200 space-y-8 animate-in fade-in shadow-sm">
                          <div className="bg-zinc-950 p-6 rounded-2xl text-white shadow-md relative overflow-hidden border border-zinc-800/50">
                            {config.isSetupLocked && (
                              <div className="absolute top-1/2 -translate-y-1/2 right-6 opacity-10 pointer-events-none">
                                <Lock size={64} strokeWidth={1} />
                              </div>
                            )}
                            <label className="text-zinc-400 mb-2 block uppercase font-medium text-[9px] tracking-widest flex items-center gap-2">
                              {config.isSetupLocked ? <Lock size={10} className="text-amber-500"/> : <Building2 size={10} className="text-amber-500"/>} 
                              Modelo Operativo {config.isSetupLocked ? '(Bloqueado)' : '(Configuración Inicial)'}
                            </label>
                            <select 
                              disabled={config.isSetupLocked} 
                              className={`w-full p-3 rounded-lg outline-none font-medium text-sm transition-all appearance-none ${config.isSetupLocked ? 'bg-zinc-900/50 border border-zinc-800 text-zinc-500 cursor-not-allowed' : 'bg-zinc-900 border border-zinc-700 text-white cursor-pointer hover:border-amber-500'}`} 
                              value={config.isChainMode ? 'cadena' : 'hotel'} 
                              onChange={e => {
                                const isChain = e.target.value === 'cadena';
                                const currentPlan = config.plan || 'Boutique';
                                // Si cambia a cadena y tiene un plan básico, lo forzamos a Enterprise
                                let newPlan = currentPlan;
                                if (isChain && (currentPlan === 'Boutique' || currentPlan === 'Premium')) {
                        newPlan = 'ENTERPRISE';
                      }
                                setConfig({...config, isChainMode: isChain, plan: newPlan});
                              }}
                            >
                              <option value="hotel" className="bg-zinc-900 text-white">Propiedad Independiente</option>
                              <option value="cadena" className="bg-zinc-900 text-white">Red de Hoteles (Cadena / Enterprise)</option>
                            </select>
                            <p className={`text-[8px] uppercase tracking-widest mt-3 font-medium ${config.isSetupLocked ? 'text-zinc-500' : 'text-amber-500'}`}>
                              {config.isSetupLocked 
                                ? 'Protección de arquitectura activada. Contacte a soporte para migrar de modelo.' 
                                : '⚠️ ATENCIÓN: Al guardar los cambios, el modelo operativo quedará bloqueado por seguridad.'}
                            </p>

                            {/* --- SELECCIÓN DE PLAN (MODO PRODUCCIÓN SaaS DEFINITIVO - ZERO TOUCH) --- */}
                            <label className="text-zinc-400 mb-2 mt-6 block uppercase font-medium text-[9px] tracking-widest flex items-center gap-2">
                              <Star size={10} className="text-amber-500"/> Estado de la Suscripción
                            </label>
                            
                            {/* VISTA PARA EL CLIENTE NORMAL */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5 bg-zinc-900 border border-zinc-800/60 rounded-2xl shadow-inner gap-4">
                              <div className="flex flex-col">
                                <span className="text-zinc-500 text-[8px] uppercase tracking-widest font-bold">Plan Activo</span>
                                <span className="text-white font-bold text-sm tracking-wide mt-1">
                                  {config.isChainMode 
                                    ? `Cadena Hotelera — Nivel: ${config.plan || 'CONNECT'} (${hotels.filter(h => h.active !== false).length} ${hotels.filter(h => h.active !== false).length === 1 ? 'Sede Activa' : 'Sedes Activas'})`
                                    : `Propiedad Independiente — Plan: ${config.plan || 'Boutique'}`
                                  }
                                </span>
                                <span className="text-emerald-500 text-[8px] uppercase tracking-widest mt-2 flex items-center gap-1 font-semibold">
                                  <CheckCircle size={10} strokeWidth={2.5} /> 
                                  {config.isChainMode 
                                    ? `Tarifa actual: $${hotels.filter(h => h.active !== false).length > 1 ? (config.plan === 'ENTERPRISE' ? '189' : config.plan === 'GROWTH' ? '99' : '59') : (config.plan === 'ENTERPRISE' ? '249' : config.plan === 'GROWTH' ? '149' : '79')} USD/mes por sede activa`
                                    : 'Suscripción Individual Activa'
                                  }
                                </span>
                              </div>
                              
                              <button 
                                type="button"
                                onClick={() => {
                                  window.open('https://billing.stripe.com/p/login/tu_enlace_definitivo_de_stripe', '_blank'); 
                                }}
                                className="px-5 py-3 bg-amber-500 text-black rounded-xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-amber-400 transition-all duration-300 flex items-center justify-center gap-2 shadow-lg active:scale-95 text-center shrink-0"
                              >
                                <CreditCard size={14} strokeWidth={2.5} /> Gestionar Facturación
                              </button>
                            </div>

                            {/* --- GOD MODE: SOLO VISIBLE PARA TI (EL CREADOR) --- */}
                            {staffSession?.id === 'master' && (
                              <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl animate-in fade-in">
                                <label className="text-purple-400 mb-2 block uppercase font-bold text-[9px] tracking-widest flex items-center gap-2">
                                  <Terminal size={10} /> God Mode: Forzar Plan (Solo Master)
                                </label>
                                <select 
                                  className="w-full p-2 rounded-lg outline-none font-medium text-xs bg-zinc-950 border border-purple-500/50 text-purple-300 cursor-pointer focus:border-purple-400 transition-colors" 
                                  value={config.plan || (config.isChainMode ? 'Enterprise' : 'Boutique')} 
                                  onChange={e => setConfig({...config, plan: e.target.value})}
                                >
                                  <option value="Boutique">Plan Boutique</option>
                                  <option value="Premium">Plan Premium</option>
                                  <option value="Signature">Plan Signature (Marca Blanca)</option>
                                  <option value="Enterprise">Plan Enterprise</option>
                                  <option value="Enterprise+WB">Plan Enterprise + WB</option>
                                  <option value="Elite-6">Elite Pack 6</option>
                                  <option value="Elite-10">Elite Pack 10</option>
                                  <option value="Elite-15">Elite Pack 15</option>
                                </select>
                                <p className="text-purple-500/70 text-[8px] uppercase tracking-widest mt-2">
                                  *Este menú puentea todas las reglas de seguridad. Úselo solo para testing. Los clientes normales no ven esto.
                                </p>
                              </div>
                            )}

                            {/* --- BOTÓN SIMULADOR DE TRIAL EXPIRADO --- */}
                            <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center justify-between">
                              <div>
                                <p className="text-red-400 text-[9px] font-bold uppercase tracking-widest">Modo Desarrollo</p>
                                <p className="text-red-300/70 text-[8px] uppercase tracking-widest">Forzar fin del período de prueba.</p>
                              </div>
                              <button 
                                type="button"
                                onClick={() => setConfig({...config, simulateExpiredTrial: !config.simulateExpiredTrial})}
                                className={`px-4 py-2 rounded-lg text-[9px] font-bold uppercase tracking-widest transition-all ${config.simulateExpiredTrial ? 'bg-red-500 text-white shadow-md' : 'bg-transparent border border-red-500/50 text-red-400 hover:bg-red-500/20'}`}
                              >
                                {config.simulateExpiredTrial ? 'Bloqueo Activado' : 'Simular Bloqueo'}
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-3"><label>Nombre Comercial</label><input type="text" className="text-xl font-light border-b border-zinc-200 focus:border-amber-500 outline-none w-full py-2" value={config.isChainMode?config.chainName:config.hotelName} onChange={e=>setConfig(config.isChainMode?{...config,chainName:e.target.value}:{...config,hotelName:e.target.value})} /></div>
                            <div className="space-y-3"><label>Color Institucional (HEX)</label><div className="flex items-center gap-4 bg-zinc-50 p-3 rounded-xl border border-zinc-100"><input type="color" value={config.primaryColor} onChange={e=>setConfig({...config,primaryColor:e.target.value})} className="w-10 h-10 rounded-full cursor-pointer border-none bg-transparent" /><span className="font-mono text-zinc-700 font-medium text-sm uppercase">{config.primaryColor}</span></div></div>
                          </div>
                          
                          {/* --- INICIO CIRUGÍA: BONO DE BIENVENIDA --- */}
                          <div className="space-y-3 pt-2">
                            <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                              <Gift size={14} className="text-amber-500" /> Bono de Bienvenida (Puntos)
                            </label>
                            <input 
                              type="number" 
                              className="text-sm font-bold text-zinc-800 bg-zinc-50 p-3 rounded-xl border border-zinc-200 w-full md:w-1/2 shadow-sm outline-none focus:bg-white focus:border-amber-500/50 transition-all" 
                              placeholder="Ej: 500" 
                              value={config.welcomePoints || 0} 
                              onChange={e => setConfig({...config, welcomePoints: parseInt(e.target.value) || 0})} 
                            />
                            <p className="text-[9px] text-zinc-400 font-medium tracking-wide">
                              Capital inicial que se le regala al huésped al crear su cuenta.
                            </p>
                          </div>

                          {/* --- INICIO CIRUGÍA: MOTOR DE RESERVAS --- */}
                          <div className="space-y-3 pt-2 border-t border-zinc-100">
                            <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                              <Globe size={14} className="text-amber-500" /> URL Motor de Reservas Directo
                            </label>
                            <input 
                              type="text" 
                              className="text-sm font-medium text-zinc-800 bg-zinc-50 p-3 rounded-xl border border-zinc-200 w-full shadow-sm outline-none focus:bg-white focus:border-amber-500/50 transition-all" 
                              placeholder="Ej: https://reservas.mihotel.com" 
                              value={config.bookingUrl || ''} 
                              onChange={e => setConfig({...config, bookingUrl: e.target.value})} 
                            />
                            <p className="text-[9px] text-zinc-400 font-medium tracking-wide">
                              Si se configura, aparecerá un botón destacado en el portal del huésped para desviar tráfico de las OTAs.
                            </p>
                          </div>
                          {/* --- FIN CIRUGÍA --- */}

                          <div className="space-y-4 pt-4 border-t border-zinc-100">
                            <label>Logotipo</label>
                            <div className="flex flex-col md:flex-row items-center gap-6 bg-zinc-50 p-6 rounded-3xl border border-dashed border-zinc-200">
                              <div className="w-full md:w-1/3 flex items-center justify-center bg-white p-4 rounded-xl border border-zinc-100 shadow-sm min-h-[100px]"><img src={config.logo} className="max-h-16 max-w-full object-contain" alt="Logo de la propiedad" /></div>
                              <div className="flex-1 w-full space-y-3">
                                <input type="text" className="hidden text-xs bg-white p-3 rounded-lg border border-zinc-200 w-full shadow-sm" placeholder="URL Logo..." value={config.logo} onChange={e=>setConfig({...config,logo:e.target.value})} />
                                <div className="relative"><input type="file" accept="image/*" onChange={handleFileUpload} className="absolute inset-0 opacity-0 cursor-pointer" /><button className="w-full py-3 bg-zinc-200 text-zinc-700 text-[9px] font-medium uppercase tracking-widest rounded-lg hover:bg-zinc-300 transition-all">Explorar Archivo</button></div>
                              </div>
                            </div>
                          </div>
                          <div className="pt-6 flex justify-end border-t border-zinc-100">
                            <div className="col-span-1 md:col-span-2 mt-4 pt-4 border-t border-zinc-100">
                    <h4 className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-4">Documentación Legal (Opcional)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-zinc-600">URL Términos y Condiciones</label>
                            <input type="text" placeholder="https://..." value={config.termsUrl || ''} onChange={e => setConfig({...config, termsUrl: e.target.value})} className="w-full p-3 bg-zinc-50 border border-zinc-200 rounded-xl outline-none focus:bg-white transition-all text-xs"/>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-zinc-600">URL Política de Privacidad</label>
                            <input type="text" placeholder="https://..." value={config.privacyUrl || ''} onChange={e => setConfig({...config, privacyUrl: e.target.value})} className="w-full p-3 bg-zinc-50 border border-zinc-200 rounded-xl outline-none focus:bg-white transition-all text-xs"/>
                        </div>
                    </div>
                </div>
                            <button onClick={async()=>{
                              setIsProcessing(true);
                              try {
                                const finalConfig = { ...config, isSetupLocked: true };
                                await setDoc(doc(db,`/artifacts/${appId}/public/data/settings/config`), finalConfig);
                                setConfig(finalConfig);
                                showToast("Configuración Inicial Guardada y Bloqueada.", "success");
                              } catch(e) {
                                showToast(e.message,'error');
                              } finally {
                                setIsProcessing(false);
                              }
                            }} className="bg-zinc-950 text-white px-8 py-3 rounded-xl text-[10px] font-medium uppercase tracking-widest shadow-md hover:bg-zinc-800 transition-all active:scale-95">Guardar Cambios</button>
                          </div>
                        </div>
                      )}
                      {/* --- MÓDULO REFIERA PAXFIDELY --- */}
                      {/* --- MÓDULO REFIERA PAXFIDELY --- */}
                      {configView==='referral' && (() => {
                        // Lógica dinámica: Detecta si estamos en una sede o en la red global
                        const referralCode = config.isChainMode && activeAdminHotel !== 'Todos' 
                          ? activeAdminHotel.toUpperCase()
                          : appId.replace('tenant-', '').toUpperCase();
                        
                        const referralLink = `https://paxfidely.com/onboarding?ref=${referralCode}`;

                        return (
                          <div className="bg-white p-8 md:p-10 rounded-3xl border border-zinc-200 space-y-8 animate-in fade-in shadow-sm">
                             
                             {/* Encabezado */}
                             <div className="border-b border-zinc-100 pb-6 flex items-center justify-between">
                               <div>
                                 <h3 className="text-2xl font-light text-zinc-900 tracking-tight uppercase mb-1">Refiera <span className="font-bold text-amber-500">PAXFIDELY</span></h3>
                                 <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-widest">
                                   {config.isChainMode && activeAdminHotel !== 'Todos' 
                                     ? `Invite a gerentes a sumar su hotel y gane crédito para ${hotels.find(h=>h.id === activeAdminHotel)?.name || 'su sede'}.` 
                                     : 'Invite a gerentes a sumar su hotel y gane crédito para la cadena.'}
                                 </p>
                               </div>
                               <Gift size={32} className="text-amber-500 opacity-80" strokeWidth={1.5} />
                             </div>

                             {/* QR y Link */}
                             <div className="flex flex-col md:flex-row gap-10 items-center justify-center">
                               
                               {/* QR Code generado automáticamente con la API */}
                               <div className="flex-shrink-0">
                                  <div className="w-48 h-48 bg-zinc-50 rounded-2xl flex items-center justify-center border border-dashed border-zinc-300 p-4 shadow-inner">
                                     <img
                                       src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&color=27272a&data=${encodeURIComponent(referralLink)}`}
                                       alt="QR Referidos"
                                       className="w-full h-full object-contain mix-blend-multiply transition-all"
                                     />
                                  </div>
                               </div>

                               {/* Inputs para copiar */}
                               <div className="flex-1 w-full space-y-6">
                                  <div className="space-y-2">
                                    <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Link de Referido Personalizado</label>
                                    <div className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-xl p-2 pl-4">
                                      <input type="text" value={referralLink} disabled className="flex-1 bg-transparent text-xs text-zinc-600 outline-none" />
                                      <button onClick={() => { navigator.clipboard.writeText(referralLink); showToast('Link copiado al portapapeles', 'success'); }} className="p-2 bg-white rounded-lg text-amber-600 hover:bg-amber-50 border border-zinc-100 transition-all shadow-sm"><Copy size={16} /></button>
                                    </div>
                                  </div>

                                  <div className="space-y-2">
                                    <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Código de Invitación Único</label>
                                    <div className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-xl p-2 pl-4">
                                      <input type="text" value={referralCode} disabled className="flex-1 bg-transparent text-sm font-bold tracking-widest text-amber-600 outline-none" />
                                      <button onClick={() => { navigator.clipboard.writeText(referralCode); showToast('Código copiado al portapapeles', 'success'); }} className="p-2 bg-white rounded-lg text-amber-600 hover:bg-amber-50 border border-zinc-100 transition-all shadow-sm"><Copy size={16} /></button>
                                    </div>
                                  </div>
                               </div>
                             </div>

                             {/* Reglas y Botón WhatsApp */}
                             <div className="bg-zinc-950 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
                               <div className="space-y-3 flex-1 text-center md:text-left">
                                 <h4 className="text-sm font-bold text-amber-500 uppercase tracking-widest">Recompensas Exclusivas</h4>
                                 <ul className="space-y-2 text-xs font-light text-zinc-300">
                                   <li className="flex items-center gap-2"><Check size={14} className="text-amber-500"/> <span className="font-bold text-white">1 mes bonificado</span> por cada hotel contratado.</li>
                                   <li className="flex items-center gap-2"><Check size={14} className="text-amber-500"/> <span className="font-bold text-white">3 meses bonificados</span> por cadenas (3+ hoteles).</li>
                                 </ul>
                               </div>
                               <button
                                 onClick={() => {
                                   const msg = `Te invito a conocer PAXFIDELY, el estándar en fidelización hotelera. Regístrate y prueba la aplicación desde mi enlace: ${referralLink}`;
                                   window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`, '_blank');
                                 }}
                                 className="w-full md:w-auto bg-[#25D366] hover:bg-[#128C7E] text-white font-bold py-4 px-8 rounded-xl flex items-center justify-center gap-3 text-[11px] uppercase tracking-widest shadow-lg transition-all active:scale-95 whitespace-nowrap"
                               >
                                 <MessageCircle size={18} /> Enviar por WhatsApp
                               </button>
                             </div>

                             {/* Legales */}
                             <p className="text-[9px] text-zinc-400 text-center font-medium uppercase tracking-widest pt-4">
                               El premio se hace efectivo una vez contratado por el referido, con período de permanencia mínimo de 6 meses.
                             </p>

                          </div>
                        );
                      })()}
                      {/* --- NUEVA PESTAÑA: QR DE RECEPCIÓN MINIMALISTA --- */}
                      {configView==='qr' && (
                        <div className="bg-white p-8 md:p-10 rounded-3xl border border-zinc-200 shadow-sm animate-in fade-in">
                           <div className="border-b border-zinc-100 pb-5 mb-8">
                             <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900 mb-1">Ecosistema Móvil</h3>
                             <p className="text-[10px] text-zinc-400 uppercase tracking-widest font-medium">Material oficial para exhibir en el Front-Desk del hotel.</p>
                           </div>

                           <div className="flex flex-col md:flex-row items-center gap-10">
                             {/* Contenedor del QR estilo Tarjeta Digital */}
                             <div className="flex-shrink-0">
                                <div className="w-56 h-56 bg-zinc-50 rounded-2xl flex items-center justify-center border border-dashed border-zinc-300 p-5 relative shadow-inner">
                                    <img 
                                      src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&color=27272a&data=${encodeURIComponent(window.location.origin + '/?hotel=' + (activeTenantId || ''))}`} 
                                      alt="QR Recepción" 
                                      className="w-full h-full object-contain relative z-10 mix-blend-multiply"
                                    />
                                </div>
                             </div>
                             
                             {/* Textos y botones limpios */}
                             <div className="flex-1 text-center md:text-left">
                                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-zinc-100 text-zinc-600 rounded-md text-[9px] font-bold uppercase tracking-widest mb-4 border border-zinc-200">
                                   <QrCode size={12} /> Escaneo Universal
                                </div>
                                <h3 className="text-3xl font-light text-zinc-900 uppercase tracking-widest mb-3">QR de Recepción</h3>
                                <p className="text-zinc-500 text-sm leading-relaxed mb-8 max-w-xl">
                                  Exhiba este código a sus huéspedes. Al escanearlo, ingresarán directamente a su portal de fidelización sin necesidad de descargar aplicaciones.
                                </p>
                                <div className="flex flex-col sm:flex-row justify-center md:justify-start gap-4">
                                   <button 
                                     onClick={() => window.open(`https://api.qrserver.com/v1/create-qr-code/?size=1000x1000&color=27272a&data=${encodeURIComponent(window.location.origin + '/?hotel=' + (activeTenantId || ''))}`)} 
                                     className="px-8 py-4 rounded-xl text-[10px] font-bold uppercase tracking-[0.2em] transition-all hover:scale-105 active:scale-95 shadow-md"
                                     style={{ backgroundColor: currentBranding.primaryColor, color: currentBranding.primaryColor === '#C5A059' ? '#000' : '#fff' }}
                                   >
                                     Descargar Código HD
                                   </button>
                                   <button 
                                     onClick={() => {
                                        navigator.clipboard.writeText(window.location.origin + '/?hotel=' + (activeTenantId || '')); 
                                        showToast('Enlace copiado al portapapeles');
                                     }} 
                                     className="bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border border-zinc-200 px-8 py-4 rounded-xl text-[10px] font-bold uppercase tracking-[0.2em] transition-all"
                                   >
                                     Copiar Enlace
                                   </button>
                                </div>
                             </div>
                           </div>
                        </div>
                      )}
                      
                      {/* SEDES */}
                      {configView==='hotels' && (
                        <div className="space-y-6 animate-in fade-in">
                          <div className="flex justify-between items-center px-1">
                            <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900">Propiedades</h3>
                            <button 
                              onClick={() => {
                                // --- NUEVO CONTROL DE SEDES ILIMITADAS PARA CADENAS ---
                if (!editMode && !config.isChainMode && hotels.length >= 1) {
                   setShowModal(null);
                   return setUpgradeRequired({ module: "Multi-Sede (Administrar más de 1 hotel)", plan: "ENTERPRISE" });
                }
                                
                                resetFormState();
                                setShowModal('add_hotel');
                              }} 
                              className="bg-zinc-950 text-white px-5 py-2.5 rounded-lg text-[9px] font-medium uppercase tracking-widest shadow-md hover:bg-zinc-800 transition-all flex items-center gap-2"
                            >
                              <Plus size={14}/> Nueva Sede
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {hotels.map(h=>(
                              <div key={h.id} className="bg-white p-6 rounded-3xl border border-zinc-200 flex flex-col justify-between h-44 group hover:shadow-md transition-all shadow-sm">
                                <div>
                                  <h6 className="text-lg font-medium uppercase mb-1.5 text-zinc-900 leading-tight">{h.name}</h6>
                                  <p className="text-[9px] font-medium text-zinc-400 uppercase tracking-widest flex items-center gap-1.5"><MapPin size={10} className="text-amber-500"/> {h.city}</p>
                                </div>
                                <div className="flex justify-between items-center pt-5 border-t border-zinc-50 mt-auto">
                                  <button 
                                    onClick={async () => {
                                      const nuevoEstado = h.active === false;
                                      try {
                                        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'hotels', h.id), { active: nuevoEstado });
                                        showToast(`Sede ${nuevoEstado ? 'activada' : 'desactivada'} con éxito`);
                                      } catch(e) { showToast(e.message, 'error'); }
                                    }}
                                    className={`text-[8px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-all active:scale-95 ${
                                      h.active !== false 
                                        ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' 
                                        : 'bg-zinc-100 text-zinc-400 border-zinc-200 hover:bg-zinc-200'
                                    }`}
                                    title="Haga clic para pausar o activar la facturación y acceso de esta sede"
                                  >
                                    {h.active !== false ? '● Activa' : '○ Inactiva'}
                                  </button>
                                  <div className="flex gap-3">
                                    <button onClick={()=>openEditModal('hotel',h)} className="p-2 bg-zinc-50 rounded text-zinc-400 hover:text-amber-600 transition-all"><Edit size={16} strokeWidth={1.5}/></button>
                                    <button onClick={()=>handleDelete('hotels',h.id)} className="p-2 bg-zinc-50 rounded text-zinc-400 hover:text-red-500 transition-all"><Trash2 size={16} strokeWidth={1.5}/></button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* STAFF */}
                      {configView==='staff' && (
                        <div className="space-y-6 animate-in fade-in">
                           <div className="flex justify-between items-center px-1">
                              <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900">Equipo Staff</h3>
                              <button onClick={()=>{resetFormState();setShowModal('add_staff')}} className="bg-zinc-950 text-white px-5 py-2.5 rounded-lg text-[9px] font-medium uppercase tracking-widest shadow-md hover:bg-zinc-800 transition-all flex items-center gap-2"><Plus size={14}/> Nuevo Miembro</button>
                           </div>
                           <div className="bg-white rounded-3xl border border-zinc-200 overflow-hidden shadow-sm">
                              <table className="w-full text-left text-xs min-w-[500px]">
                                 <thead className="bg-zinc-50 text-zinc-500 font-medium text-[9px] uppercase tracking-[0.2em] border-b border-zinc-100">
                                    <tr><th className="p-5">Miembro</th><th className="p-5">Rol / Acceso</th><th className="p-5">Sede Asignada</th><th className="p-5 text-right">Acción</th></tr>
                                 </thead>
                                 <tbody>
                                    {filteredStaffAdmin.map(s=>(
                                       <tr key={s.id} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-all">
                                          <td className="p-5 font-medium text-zinc-900">{s.name}<br/><span className="text-[9px] text-zinc-400 font-light tracking-widest mt-1 block">{s.email}</span></td>
                                          <td className="p-5"><span className="px-3 py-1 rounded-md bg-zinc-100 text-zinc-600 font-medium text-[9px] tracking-widest uppercase">{s.role}</span></td>
                                          <td className="p-5"><span className="text-[9px] text-zinc-500 font-bold uppercase tracking-widest">{s.hotelId === 'Todos' ? 'Global Network' : (hotels.find(h=>h.id === s.hotelId)?.name || s.hotelId)}</span></td>
                                          <td className="p-5 text-right flex justify-end gap-3">
                                            {s.id !== 'master' && (
                                              <>
                                                <button onClick={()=>openEditModal('staff',s)} className="p-2 bg-zinc-50 rounded text-zinc-400 hover:text-amber-600 transition-all"><Edit size={16}/></button>
                                                <button onClick={()=>handleDelete('staff',s.id)} className="p-2 bg-zinc-50 rounded text-zinc-400 hover:text-red-500 transition-all"><Trash2 size={16}/></button>
                                              </>
                                            )}
                                          </td>
                                       </tr>
                                    ))}
                                 </tbody>
                              </table>
                           </div>
                        </div>
                      )}

                      {/* PMS INTEGRACIÓN */}
                      {configView==='pms' && (
                        <div className="bg-white p-8 md:p-10 rounded-3xl border border-zinc-200 space-y-8 animate-in fade-in shadow-sm">
                           <div className="border-b border-zinc-100 pb-5">
                              <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900 mb-1">Integración PMS / Hub</h3>
                              <p className="text-[10px] text-zinc-400 uppercase tracking-widest font-medium">Conecte su sistema de gestión mediante API.</p>
                           </div>
                           <div className="bg-zinc-50 p-8 rounded-2xl border border-zinc-100 text-center space-y-4">
                              <Webhook size={40} className="mx-auto text-amber-500 opacity-50" strokeWidth={1.5} />
                              <h4 className="text-sm font-medium text-zinc-900">Módulo API en desarrollo</h4>
                              <p className="text-xs text-zinc-500 font-light max-w-md mx-auto leading-relaxed">Para conectar su PMS actual, contacte al soporte técnico de PaxFidely.</p>
                              <button className="mt-4 px-6 py-3 bg-white border border-zinc-200 rounded-xl text-[10px] font-medium uppercase tracking-widest text-zinc-600 shadow-sm hover:text-amber-600 transition-all">Solicitar Docs</button>
                           </div>
                        </div>
                      )}

                      {/* CRM PREFERENCIAS */}
                      {configView==='preferences' && (
                        <div className="bg-white p-8 md:p-10 rounded-3xl border border-zinc-200 space-y-8 animate-in fade-in shadow-sm">
                           <div className="border-b border-zinc-100 pb-5">
                             <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900 mb-1">CRM de Preferencias</h3>
                             <p className="text-[10px] text-zinc-400 uppercase tracking-widest font-medium">Gestione las etiquetas para perfilar a sus socios.</p>
                           </div>
                           <div className="space-y-8">
                              {Object.entries(preferenceSettings).map(([category, tags]) => (
                                 <div key={category} className="bg-zinc-50 p-6 rounded-2xl border border-zinc-100">
                                   <div className="flex justify-between items-center mb-5">
                                     <h4 className="text-[10px] font-medium text-zinc-800 uppercase tracking-[0.2em] flex items-center gap-2"><Tag size={14} className="text-amber-500"/> {category}</h4>
                                     <button onClick={()=>removePrefCategory(category)} className="text-zinc-400 hover:text-red-500 transition-colors"><Trash2 size={16} strokeWidth={1.5}/></button>
                                   </div>
                                   <div className="flex flex-wrap gap-2 mb-5">
                                      {tags.map(tag => (
                                        <div key={tag} className="bg-white border border-zinc-200 px-3 py-1.5 rounded-lg text-[9px] font-medium text-zinc-600 uppercase tracking-widest flex items-center gap-2 shadow-sm">
                                           {tag} <button onClick={()=>removePrefTag(category, tag)} className="text-zinc-300 hover:text-red-500"><XCircle size={12}/></button>
                                        </div>
                                      ))}
                                      {tags.length === 0 && <span className="text-[9px] text-zinc-400 font-light italic">Sin opciones cargadas.</span>}
                                   </div>
                                   <div className="flex gap-2">
                                      <input type="text" placeholder="Nueva etiqueta..." value={newPrefTag[category] || ""} onChange={e=>setNewPrefTag({...newPrefTag, [category]: e.target.value})} className="bg-white border border-zinc-200 rounded-lg px-4 py-2 text-xs outline-none flex-1 font-light" />
                                      <button onClick={()=>addPrefTag(category)} className="bg-zinc-900 text-white px-5 rounded-lg text-[9px] font-medium uppercase tracking-widest hover:bg-zinc-800 transition-all shadow-md">Añadir</button>
                                   </div>
                                 </div>
                              ))}
                              
                              <div className="flex flex-col md:flex-row gap-3 pt-4">
                                <input type="text" placeholder="Nueva Categoría (Ej: Vinos, Almohadas...)" value={newPrefCategory} onChange={e=>setNewPrefCategory(e.target.value)} className="bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-3 text-xs outline-none flex-1 font-light focus:bg-white transition-all" />
                                <button onClick={addPrefCategory} className="bg-amber-500 text-black px-6 py-3 rounded-xl text-[10px] font-medium uppercase tracking-widest hover:bg-amber-400 transition-all shadow-md flex items-center justify-center gap-2"><Plus size={16} strokeWidth={1.5}/> Crear Grupo</button>
                              </div>
                           </div>
                        </div>
                      )}

                      {/* NIVELES Y CASHBACK */}
                      {configView==='tiers' && (
                        <div className="bg-white p-8 rounded-3xl border border-zinc-200 space-y-8 animate-in fade-in shadow-sm">
                          <div className="flex justify-between items-center border-b border-zinc-100 pb-5">
                             <div>
                               <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900 mb-1">Niveles de Lealtad</h3>
                               <p className="text-[10px] text-zinc-400 uppercase tracking-widest font-medium">Gestione los niveles y su retorno de puntos.</p>
                             </div>
                             <button onClick={()=>{
                               const newTiers = [...(config.tiers || []), { id: 't'+Date.now(), active: true, name: 'Nuevo Nivel', cashback: 0, color: '#9ca3af' }];
                               setConfig({...config, tiers: newTiers});
                             }} className="bg-amber-50 text-amber-600 px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-amber-100 transition-all"><Plus size={14} strokeWidth={2}/> Nivel</button>
                          </div>
                          <div className="grid grid-cols-1 gap-4">
                             {(config.tiers || []).map((t, idx) => (
                               <div key={t.id} className="bg-zinc-50 p-4 rounded-2xl border border-zinc-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                  <div className="flex items-center gap-3 w-full md:w-auto">
                                    <input type="color" value={t.color || '#9ca3af'} onChange={e=>{
                                        const newTiers = [...config.tiers]; newTiers[idx].color = e.target.value; setConfig({...config, tiers: newTiers});
                                    }} className="w-8 h-8 rounded-full border-none cursor-pointer bg-transparent" />
                                    <input type="text" value={t.name} onChange={e=>{
                                        const newTiers = [...config.tiers]; newTiers[idx].name = e.target.value; setConfig({...config, tiers: newTiers});
                                    }} className="bg-white border border-zinc-200 rounded-lg p-2 text-xs outline-none font-medium flex-1 md:w-48 uppercase tracking-widest" />
                                  </div>
                                  <div className="flex items-center gap-4 w-full md:w-auto justify-between">
                                     <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest">Cashback:</span>
                                        <input type="number" value={t.cashback} onChange={e => {
                                           const newTiers = [...config.tiers]; newTiers[idx].cashback = Number(e.target.value); setConfig({...config, tiers: newTiers});
                                        }} className="w-16 bg-white border border-zinc-200 rounded-lg p-2 text-center text-sm outline-none font-medium" />
                                        <span className="text-xs font-medium text-zinc-400">%</span>
                                     </div>
                                     <button onClick={()=>{
                                        const newTiers = config.tiers.filter((_, i) => i !== idx); setConfig({...config, tiers: newTiers});
                                     }} className="text-zinc-400 hover:text-red-500 transition-colors bg-white p-2 rounded-lg border border-zinc-200 shadow-sm"><Trash2 size={16} strokeWidth={1.5}/></button>
                                  </div>
                               </div>
                             ))}
                          </div>
                          <div className="pt-4 flex justify-end">
                            <button onClick={async()=>{setIsProcessing(true);try{await setDoc(doc(db,`/artifacts/${appId}/public/data/settings/config`),config);showToast("Niveles Guardados.");}catch(e){showToast(e.message,'error');}finally{setIsProcessing(false);}}} className="bg-zinc-950 text-white px-8 py-3 rounded-xl text-[10px] font-medium uppercase tracking-widest shadow-md hover:bg-zinc-800 transition-all active:scale-95">Guardar Niveles</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
            {/* --- INICIO INTERFAZ VISUAL: MODAL DE UPSELL AUTOMATIZADO --- */}
                {upgradeRequired && (
                  <div className="fixed inset-0 z-[60] bg-zinc-950/80 backdrop-blur-md flex flex-col items-center justify-center p-6 animate-in fade-in duration-300">
                    <div className="bg-zinc-900 border border-zinc-800/80 p-10 rounded-[3rem] shadow-[0_25px_60px_rgba(0,0,0,0.8)] max-w-md w-full text-center relative overflow-hidden">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_rgba(245,158,11,0.05),_transparent_50%)]"></div>
                      
                      {/* Botón de salida elegante */}
                      <button onClick={() => setUpgradeRequired(null)} className="absolute top-6 right-6 text-zinc-500 hover:text-white transition-colors">
                        <LogOut size={18} className="rotate-180" />
                      </button>
                      
                      {/* Ícono de alta tecnología */}
                      <div className="w-20 h-20 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-amber-500/20 shadow-[0_0_30px_rgba(245,158,11,0.15)] animate-pulse">
                        <Lock size={30} className="text-amber-500" strokeWidth={1.5} />
                      </div>
                      
                      <h3 className="text-2xl text-white font-light uppercase tracking-[0.15em] mb-3">Módulo Exclusivo</h3>
                      <p className="text-zinc-400 text-[11px] uppercase tracking-[0.2em] leading-relaxed mb-10 font-medium px-2">
                        Para habilitar el acceso a <span className="text-amber-500 font-bold">{upgradeRequired.module}</span>, es necesario actualizar de forma instantánea su suscripción al nivel <span className="text-white font-bold">{upgradeRequired.plan}</span> o superior.
                      </p>
                      
                      <div className="space-y-4 relative z-10">
                        {/* El único botón de acción: Checkout 100% Automatizado */}
                        <button 
                          onClick={() => {
                            // Este link abrirá tu Stripe Checkout personalizado con el precio del plan requerido
                            window.open('https://buy.stripe.com/test_link_pago_recurrente_SaaS', '_blank'); 
                            setUpgradeRequired(null);
                          }}
                          className="w-full py-4.5 bg-amber-500 text-black rounded-xl text-[10px] font-black uppercase tracking-[0.25em] hover:bg-amber-400 hover:shadow-[0_0_25px_rgba(245,158,11,0.4)] transition-all flex justify-center items-center gap-2 active:scale-95 duration-300"
                        >
                          <CreditCard size={16} strokeWidth={2.5} /> Desbloquear Instantáneamente
                        </button>
                        
                        <p className="text-[8px] text-zinc-500 uppercase tracking-widest font-medium">
                          🔒 Facturación segura mediante Stripe de extremo a extremo. Permisos provistos en tiempo real tras el pago.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {/* --- FIN INTERFAZ VISUAL: MODAL DE UPSELL AUTOMATIZADO --- */}
            </main>

            </div>
          </div>
        ) : (
          <div className="flex-1 bg-white flex flex-col p-8 justify-center max-w-md mx-auto text-center animate-in fade-in duration-500">
             
             {/* Logo dinámico en login de Staff */}
             <div className="flex justify-center mb-10">
                 <img src={tenantData?.logoUrl || currentBranding.logo} alt="Logo" className="h-16 object-contain" />
             </div>

            <h2 className="text-2xl font-light text-zinc-900 mb-10 uppercase tracking-[0.2em]">{t.staff_access}</h2>
            <div className="space-y-6 text-left">
              <div className="space-y-4">
                <div>
                  <label>Email Corporativo</label>
                  <input type="email" placeholder="ejemplo@hotel.com" value={adminLoginData.email} onChange={e=>setAdminLoginData({...adminLoginData, email: e.target.value})} className="p-4 bg-zinc-50 border border-zinc-100 rounded-xl w-full font-medium outline-none focus:bg-white transition-all text-sm" />
                </div>
                <div>
                  <label>PIN Maestro (Solo creador)</label>
                  <input type="password" placeholder="••••" maxLength="4" className="text-2xl tracking-[0.5em] font-mono text-center p-4 bg-zinc-50 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all" value={adminLoginData.pin} onChange={e=>setAdminLoginData({...adminLoginData, pin: e.target.value})} />
                </div>
              </div>
              <button onClick={() => { 
                  // 1. Bypass para el master absoluto
                  if (adminLoginData.email === 'master@loyalty.com' && adminLoginData.pin === '9999') { 
                      setStaffSession({ id: 'master', name: 'System', role: 'System', hotelId: 'Todos' }); return; 
                  } 
                  
                  // 2. Bypass MÁGICO para el dueño del hotel que viene del Onboarding
                  if (tenantData && adminLoginData.email.toLowerCase() === tenantData.adminEmail.toLowerCase()) {
                      setStaffSession({ id: 'owner', name: tenantData.adminName, role: 'Superadmin', hotelId: 'Todos' });
                      showToast("Acceso Concedido (Admin Creador).", "success");
                      return;
                  }

                  // 3. Verificación de staff normal creado en la app
                  const f = staff.find(s => s.email.toLowerCase() === adminLoginData.email.toLowerCase() && s.pin === adminLoginData.pin); 
                  if (f) { 
                      // VALIDACIÓN DE SEDE ACTIVA
                      const hotelAsignado = hotels.find(h => h.id === f.hotelId);
                      const esStaffLocal = f.role !== 'Superadmin' && f.role !== 'System';
                      
                      if (config.isChainMode && esStaffLocal && hotelAsignado && hotelAsignado.active === false) {
                        return showToast("Acceso suspendido: Su sede asignada no se encuentra operativa.", "error");
                      }

                      setStaffSession(f); showToast("Acceso Concedido."); 
                  } else {
                      showToast("Credenciales inválidas", "error"); 
                  }
                  
              }} className="w-full py-4 rounded-xl text-white uppercase tracking-[0.2em] text-[11px] font-medium shadow-xl btn-premium mt-4 transition-all" style={{ backgroundColor: currentBranding.primaryColor }}>Acceder al Hub</button>
              {isDemoMode && (
                 <button onClick={() => { setAdminLoginData({ email: 'gerencia@demo.com', pin: '1234' }); showToast("Credenciales demo listas", "success"); }} className="w-full py-3 rounded-xl border border-amber-500 text-amber-600 text-[10px] font-medium uppercase transition-all hover:bg-amber-50 mt-4">⚡ Autocompletar Demo</button>
              )}

            </div>
            <div className="pt-8 border-t border-zinc-50 mt-8 flex justify-center">
               <button onClick={() => {navigate(activeTenantId ? `/?hotel=${activeTenantId}` : '/');}} className="py-3 px-6 border border-zinc-200 rounded-xl text-[9px] font-medium text-zinc-500 uppercase tracking-widest hover:bg-zinc-50 transition-all flex items-center gap-2"><ArrowLeft size={14}/> Volver al Inicio</button>
            </div>
          </div>
        )} />
      </Routes>

      {/* --- MODALES CENTRALIZADOS (Elegantes y de proporciones correctas) --- */}
      {showModal === 'digital_card' && currentUser && (() => {
        // Motor dinámico: Calcula el hotel real de origen del socio para armar su link local
        const hotelOriginId = currentUser.registeredAtHotel && currentUser.registeredAtHotel !== 'Global' 
          ? currentUser.registeredAtHotel 
          : appId.replace('tenant-', '');
        
        const hotelPortalLink = `${window.location.origin}/?hotel=${hotelOriginId}`;

        return (
          <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in">
            <div className="bg-white rounded-3xl w-full max-w-xs overflow-hidden shadow-2xl relative">
              <button onClick={()=>setShowModal(null)} className="absolute top-6 right-6 text-zinc-400 hover:text-white transition-all hover:rotate-90 z-10"><XCircle size={24} strokeWidth={1.5}/></button>
              <div className="bg-zinc-950 p-8 text-center relative overflow-hidden" style={{backgroundColor: currentBranding.primaryColor === '#C5A059' ? '#09090b' : currentBranding.primaryColor}}>
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.4),transparent_70%)]"></div>
                <img src={currentBranding.logo} className="h-8 mx-auto mb-4 relative z-10 bg-white/10 p-1 rounded-md" alt="logo" />
                <h3 className="text-lg text-white font-light uppercase tracking-widest relative z-10 mb-1">{currentUser.name}</h3>
                <p className="text-white/80 text-[8px] font-bold uppercase tracking-[0.3em] relative z-10">{getDisplayTier(currentUser.tier)} MEMBER</p>
              </div>
              <div className="p-6 flex flex-col items-center bg-white space-y-4">
                
                {/* QR REAL Y ESCANEABLE VINCULADO AL PORTAL DE SU HOTEL DE ORIGEN */}
                <div className="w-40 h-40 bg-zinc-50 rounded-2xl flex items-center justify-center border border-dashed border-zinc-300 p-3 shadow-inner">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&color=27272a&data=${encodeURIComponent(hotelPortalLink)}`}
                    alt="QR Socio"
                    className="w-full h-full object-contain mix-blend-multiply"
                  />
                </div>
                
                <div className="text-center w-full bg-zinc-50 rounded-xl p-3 border border-zinc-100">
                  <p className="text-[8px] font-medium text-zinc-400 uppercase tracking-widest mb-1">Id de Socio</p>
                  <p className="text-base font-mono tracking-[0.2em] text-zinc-800 font-bold">{currentUser.dni}</p>
                </div>

                {/* BOTÓN VIRAL B2C: Permite al Huésped invitar a amigos a registrarse en este hotel específico */}
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(hotelPortalLink);
                    showToast('¡Enlace de invitación de tu hotel copiado!', 'success');
                  }}
                  className="w-full py-2.5 bg-zinc-950 text-white hover:bg-zinc-800 rounded-xl text-[9px] font-bold uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <Copy size={12} /> Compartir mi Hotel
                </button>

              </div>
            </div>
          </div>
        );
      })()}

      {showModal === 'invoice_upload' && currentUser && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-end md:items-center justify-center p-0 md:p-4 animate-in slide-in-from-bottom-4">
          <div className="bg-white rounded-t-[2.5rem] md:rounded-3xl w-full max-w-sm p-8 shadow-2xl relative">
            <button onClick={()=>{setShowModal(null);setBillAmount("");setBillDesc("");}} className="absolute top-6 right-6 text-zinc-400 hover:text-zinc-900 transition-all"><XCircle size={28} strokeWidth={1.5}/></button>
            <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900 mb-2">Cargar Consumo</h3>
            <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-8 font-medium">El personal validará su ticket.</p>
            <div className="space-y-6">
              <div className="space-y-3">
                <label>Importe Facturado ($)</label>
                <input type="number" placeholder="Ej: 15500.00" value={billAmount} onChange={e=>setBillAmount(e.target.value)} className="text-3xl font-light border-b border-zinc-200 focus:border-amber-500 transition-all py-2 w-full outline-none font-mono" />
              </div>
              <div className="space-y-3">
                <label>Detalle del Consumo</label>
                <input type="text" placeholder="Ej: Cena, Masaje..." value={billDesc} onChange={e=>setBillDesc(e.target.value)} className="p-4 bg-zinc-50 border border-zinc-100 rounded-xl w-full text-xs font-medium outline-none uppercase tracking-widest focus:bg-white transition-all" />
              </div>
              <div className="bg-amber-50 p-5 rounded-2xl border border-amber-100 flex justify-between items-center mt-4">
                <span className="text-[9px] font-medium text-amber-700 uppercase tracking-widest">Puntos Estimados</span>
                <span className="text-2xl font-light text-amber-600 tracking-tighter">+{Math.floor((Number(billAmount||0)/1.21)*((config.tiers || []).find(t=>t.name===getDisplayTier(currentUser.tier))?.cashback||2)/100)}</span>
              </div>
              <button onClick={async()=>{if(!billAmount||!billDesc)return showToast("Complete todos los campos.","error");setIsProcessing(true);try{const pts=Math.floor((Number(billAmount)/1.21)*((config.tiers || []).find(t=>t.name===getDisplayTier(currentUser.tier))?.cashback||2)/100);await addDoc(collection(db,`/artifacts/${appId}/public/data/requests`),{type:'FACTURA',guestId:currentUser.id,guestName:currentUser.name,dni:currentUser.dni,pts:pts,status:'Pendiente',conceptDetail:billDesc,benefitTitle:'Acreditación Consumo',hotelId:guestLocation,createdAt:serverTimestamp(),rating:0,messages:[]});showToast(`¡Comprobante asegurado! Sus +${pts} Pts están en revisión.`, "success");setShowModal(null);setBillAmount("");setBillDesc("");}catch(e){showToast(e.message,'error');}finally{setIsProcessing(false);}}} className="w-full py-4 rounded-xl text-[10px] font-medium text-white uppercase tracking-[0.2em] shadow-lg transition-all hover:scale-105 active:scale-95 mt-2" style={{backgroundColor:currentBranding.primaryColor}}>Enviar Ticket</button>
            </div>
          </div>
        </div>
      )}

      {showModal === 'process_request' && selectedRequest && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-sm p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto no-scrollbar flex flex-col">
            <button onClick={()=>{setShowModal(null); setChatMessage("");}} className="absolute top-6 right-6 text-zinc-400 hover:text-zinc-900 transition-all hover:rotate-90 z-10"><XCircle size={28} strokeWidth={1.5}/></button>
            <h3 className="text-[9px] font-medium uppercase tracking-[0.3em] text-zinc-400 mb-2 border-b border-zinc-100 pb-3">Validación: {selectedRequest.type}</h3>
            
            <div className="py-4 flex-1 flex flex-col">
              <p className="text-2xl font-light tracking-widest uppercase mb-1 text-zinc-900">{selectedRequest.guestName}</p>
              <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-[0.2em] mb-6">ID: {selectedRequest.dni}</p>
              
              <div className="bg-zinc-50 p-6 rounded-2xl border border-zinc-100 mb-4 shadow-sm">
                <p className="text-[8px] font-medium text-zinc-400 uppercase tracking-widest mb-2">Detalle:</p>
                <p className="text-lg font-medium text-zinc-800 mb-3 leading-tight">{selectedRequest.benefitTitle}</p>
                {selectedRequest.conceptDetail && <p className="text-xs text-zinc-500 italic border-l-2 border-amber-500 pl-3 py-0.5 mb-4">"{selectedRequest.conceptDetail}"</p>}
                {/* CAJA DE IMPACTO / EDICIÓN DE PUNTOS */}
                <div className="mt-4 pt-4 border-t border-zinc-200 flex justify-between items-center">
                  <span className="text-[9px] font-medium text-zinc-400 uppercase tracking-widest">Impacto</span>
                  {selectedRequest.type === 'FACTURA' && selectedRequest.status?.toUpperCase() === 'PENDIENTE' ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-light text-green-600">+</span>
                      <input 
                        type="number" 
                        value={selectedRequest.pts} 
                        onChange={e => setSelectedRequest({...selectedRequest, pts: Number(e.target.value) || 0})}
                        className="w-24 bg-zinc-100 border border-zinc-200 text-green-700 text-xl font-light tracking-tighter text-right p-1 rounded outline-none focus:border-amber-500 transition-all shadow-inner"
                        title="Modifique este valor si el huésped cargó un importe erróneo"
                      />
                      <span className="text-[9px] font-bold uppercase text-green-700 mt-1">PTS</span>
                    </div>
                  ) : (
                    <span className={`text-xl font-light ${selectedRequest.pts>0?'text-green-600':'text-amber-600'}`}>
                      {selectedRequest.type === 'ALTA' || selectedRequest.type === 'MENSAJE' ? 'Tema General' : (selectedRequest.pts>0 ? `+${selectedRequest.pts} PTS` : `-${selectedRequest.cost} PTS`)}
                    </span>
                  )}
                </div>
              </div>

              {/* --- BOTONES DE ACCIÓN DINÁMICOS --- */}
              <div className="space-y-3 mb-4">
                
                {/* 1. Ticket Recién Ingresado (Aprobar o Rechazar) */}
                {selectedRequest?.status?.toUpperCase() === 'PENDIENTE' && selectedRequest?.type !== 'MENSAJE' && (
                  <div className="flex gap-2">
                    <button onClick={() => { handleAdminApproval(selectedRequest, 'APROBAR'); setSelectedRequest({...selectedRequest, status: 'Aprobado'}); }} className="flex-1 bg-amber-500 text-zinc-950 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-amber-400 transition-all shadow-md active:scale-95 flex justify-center items-center gap-2">
                      <Check size={14} strokeWidth={2.5} /> Aprobar
                    </button>
                    <button onClick={() => { handleAdminApproval(selectedRequest, 'RECHAZAR'); setSelectedRequest({...selectedRequest, status: 'Rechazado'}); }} className="flex-1 bg-red-50 text-red-600 border border-red-200 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-red-100 transition-all shadow-sm active:scale-95 flex justify-center items-center gap-2">
                      <XCircle size={14} strokeWidth={2} /> Rechazar
                    </button>
                  </div>
                )}

                {/* 2. Beneficio Aprobado pero NO entregado físicamente */}
                {selectedRequest?.type === 'BENEFICIO' && selectedRequest?.status?.toUpperCase() === 'APROBADO' && !selectedRequest.deliveredAt && (
                  <button onClick={() => { handleMarkDelivered(selectedRequest); setSelectedRequest({...selectedRequest, deliveredAt: new Date()}); setShowModal(null); }} className="w-full bg-zinc-950 text-amber-500 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 transition-all shadow-md border border-amber-500/30 flex justify-center items-center gap-2 active:scale-95">
                    <CheckCircle size={14} strokeWidth={2} /> Registrar Entrega Física
                  </button>
                )}

                {/* 3. Beneficio Entregado exitosamente */}
                {selectedRequest?.type === 'BENEFICIO' && selectedRequest?.deliveredAt && (
                  <div className="w-full bg-green-50 border border-green-200 text-green-700 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest flex justify-center items-center gap-2 shadow-inner">
                    <Check size={14} strokeWidth={3} /> ¡Premio Entregado!
                  </div>
                )}

                {/* 4. Ticket Rechazado */}
                {selectedRequest?.status?.toUpperCase() === 'RECHAZADO' && selectedRequest?.type !== 'MENSAJE' && (
                  <div className="w-full bg-red-50 border border-red-100 text-red-700 py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest flex justify-center items-center gap-2 shadow-inner">
                    <XCircle size={14} strokeWidth={2} /> Solicitud Rechazada
                  </div>
                )}

              </div>
              
              {/* --- HISTORIAL DE CHAT --- */}
              <div className="mb-4 max-h-40 overflow-y-auto space-y-3 bg-zinc-50/50 p-4 rounded-2xl border border-zinc-100 no-scrollbar">
                {selectedRequest.messages?.map((m,i)=>(
                  <div key={i} className={`flex flex-col ${m.sender==='hotel'?'items-end':'items-start'}`}>
                    <span className="text-[7px] font-medium uppercase text-zinc-400 mb-1 px-1">{m.sender==='hotel'?'Staff':'Socio'}</span>
                    <div className={`p-3 rounded-xl text-[10px] font-medium leading-relaxed max-w-[90%] shadow-sm whitespace-pre-wrap ${m.sender==='hotel'?'bg-zinc-900 text-white rounded-tr-none':'bg-white text-zinc-700 border border-zinc-200 rounded-tl-none'}`}>
                      {m.image && <img src={m.image} alt="Adjunto" className="w-full rounded-lg mb-2 object-cover border border-white/10" />}
                      {m.text}
                    </div>
                  </div>
                ))}
                {(!selectedRequest.messages || selectedRequest.messages.length === 0) && <p className="text-center text-[9px] text-zinc-400 uppercase tracking-widest py-4">Sin mensajes</p>}
              </div>

              {/* --- RESPUESTAS RÁPIDAS (PLANTILLAS) --- */}
              <div className="flex flex-wrap gap-2 mb-3">
                <button onClick={() => setChatMessage("¡Bienvenido a nuestro programa de fidelidad! Tu cuenta ha sido activada y tu bono de bienvenida ya está acreditado. ¡Empieza a disfrutar de tus beneficios!")} className="bg-zinc-100 hover:bg-amber-50 text-zinc-600 hover:text-amber-600 border border-zinc-200 px-3 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-widest transition-all shadow-sm">
                  Bienvenida
                </button>
                <button onClick={() => setChatMessage("¡Hola! Hemos verificado tu consumo exitosamente. Los puntos ya se encuentran acreditados en tu cuenta. ¡Gracias por elegirnos!")} className="bg-zinc-100 hover:bg-amber-50 text-zinc-600 hover:text-amber-600 border border-zinc-200 px-3 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-widest transition-all shadow-sm">
                  Ticket Aprobado
                </button>
                <button onClick={() => setChatMessage("¡Hola! No logramos identificar este registro. ¿Podrías confirmarnos a qué número de habitación fue cargado o la fecha exacta, por favor?")} className="bg-zinc-100 hover:bg-amber-50 text-zinc-600 hover:text-amber-600 border border-zinc-200 px-3 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-widest transition-all shadow-sm">
                  Faltan Datos
                </button>
                <button onClick={() => setChatMessage("¡Hola! Validamos tu consumo, pero hemos realizado un leve ajuste en los puntos otorgados para que coincidan con el valor final de nuestro sistema.")} className="bg-zinc-100 hover:bg-amber-50 text-zinc-600 hover:text-amber-600 border border-zinc-200 px-3 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-widest transition-all shadow-sm">
                  Ajuste Puntos
                </button>
              </div>

              {/* --- INPUT DE CHAT --- */}
              <div className="flex gap-2 mt-auto">
                <input type="text" placeholder="Escriba un mensaje..." value={chatMessage} onChange={e=>setChatMessage(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSendMessage(selectedRequest.id,'hotel')} className="flex-1 bg-zinc-50 p-3 rounded-xl border border-zinc-100 text-xs font-light outline-none focus:bg-white transition-all shadow-inner" />
                <button onClick={()=>handleSendMessage(selectedRequest.id,'hotel')} className="bg-zinc-900 text-white p-3 rounded-xl hover:bg-zinc-800 shadow-md transition-all"><Send size={18} strokeWidth={1.5}/></button>
              </div>

            </div>
          </div>
        </div>
      )}

      {showModal === 'guest_chat_modal' && selectedRequest && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-sm p-8 shadow-2xl relative h-[75vh] flex flex-col">
            <button onClick={()=>{setShowModal(null);setChatMessage("");}} className="absolute top-6 right-6 text-zinc-400 hover:text-zinc-900 transition-all hover:rotate-90 z-10"><XCircle size={28} strokeWidth={1.5}/></button>
            <h3 className="text-[9px] font-medium uppercase tracking-[0.3em] text-zinc-400 mb-2 border-b border-zinc-100 pb-4 pr-10">Ticket: {selectedRequest.benefitTitle}</h3>
            <div className="flex-1 overflow-y-auto py-4 space-y-4 no-scrollbar flex flex-col">
              {selectedRequest.messages?.map((msg,i)=>(
                <div key={i} className={`p-4 rounded-2xl max-w-[85%] shadow-sm ${msg.sender==='guest'?'bg-zinc-900 text-white self-end rounded-tr-none':'bg-zinc-50 border border-zinc-200 text-zinc-800 self-start rounded-tl-none'}`}>
                  {msg.image && <img src={msg.image} alt="Adjunto" className="w-full rounded-xl mb-3 object-cover shadow-sm" />}
                  <p className="text-[11px] font-light leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                  <p className={`text-[7px] mt-2 text-right font-medium uppercase tracking-widest opacity-50 ${msg.sender==='guest'?'text-zinc-300':'text-zinc-500'}`}>{new Date(msg.timestamp).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}</p>
                </div>
              ))}
            </div>
            <div className="pt-4 border-t border-zinc-100">
              <div className="flex gap-3">
                <input type="text" placeholder="Mensaje..." value={chatMessage} onChange={e=>setChatMessage(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSendMessage(selectedRequest.id,'guest')} className="flex-1 bg-zinc-50 border border-zinc-100 p-3 rounded-xl text-xs font-light outline-none focus:bg-white transition-all shadow-inner" />
                <button onClick={()=>handleSendMessage(selectedRequest.id,'guest')} className="bg-amber-500 text-black w-10 h-10 rounded-xl flex items-center justify-center shadow-md hover:bg-amber-400 transition-all active:scale-95" style={{backgroundColor:currentBranding.primaryColor}}><Send size={18} strokeWidth={1.5}/></button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal === 'new_general_chat' && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-sm p-8 shadow-2xl relative">
            <button onClick={()=>{setShowModal(null);setNewChatMsg("");}} className="absolute top-6 right-6 text-zinc-400 hover:text-zinc-900 transition-all"><XCircle size={28} strokeWidth={1.5}/></button>
            <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900 mb-3">Conserjería Digital</h3>
            <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-8 leading-relaxed font-medium">Envíe consultas directamente a recepción.</p>
            <div className="space-y-5">
              <textarea rows="4" placeholder="¿En qué podemos ayudarle?" value={newChatMsg} onChange={e=>setNewChatMsg(e.target.value)} className="w-full bg-zinc-50 border border-zinc-100 rounded-2xl p-5 text-xs font-light resize-none shadow-inner outline-none focus:bg-white transition-all"></textarea>
              <button onClick={handleCreateGeneralChat} className="w-full py-4 rounded-xl text-[10px] font-medium uppercase text-white tracking-[0.2em] shadow-md transition-all hover:scale-[1.02] active:scale-95" style={{backgroundColor:currentBranding.primaryColor}}>Enviar Mensaje</button>
            </div>
          </div>
        </div>
      )}

      {showModal === 'add_benefit' && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-sm p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto no-scrollbar">
            <button onClick={resetFormState} className="absolute top-6 right-6 text-zinc-400 hover:text-zinc-900 transition-all"><XCircle size={28} strokeWidth={1.5}/></button>
            <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900 mb-6">{editMode?'Editar':'Nuevo'} Premio</h3>
            <div className="space-y-5">
              <div className="space-y-2"><label>Título</label><input type="text" value={newBenefit.title} onChange={e=>setNewBenefit({...newBenefit,title:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl font-medium w-full outline-none focus:bg-white transition-all shadow-inner text-sm"/></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><label>Costo PTS</label><input type="number" value={newBenefit.cost} onChange={e=>setNewBenefit({...newBenefit,cost:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl text-amber-600 font-medium w-full outline-none focus:bg-white transition-all shadow-inner text-sm"/></div>
                <div className="space-y-2"><label>Stock (Uni.)</label><input type="number" value={newBenefit.stock} onChange={e=>setNewBenefit({...newBenefit,stock:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all shadow-inner text-sm"/></div>
              </div>
              <div className="space-y-2"><label>Ícono o Imagen URL</label><input type="text" placeholder="Ej: 🍽️ o https://..." value={newBenefit.image} onChange={e=>setNewBenefit({...newBenefit,image:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl text-xs w-full outline-none focus:bg-white transition-all shadow-inner"/></div>
              <div className="space-y-2"><label>Descripción</label><textarea rows="3" value={newBenefit.desc} onChange={e=>setNewBenefit({...newBenefit,desc:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl text-xs w-full outline-none focus:bg-white transition-all shadow-inner resize-none font-light"></textarea></div>
              
              {/* REGLA MULTI-TENANT: ASIGNACIÓN DE SEDE PARA EL PREMIO */}
              {config.isChainMode && (
                <div className="space-y-2">
                  <label>Alcance del Premio</label>
                  {isMasterOrChain ? (
                    <select 
                      value={newBenefit.hotelId || 'Todos'} 
                      onChange={e => setNewBenefit({...newBenefit, hotelId: e.target.value})}
                      className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl font-medium uppercase text-[10px] tracking-widest outline-none w-full shadow-inner cursor-pointer"
                    >
                      <option value="Todos">🌐 Toda la Cadena (Global Network)</option>
                      {hotels.map(h => <option key={h.id} value={h.id}>📍 Sede: {h.name}</option>)}
                    </select>
                  ) : (
                    <div className="bg-zinc-100 p-3 rounded-xl text-xs text-zinc-600 font-medium uppercase tracking-wider border border-zinc-200">
                      📍 Exclusivo para su Sede ({hotels.find(h => h.id === staffSession?.hotelId)?.name || 'Sede Local'})
                    </div>
                  )}
                </div>
              )}

              <button 
                onClick={() => {
                  // Forzar el hotelId correcto antes de guardar si es un admin local
                  let beneficioFinal = { ...newBenefit };
                  if (config.isChainMode && !isMasterOrChain) {
                    beneficioFinal.hotelId = staffSession?.hotelId || 'Global';
                  }
                  handleAction('ADD_BENEFIT', beneficioFinal);
                }} 
                className="w-full py-4 bg-zinc-950 text-white rounded-xl text-[10px] font-medium uppercase tracking-[0.2em] shadow-md mt-4 transition-all hover:bg-zinc-800"
              >
                Guardar Premio
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal === 'benefit_history' && selectedBenefit && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-2xl p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto no-scrollbar">
            <button onClick={()=>setShowModal(null)} className="absolute top-6 right-6 text-zinc-400 hover:text-zinc-900 transition-all hover:rotate-90"><XCircle size={28} strokeWidth={1.5}/></button>
            <h3 className="text-[9px] font-medium uppercase tracking-[0.3em] text-zinc-400 mb-2 border-b border-zinc-100 pb-3">Historial de Entregas</h3>
            <h2 className="text-2xl font-light uppercase tracking-widest text-zinc-900 mb-6">{selectedBenefit.title}</h2>
            <div className="overflow-x-auto w-full border border-zinc-200 rounded-2xl bg-zinc-50/50">
              <table className="w-full text-left text-xs min-w-[500px]">
                <thead><tr className="bg-zinc-100 text-zinc-800 font-bold text-[10px] uppercase tracking-[0.2em] border-b border-zinc-200"><th className="p-4">Fecha</th><th className="p-4">Socio</th><th className="p-4 text-center">Feedback</th><th className="p-4 text-right">Estatus</th></tr></thead>
                <tbody>
                  {requests.filter(r=>r.type==='BENEFICIO' && (r.benefitId===selectedBenefit.id || r.benefitTitle===selectedBenefit.title)).map(req=>(
                    <tr key={req.id} className="border-b border-zinc-100 hover:bg-white transition-all">
                      <td className="p-4 text-zinc-500 text-[10px] font-mono">{new Date(req.createdAt?.seconds*1000).toLocaleDateString()}</td>
                      <td className="p-4 font-medium text-zinc-800">{req.guestName}<br/><span className="text-[8px] text-zinc-400 font-light tracking-widest mt-1 block uppercase">ID: {req.dni}</span></td>
                      <td className="p-4 text-center flex justify-center">{req.rating>0?<RatingFaces currentRating={req.rating} readOnly size={18}/>:<span className="text-[9px] text-zinc-600 font-bold italic uppercase">Pendiente</span>}</td>
                      <td className="p-4 text-right">
                        <div className="flex flex-col items-end gap-1.5">
                          <span className={`text-[8px] font-medium uppercase px-2.5 py-1 rounded-md ${req.status==='APROBADO'?'bg-green-50 text-green-700 border border-green-100':'bg-amber-50 text-amber-700 border border-amber-100'}`}>{req.status}</span>
                          {req.deliveredAt?<span className="text-[8px] text-green-600 font-medium flex items-center gap-1 mt-1"><CheckCircle size={10} strokeWidth={1.5}/> Entregado</span>:req.status==='APROBADO'&&<button onClick={()=>handleMarkDelivered(req)} className="text-[8px] font-medium text-amber-600 underline uppercase mt-1 hover:text-amber-800">Cerrar Entrega</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {requests.filter(r=>r.type==='BENEFICIO' && (r.benefitId===selectedBenefit.id || r.benefitTitle===selectedBenefit.title)).length === 0 && <p className="text-center py-8 text-[10px] font-medium uppercase tracking-widest text-zinc-400">Sin canjes registrados.</p>}
            </div>
          </div>
        </div>
      )}

{showModal === 'add_hotel' && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl relative">
            <button onClick={resetFormState} className="absolute top-6 right-6 text-zinc-400 hover:text-zinc-900 transition-all"><XCircle size={28} strokeWidth={1.5}/></button>
            <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900 mb-6">{editMode ? 'Editar' : 'Nueva'} Sede</h3>
            <div className="space-y-5">
              <div className="space-y-2">
                <label>Nombre de la Sede</label>
                <input type="text" placeholder="Ej: Grand Hotel Centro" value={newHotel.name || ''} onChange={e=>setNewHotel({...newHotel,name:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all shadow-inner text-sm font-medium"/>
              </div>
              <div className="space-y-2">
                <label>Ciudad / Ubicación</label>
                <input type="text" placeholder="Ej: Buenos Aires" value={newHotel.city || ''} onChange={e=>setNewHotel({...newHotel,city:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all shadow-inner text-sm font-medium"/>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="flex items-center gap-1"><Gift size={10} className="text-amber-500"/> Bono Bienvenida</label>
                  <input type="number" value={newHotel.welcomePoints || 0} onChange={e=>setNewHotel({...newHotel,welcomePoints:Number(e.target.value)})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all shadow-inner text-sm font-medium text-amber-600"/>
                </div>
                <div className="space-y-2">
                  <label>Color Identificador</label>
                  <div className="flex items-center gap-3 bg-zinc-50 p-2 border border-zinc-100 rounded-xl">
                    <input type="color" value={newHotel.primaryColor || '#C5A059'} onChange={e=>setNewHotel({...newHotel,primaryColor:e.target.value})} className="w-8 h-8 rounded-full border-none cursor-pointer bg-transparent" />
                    <span className="text-xs font-mono text-zinc-500 uppercase">{newHotel.primaryColor || '#C5A059'}</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="flex items-center gap-1"><Globe size={10} className="text-amber-500"/> Motor de Reservas (Específico de Sede)</label>
                <input type="text" placeholder="Ej: https://reservas.mihotel.com/rafaela" value={newHotel.bookingUrl || ''} onChange={e=>setNewHotel({...newHotel,bookingUrl:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all shadow-inner text-sm font-medium"/>
              </div>
              <div className="space-y-2 pt-2">
                <label>Estado de la Propiedad</label>
                <div className="flex items-center gap-3 bg-zinc-50 p-3 rounded-xl border border-zinc-100 shadow-sm">
                  <input 
                    type="checkbox" 
                    checked={newHotel.active !== false} 
                    onChange={e => setNewHotel({...newHotel, active: e.target.checked})} 
                    className="w-4 h-4 accent-amber-500 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-600 font-medium uppercase tracking-wider">
                    {newHotel.active !== false ? '● Activa (Disponible para Huéspedes y Facturable)' : '○ Inactiva (Modo Borrador / No cuenta en facturación)'}
                  </span>
                </div>
              </div>
              <button onClick={() => {
                if(!newHotel.name || !newHotel.city) return showToast("Completar Nombre y Ciudad", "error");
                
                // --- CONTROL DE SEDES ILIMITADAS PARA CADENAS ---
              if (!editMode && !config.isChainMode && hotels.length >= 1) {
                 setShowModal(null);
                 return setUpgradeRequired({ module: "Multi-Sede (Administrar más de 1 hotel)", plan: "ENTERPRISE" });
              }

              // Guardamos la sede con su estado dinámico de activación
              const finalHotel = { ...newHotel, active: newHotel.active !== false };
              handleAction('ADD_HOTEL', finalHotel);
              }} className="w-full py-4 text-white rounded-xl text-[10px] font-medium uppercase tracking-[0.2em] shadow-md mt-4 transition-all hover:scale-[1.02]" style={{backgroundColor:currentBranding.primaryColor}}>
                Guardar Sede
              </button>
            </div>
          </div>
        </div>
      )}
      {showModal === 'add_staff' && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-sm p-8 shadow-2xl relative">
            <button onClick={resetFormState} className="absolute top-6 right-6 text-zinc-400 hover:text-zinc-900 transition-all"><XCircle size={28} strokeWidth={1.5}/></button>
            <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900 mb-6">Miembro Staff</h3>
            <div className="space-y-5">
              <div className="space-y-2"><label>Nombre Completo</label><input type="text" value={newStaff.name} onChange={e=>setNewStaff({...newStaff,name:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all shadow-inner text-sm font-medium"/></div>
              <div className="space-y-2"><label>Email (Login)</label><input type="email" value={newStaff.email} onChange={e=>setNewStaff({...newStaff,email:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all shadow-inner text-sm font-medium"/></div>
              <div className="space-y-2"><label>PIN de Acceso</label><input type="password" maxLength="4" value={newStaff.pin} onChange={e=>setNewStaff({...newStaff,pin:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl text-center text-2xl font-mono tracking-[0.5em] w-full outline-none focus:bg-white transition-all shadow-inner"/></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label>Rol</label>
                  <select value={newStaff.role} onChange={e=>setNewStaff({...newStaff,role:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl font-medium uppercase text-[10px] tracking-widest outline-none w-full shadow-inner cursor-pointer">
                    <option value="Operador">Operador de Recepción</option>
                    <option value="Admin">Administrador Sede</option>
                    {isMasterOrChain && <option value="Superadmin">Superadmin Global</option>}
                  </select>
                </div>
{/* SI ES CADENA Y NO ES SUPERADMIN, DEBE ASIGNARSE LA SEDE */}
                {config.isChainMode && newStaff.role !== 'Superadmin' && (
                  <div className="space-y-2">
                    <label>Sede Asignada</label>
                    <select 
                      value={!isMasterOrChain ? staffSession.hotelId : (newStaff.hotelId || 'Global')} 
                      onChange={e=>setNewStaff({...newStaff,hotelId:e.target.value})} 
                      disabled={!isMasterOrChain}
                      className={`bg-zinc-50 p-3 border border-zinc-100 rounded-xl font-medium uppercase text-[10px] tracking-widest outline-none w-full shadow-inner ${!isMasterOrChain ? 'opacity-50 cursor-not-allowed bg-zinc-200/50' : 'cursor-pointer'}`}
                    >
                      {isMasterOrChain && <option value="Global">Seleccione Sede...</option>}
                      {!isMasterOrChain ? (
                        <option value={staffSession.hotelId}>{hotels.find(h => h.id === staffSession.hotelId)?.name || 'Sede Local'}</option>
                      ) : (
                        hotels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)
                      )}
                    </select>
                  </div>
                )}
              </div>
              <button onClick={()=>handleAction('ADD_STAFF',newStaff)} className="w-full py-4 bg-zinc-950 text-white rounded-xl text-[10px] font-medium uppercase tracking-[0.2em] shadow-md mt-4 transition-all hover:bg-zinc-800">Habilitar Acceso</button>
            </div>
          </div>
        </div>
      )}

{/* --- MODAL HUÉSPED: AUTO-REGISTRO --- */}
      {showModal === 'guest_register' && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md p-8 shadow-2xl relative border border-zinc-100">
            <button onClick={() => setShowModal(null)} className="absolute top-6 right-6 text-zinc-400 hover:text-zinc-900 transition-all">
               <XCircle size={28} strokeWidth={1.5}/>
            </button>
            
            <div className="text-center mb-8 mt-2">
               <h3 className="text-2xl font-light text-zinc-900 tracking-tight mb-1">Solicitud de Alta</h3>
               <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-widest">Complete sus datos para unirse</p>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                 <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest ml-1">Nombre y Apellido</label>
                 <input type="text" placeholder="Ej. Juan Pérez" className="bg-zinc-50/50 p-4 border border-zinc-200 rounded-2xl w-full outline-none focus:bg-white focus:border-zinc-400 transition-all text-sm shadow-inner"/>
              </div>
              
              <div className="space-y-2">
                 <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest ml-1">DNI / Documento</label>
                 <input type="text" placeholder="Su número de identificación" className="bg-zinc-50/50 p-4 border border-zinc-200 rounded-2xl w-full outline-none focus:bg-white focus:border-zinc-400 transition-all text-sm shadow-inner"/>
              </div>
              
              <div className="space-y-2">
                 <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest ml-1">WhatsApp</label>
                 <input type="text" placeholder="+54 9..." className="bg-zinc-50/50 p-4 border border-zinc-200 rounded-2xl w-full outline-none focus:bg-white focus:border-zinc-400 transition-all text-sm shadow-inner"/>
              </div>

              <button 
                disabled={isProcessing}
                onClick={async () => {
                  const nameInput = document.querySelector('input[placeholder="Ej. Juan Pérez"]')?.value;
                  const dniInput = document.querySelector('input[placeholder="Su número de identificación"]')?.value;
                  const phoneInput = document.querySelector('input[placeholder="+54 9..."]')?.value;

                  if(!nameInput || !dniInput || !phoneInput) {
                    showToast('Por favor, complete todos los campos', 'error');
                    return;
                  }
                  
                  const dniClean = dniInput.trim();
                  
                  // Validación 1: Verificar que no exista ya el usuario
                  const existeSocio = guests.find(g => g.dni?.trim() === dniClean);
                  if(existeSocio) {
                     showToast('Este documento ya está registrado. Cierre esta ventana e inicie sesión.', 'error');
                     return;
                  }

                  setIsProcessing(true);
                  try {
                    // 1. Generamos el PIN y preparamos los datos
                    const pinGenerado = (dniClean && dniClean.length >= 4) ? dniClean.slice(-4) : '1234';
                    const welcomePts = currentBranding.welcomePoints || 500;
                    const hotelDestino = activeTenantId || 'Global';

                    // 2. Creamos al socio oficial directamente en el directorio (¡Fricción Cero!)
                    const newGuestData = {
                        name: nameInput.trim(),
                        dni: dniClean,
                        phone: phoneInput.trim(),
                        points: welcomePts,
                        tier: 'Classic',
                        status: 'ACTIVO', 
                        registeredAtHotel: hotelDestino,
                        pin: pinGenerado,
                        createdAt: serverTimestamp(),
                        preferences: []
                    };
                    const docRef = await addDoc(collection(db, `artifacts/${appId}/public/data/guests`), newGuestData);

                    // 3. Le inyectamos su Bono de Bienvenida para que vea los puntos de inmediato
                    await addDoc(collection(db, `artifacts/${appId}/public/data/requests`), { 
                        type: 'BONO', guestId: docRef.id, guestName: newGuestData.name, 
                        dni: newGuestData.dni, pts: welcomePts, status: 'APROBADO', 
                        benefitTitle: 'Bono Inicial (Auto-Registro)', hotelId: hotelDestino, 
                        createdAt: serverTimestamp(), rating: 0, messages: [] 
                    });

                    // 4. Creamos un ticket en la Bandeja del Staff como 'Auditoría' (Ya nace aprobado)
                    await addDoc(collection(db, `artifacts/${appId}/public/data/requests`), {
                        type: 'ALTA', guestId: docRef.id, guestName: newGuestData.name,
                        dni: newGuestData.dni, phone: newGuestData.phone,
                        benefitTitle: 'Auto-Registro Digital', status: 'APROBADO',
                        hotelId: hotelDestino, createdAt: serverTimestamp(), rating: 0, messages: []
                    });

                    // 5. ¡LA MAGIA! Logueamos al huésped automáticamente en su dispositivo
                    const loggedInGuest = { id: docRef.id, ...newGuestData };
                    setCurrentUser(loggedInGuest);
                    
                    showToast('¡Cuenta activada! Bienvenido a sus beneficios.', 'success'); 
                    setShowModal(null);
                  } catch (err) {
                    showToast('Error al crear la cuenta', 'error');
                    console.error(err);
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                className="w-full py-4 rounded-2xl text-white text-[10px] font-bold uppercase tracking-[0.2em] shadow-lg mt-6 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2" 
                style={{ backgroundColor: currentBranding?.primaryColor || '#000', color: currentBranding?.primaryColor === '#C5A059' ? '#000' : '#fff' }}
              >
                {isProcessing ? <Loader2 size={16} className="animate-spin" /> : 'CREAR CUENTA Y ACCEDER'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal === 'add_guest' && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl relative">
            <button onClick={resetFormState} className="absolute top-6 right-6 text-zinc-400 hover:text-zinc-900 transition-all"><XCircle size={28} strokeWidth={1.5}/></button>
            <h3 className="text-xl font-light uppercase tracking-widest text-zinc-900 mb-6">Alta de Socio</h3>
            <div className="space-y-5">
              <div className="space-y-2"><label>Nombre y Apellido</label><input type="text" value={newGuest.name} onChange={e=>setNewGuest({...newGuest,name:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all shadow-inner text-sm font-medium"/></div>
              
              <div className="grid grid-cols-2 gap-4">
                 <div className="space-y-2"><label>DNI / ID</label><input type="text" value={newGuest.dni} onChange={e=>setNewGuest({...newGuest,dni:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all shadow-inner text-sm font-medium"/></div>
                 <div className="space-y-2"><label>WhatsApp</label><input type="text" placeholder="+54 9..." value={newGuest.phone || ''} onChange={e=>setNewGuest({...newGuest,phone:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all shadow-inner text-sm font-medium"/></div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                 <div className="space-y-2">
                    <label>Nivel Inicial</label>
                    <select value={newGuest.tier} onChange={e=>setNewGuest({...newGuest,tier:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl w-full outline-none focus:bg-white transition-all shadow-inner text-[11px] font-medium uppercase tracking-widest cursor-pointer">
                      {(config.tiers || []).map(t=>(<option key={t.id} value={t.name}>{t.name}</option>))}
                    </select>
                 </div>
                 <div className="space-y-2"><label>PIN Acceso</label><input type="text" maxLength="4" placeholder="••••" value={newGuest.pin} onChange={e=>setNewGuest({...newGuest,pin:e.target.value})} className="bg-zinc-50 p-3 border border-zinc-100 rounded-xl text-center text-xl font-mono tracking-[0.5em] w-full shadow-inner outline-none focus:bg-white transition-all"/></div>
              </div>

              <button onClick={()=>handleAction('ADD_GUEST',newGuest)} className="w-full py-4 text-white rounded-xl text-[10px] font-medium uppercase tracking-[0.2em] shadow-md mt-4 transition-all hover:scale-[1.02]" style={{backgroundColor:currentBranding.primaryColor}}>Confirmar Alta</button>
            </div>
          </div>
        </div>
      )}
      {showModal === 'guest_audit' && (
        <div className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-4xl p-8 shadow-2xl relative max-h-[90vh] overflow-y-auto no-scrollbar">
            <button onClick={() => setShowModal(null)} className="absolute top-6 right-6 text-zinc-400 hover:text-zinc-900 transition-all"><XCircle size={28} strokeWidth={1.5}/></button>
            
            <div className="flex items-center gap-4 mb-2">
                <div className="p-3 bg-amber-100 text-amber-600 rounded-2xl shadow-inner"><Activity size={28} strokeWidth={1.5}/></div>
                <div>
                    <h3 className="text-2xl font-light uppercase tracking-widest text-zinc-900 mb-1">Auditoría de Inactividad</h3>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold">Gestión de Vencimientos (24 y 36 meses)</p>
                </div>
            </div>
            
            <div className="mt-8 bg-zinc-50 rounded-2xl border border-zinc-200 overflow-hidden shadow-sm">
                <table className="w-full text-left text-xs">
                    <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-widest text-[9px] border-b border-zinc-200 font-bold">
                        <tr>
                            <th className="p-5">Socio</th>
                            <th className="p-5">Última Actividad</th>
                            <th className="p-5">Estado Legal</th>
                            <th className="p-5 text-right">Acción Requerida</th>
                        </tr>
                    </thead>
                    <tbody>
                        {guests.filter(g => {
                            const lastAct = g.lastActivityAt?.seconds ? g.lastActivityAt.seconds * 1000 : (g.createdAt?.seconds ? g.createdAt.seconds * 1000 : Date.now());
                            const monthsInactive = (Date.now() - lastAct) / (1000 * 60 * 60 * 24 * 30);
                            return monthsInactive > 24 && g.points > 0; // Solo mostrar si pasaron 24 meses Y tienen puntos
                        }).map(g => {
                            const lastAct = g.lastActivityAt?.seconds ? g.lastActivityAt.seconds * 1000 : (g.createdAt?.seconds ? g.createdAt.seconds * 1000 : Date.now());
                            const monthsInactive = Math.floor((Date.now() - lastAct) / (1000 * 60 * 60 * 24 * 30));
                            
                            return (
                                <tr key={g.id} className="border-b border-zinc-100 hover:bg-white transition-all">
                                    <td className="p-5 font-bold text-zinc-900 text-sm">{g.name} <br/><span className="text-[9px] text-zinc-500 font-bold uppercase tracking-widest">ID: {g.dni}</span></td>
                                    <td className="p-5 font-mono text-[10px] text-zinc-500">{new Date(lastAct).toLocaleDateString()} <br/><span className="text-amber-600 font-bold text-[9px] uppercase tracking-widest">{monthsInactive} meses inactivo</span></td>
                                    <td className="p-5">
                                        {monthsInactive >= 36 ? (
                                            <span className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-widest border border-red-100">Expiración Total (Reset)</span>
                                        ) : (
                                            <span className="bg-amber-50 text-amber-700 px-3 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-widest border border-amber-200">Puntos Vencidos</span>
                                        )}
                                    </td>
                                    <td className="p-5 text-right">
                                        <button onClick={async () => {
                                            if(window.confirm(`¿Confirmar auditoría para ${g.name}? Se vaciará su saldo de puntos por inactividad.`)) {
                                                const updateData = monthsInactive >= 36 
                                                    ? { points: 0, tier: 'Classic', lastActivityAt: serverTimestamp() } 
                                                    : { points: 0, lastActivityAt: serverTimestamp() };
                                                
                                                await updateDoc(doc(db, `artifacts/${appId}/public/data/guests`, g.id), updateData);
                                                
                                                // Dejar registro en el historial del huésped
                                                await addDoc(collection(db, `artifacts/${appId}/public/data/requests`), {
                                                    type: 'MENSAJE', guestId: g.id, guestName: g.name, dni: g.dni,
                                                    status: 'APROBADO', hotelId: g.registeredAtHotel || 'Global', pts: 0, cost: g.points, rating: 0, benefitTitle: 'Ajuste por Inactividad',
                                                    messages: [{ sender: 'hotel', text: 'Saldo expirado por políticas de inactividad de la cuenta.', timestamp: Date.now() }],
                                                    createdAt: serverTimestamp(), unreadByGuest: true, unreadByAdmin: false
                                                });

                                                showToast("Cuenta actualizada", "success");
                                            }
                                        }} className="px-6 py-2.5 bg-zinc-950 text-white rounded-xl text-[9px] uppercase tracking-widest font-bold hover:bg-zinc-800 transition-all active:scale-95 shadow-md">Ejecutar Regla</button>
                                    </td>
                                </tr>
                            );
                        })}
                        {guests.filter(g => {
                            const lastAct = g.lastActivityAt?.seconds ? g.lastActivityAt.seconds * 1000 : (g.createdAt?.seconds ? g.createdAt.seconds * 1000 : Date.now());
                            return ((Date.now() - lastAct) / (1000 * 60 * 60 * 24 * 30)) > 24 && g.points > 0;
                        }).length === 0 && (
                            <tr><td colSpan="4" className="p-16 text-center text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Todas las cuentas están al día. Ningún socio supera los plazos de vencimiento.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const App = () => (<Router><AppContent /></Router>);
export default App;